import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { autoGenderPatch } from "@/lib/guessGender";
import { api, formatApiError, formatCurrency, formatDate, validatePlate, normalizePlate, validateItalianMobile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Home, Heart, Stethoscope, Activity, Plane, Shield, Bike, Anchor, Ship,
  Briefcase, Truck, Tractor, Wrench,
} from "lucide-react";

// 🆕 Mappa icona → componente lucide (allineata con PolicyTypes.jsx)
const TYPE_ICONS = {
  car: Car, bike: Bike, home: Home, heart: Heart, stethoscope: Stethoscope,
  activity: Activity, plane: Plane, shield: Shield, anchor: Anchor, ship: Ship,
  briefcase: Briefcase, building2: Building2, truck: Truck, tractor: Tractor,
  wrench: Wrench, sparkles: Sparkles,
};
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { VehicleAutocomplete, COMMON_BRANDS, COMMON_MODELS_BY_BRAND } from "@/components/VehicleAutocomplete";
import { VehiclesEditor, buildVehiclesPayload } from "@/components/VehiclesEditor";
import { BoatEnginesEditor, buildBoatEnginesPayload } from "@/components/BoatEnginesEditor";
import { PlateInput } from "@/components/PlateInput";
import { MoneyInput } from "@/components/MoneyInput";
import { ScanButton } from "@/components/ScanButton";
import { CameraButton } from "@/components/CameraButton";
import { DropZone } from "@/components/DropZone";
import { askMultiFileAction } from "@/components/MultiFileUploadDialog";
import { computeFiscalCode } from "@/lib/fiscalCode";
import MobileInputWithWA from "@/components/MobileInputWithWA";
import { ArrowRight, ArrowLeft, CheckCircle2, Search, UserPlus, Upload, X, FileText,
  ShieldCheck, Sparkles, Loader2, User, Building2, Receipt, Car, Send, Pencil, Plus, ExternalLink, Calculator,
} from "lucide-react";
import { toast } from "sonner";
import { EmailPreviewDialog, shouldSkipEmailPreview } from "@/components/EmailPreviewDialog";
import { isMotoBrand, BOAT_ENGINE_INSTALL_OPTIONS, BOAT_ENGINE_FUEL_OPTIONS, BOAT_HULL_MATERIAL_OPTIONS } from "@/lib/policyConstants";
import { DatePicker } from "@/components/DatePicker";
import { resolveGuarantee, buildGuaranteeCode } from "@/lib/guaranteeMatch";
import { uploadWithProgress } from "@/lib/downloadWithProgress";

const STEPS_DEFAULT = ["Cliente", "Doc. Cliente", "Polizza", "Allegati", "Conferma"];
const STEPS_QUOTE = ["Cliente", "Doc. Cliente", "Preventivo", "Allegati", "Conferma"];

const POLICY_TYPES = [
  { v: "auto", l: "Auto" }, { v: "natante", l: "Natante" }, { v: "casa", l: "Casa" }, { v: "vita", l: "Vita" },
  { v: "salute", l: "Salute" }, { v: "infortuni", l: "Infortuni" },
  { v: "viaggio", l: "Viaggio" }, { v: "responsabilita_civile", l: "RC" },
];
const FRAZ_OPTIONS = [
  { v: "annuale", l: "Annuale" }, { v: "semestrale", l: "Semestrale" },
  { v: "trimestrale", l: "Trimestrale" }, { v: "mensile", l: "Mensile" },
];

const CLIENT_DOC_KINDS = [
  { v: "carta_identita", l: "Carta d'identità" },
  { v: "patente", l: "Patente" },
  { v: "tessera_sanitaria", l: "Tessera Sanitaria / CF" },
  { v: "passaporto", l: "Passaporto" },
  { v: "contratto", l: "Contratto / altro contratto" },
  { v: "altro", l: "Altro" },
];

const debounce = (fn, ms) => {
  let t;
  return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); };
};

export default function PolicyWizard({ mode = "policy" }) {
  const navigate = useNavigate();
  const genderManualRef = useRef(new Set());
  const isQuote = mode === "quote";
  const targetName = isQuote ? "preventivo" : "polizza";
  const targetNameCap = isQuote ? "Preventivo" : "Polizza";
  const listPath = isQuote ? "/quotes" : "/policies";
  const attachBase = isQuote ? "/quotes" : "/policies";
  const STEPS = isQuote ? STEPS_QUOTE : STEPS_DEFAULT;
  const [step, setStep] = useState(0);
  // Pre-fill from offer pagata (notifica bell "Crea polizza ora")
  const fromOfferId = (typeof window !== "undefined") ? new URLSearchParams(window.location.search).get("fromOffer") : null;

  // Step 1: client search/create
  const [clientMode, setClientMode] = useState(null); // 'existing' | 'new'
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  // 🆕 Feb 2026 — Editor rapido dei campi cliente dalla barra sticky (senza uscire dal wizard).
  // { key, label, type, value, currentAddress? } | null
  const [clientQuickEdit, setClientQuickEdit] = useState(null);
  const [clientQuickEditBusy, setClientQuickEditBusy] = useState(false);
  // 🆕 Feb 2026 — Modale "Modifica cliente" completa (tutti i campi editabili), sovrapposta al wizard.
  //   Diversa dal quick-edit (singolo campo) — apre un form completo e al salvataggio aggiorna
  //   `selectedClient` in-place per riflettere subito i dati nella scheda del wizard.
  const [clientFullEdit, setClientFullEdit] = useState(null); // form snapshot | null
  const [clientFullEditBusy, setClientFullEditBusy] = useState(false);
  const openClientFullEdit = () => {
    if (!selectedClient?.id) return;
    setClientFullEdit({
      name: selectedClient.name || "",
      surname: selectedClient.surname || "",
      ragione_sociale: selectedClient.ragione_sociale || "",
      partita_iva: selectedClient.partita_iva || "",
      pec_email: selectedClient.pec_email || "",
      fiscal_code: selectedClient.fiscal_code || "",
      birth_date: selectedClient.birth_date || "",
      birth_place: selectedClient.birth_place || "",
      birth_province: selectedClient.birth_province || "",
      gender: selectedClient.gender || "",
      email: selectedClient.email || "",
      phone: selectedClient.phone || "",
      mobile: selectedClient.mobile || "",
      address: selectedClient.address || "",
      house_number: selectedClient.house_number || "",
      cap: selectedClient.cap || "",
      city: selectedClient.city || "",
      province: selectedClient.province || "",
    });
  };
  const saveClientFullEdit = async () => {
    if (!selectedClient?.id || !clientFullEdit) return;
    setClientFullEditBusy(true);
    try {
      const payload = { ...clientFullEdit };
      if (payload.fiscal_code) payload.fiscal_code = String(payload.fiscal_code).toUpperCase().trim();
      if (payload.partita_iva) payload.partita_iva = String(payload.partita_iva).trim();
      const { data } = await api.put(`/clients/${selectedClient.id}`, payload);
      // Aggiorno lo stato locale così i chip nella barra sticky si aggiornano immediatamente
      setSelectedClient((c) => ({ ...(c || {}), ...(data || payload) }));
      toast.success("Cliente aggiornato");
      setClientFullEdit(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setClientFullEditBusy(false);
    }
  };
  const openClientQuickEdit = (key, label, type = "text") => {
    if (!selectedClient?.id) return;
    let value = selectedClient[key] || "";
    // Per l'indirizzo apriamo un mini-form con più campi in un solo dialog
    if (key === "address_group") {
      setClientQuickEdit({
        key,
        label,
        type: "address",
        address: {
          address: selectedClient.address || "",
          house_number: selectedClient.house_number || "",
          cap: selectedClient.cap || "",
          city: selectedClient.city || "",
          province: selectedClient.province || "",
        },
      });
      return;
    }
    setClientQuickEdit({ key, label, type, value });
  };
  const saveClientQuickEdit = async () => {
    if (!selectedClient?.id || !clientQuickEdit) return;
    setClientQuickEditBusy(true);
    try {
      const payload = {};
      if (clientQuickEdit.key === "address_group") {
        Object.assign(payload, clientQuickEdit.address || {});
      } else {
        let v = clientQuickEdit.value;
        if (clientQuickEdit.key === "fiscal_code") v = (v || "").toUpperCase().trim();
        payload[clientQuickEdit.key] = v;
        // 🆕 Feb 2026 — Se l'utente ha decodificato il CF, salviamo anche i dati anagrafici derivati
        if (clientQuickEdit.key === "fiscal_code" && clientQuickEdit.decoded) {
          const d = clientQuickEdit.decoded;
          if (d.birth_date) payload.birth_date = d.birth_date;
          if (d.birth_place) payload.birth_place = d.birth_place;
          if (d.birth_province) payload.birth_province = d.birth_province;
          if (d.gender) payload.gender = d.gender;
        }
      }
      await api.put(`/clients/${selectedClient.id}`, payload);
      // Aggiorna localmente per rendere il refresh immediato in barra
      setSelectedClient((c) => ({ ...(c || {}), ...payload }));
      toast.success("Cliente aggiornato");
      setClientQuickEdit(null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setClientQuickEditBusy(false);
    }
  };
  // 🆕 Feb 2026 — Decodifica CF nel quick-edit: pre-compila data e luogo di nascita
  const [cfDecodingInQuickEdit, setCfDecodingInQuickEdit] = useState(false);
  const decodeCfInQuickEdit = async () => {
    const cf = ((clientQuickEdit?.value) || "").toUpperCase().trim();
    if (cf.length !== 16) {
      toast.error("Il codice fiscale deve avere 16 caratteri");
      return;
    }
    setCfDecodingInQuickEdit(true);
    try {
      const { data } = await api.post("/utils/decode-fiscal-code", { fiscal_code: cf });
      setClientQuickEdit((s) => s ? ({
        ...s,
        value: cf,
        decoded: {
          birth_date: data.birth_date || null,
          birth_place: data.birth_place || null,
          birth_province: data.birth_province || null,
          gender: data.gender || null,
        },
      }) : s);
      toast.success(`Decodificato: ${data.birth_date || "—"} · ${data.birth_place || "—"}${data.birth_province ? ` (${data.birth_province})` : ""}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCfDecodingInQuickEdit(false);
    }
  };
  const [newClient, setNewClient] = useState({
    subject_type: null, // 🆕 Feb 2026 — Privato/Azienda: obbligatorio, forza scelta prima di compilare
    // Campi comuni a entrambi
    name: "", surname: "", email: "", phone: "", mobile: "",
    fiscal_code: "", birth_date: "", gender: "M", birth_place: "", belfiore_code: "",
    address: "", house_number: "", city: "", province: "", cap: "",
    // 🆕 Campi Azienda (Persona Giuridica)
    ragione_sociale: "", partita_iva: "", sdi_code: "", pec_email: "",
    legale_rappresentante_name: "", legale_rappresentante_surname: "", legale_rappresentante_fiscal_code: "",
    legale_rappresentante_birth_date: "", legale_rappresentante_birth_place: "", legale_rappresentante_birth_province: "",
    legale_rappresentante_gender: "", legale_rappresentante_role: "", legale_rappresentante_nationality: "IT",
    // 🆕 Feb 2026 — Contatti diretti Legale Rappresentante (usati per firma OTP azienda)
    legale_rappresentante_email: "", legale_rappresentante_mobile: "",
  });
  const [cfManual, setCfManual] = useState(false);
  // 🆕 Estrazione dati cliente da PDF polizza (crea nuovo cliente senza doverli digitare)
  const [extractingClientFromPolicy, setExtractingClientFromPolicy] = useState(false);
  const [extractedFromPolicy, setExtractedFromPolicy] = useState(false); // badge "🤖 Precompilato da PDF polizza"
  // 🆕 15/07/2026 — Drag & drop feedback per "Estrai da PDF polizza" nel wizard cliente
  const [isDraggingClientPdf, setIsDraggingClientPdf] = useState(false);
  // 🆕 Task 2: CF duplicate dialog
  const [dupClientDialog, setDupClientDialog] = useState(null); // { client, reason } | null
  const [creatingClient, setCreatingClient] = useState(false);
  const [lrCfLoading, setLrCfLoading] = useState(false);
  // 🆕 Feb 2026 — Ricerca cliente + generatore CF nella sezione Bersani
  const [bersaniSearchQ, setBersaniSearchQ] = useState("");
  const [bersaniSearchResults, setBersaniSearchResults] = useState([]);
  const [bersaniSearching, setBersaniSearching] = useState(false);
  const [bersaniCfBusy, setBersaniCfBusy] = useState(false);
  // 🆕 Feb 2026 — Stati Bersani: override targa formato speciale, dropdown compagnia, upload libretto
  const [bersaniPlateOverride, setBersaniPlateOverride] = useState(false);
  const [bersaniCompanyOpen, setBersaniCompanyOpen] = useState(false);
  const [bersaniLibrettoFile, setBersaniLibrettoFile] = useState(null);
  // Ricerca cliente Bersani debounced (300 ms) — corregge bug parsing (endpoint restituisce array, non {items:[…]})
  const searchBersaniClients = useMemo(() => debounce(async (q) => {
    if (!q || q.trim().length < 2) { setBersaniSearchResults([]); return; }
    setBersaniSearching(true);
    try {
      const { data } = await api.get(`/clients?q=${encodeURIComponent(q.trim())}&limit=8`);
      setBersaniSearchResults(Array.isArray(data) ? data.slice(0, 8) : []);
    } catch (e) {
      setBersaniSearchResults([]);
    } finally {
      setBersaniSearching(false);
    }
  }, 300), []);
  const applyBersaniClient = (c) => {
    setPolicy((p) => ({
      ...p,
      bersani_ref_owner: [c.name, c.surname].filter(Boolean).join(" ").trim() || p.bersani_ref_owner,
      bersani_ref_owner_name: c.name || p.bersani_ref_owner_name,
      bersani_ref_owner_surname: c.surname || p.bersani_ref_owner_surname,
      bersani_ref_owner_fiscal_code: c.fiscal_code || p.bersani_ref_owner_fiscal_code,
      bersani_ref_owner_birth_date: c.birth_date || p.bersani_ref_owner_birth_date,
      bersani_ref_owner_birth_place: c.birth_place || p.bersani_ref_owner_birth_place,
      bersani_ref_owner_gender: c.gender || p.bersani_ref_owner_gender,
    }));
    setBersaniSearchResults([]);
    setBersaniSearchQ("");
    toast.success(`Dati importati: ${c.name || ""} ${c.surname || ""}`);
  };
  const generateBersaniCf = async () => {
    const nm = (policy.bersani_ref_owner_name || "").trim();
    const sn = (policy.bersani_ref_owner_surname || "").trim();
    const bd = (policy.bersani_ref_owner_birth_date || "").trim();
    const bp = (policy.bersani_ref_owner_birth_place || "").trim();
    const gd = (policy.bersani_ref_owner_gender || "").trim();
    if (!nm || !sn || !bd || !bp || !gd) {
      toast.error("Compila Nome, Cognome, Data di nascita, Luogo di nascita e Sesso");
      return;
    }
    setBersaniCfBusy(true);
    try {
      const { data } = await api.post("/utils/fiscal-code", { name: nm, surname: sn, birth_date: bd, birth_place: bp, gender: gd });
      setPolicy((p) => ({ ...p, bersani_ref_owner_fiscal_code: (data.fiscal_code || "").toUpperCase() }));
      toast.success(`CF generato: ${data.fiscal_code}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBersaniCfBusy(false);
    }
  };

  // 🆕 Feb 2026 — Calcolo CF del Legale Rappresentante (backend risolve belfiore code)
  const calculateLRCF = async () => {
    const n = (newClient.legale_rappresentante_name || "").trim();
    const s = (newClient.legale_rappresentante_surname || "").trim();
    const bd = (newClient.legale_rappresentante_birth_date || "").trim();
    const bp = (newClient.legale_rappresentante_birth_place || "").trim();
    const g = (newClient.legale_rappresentante_gender || "").trim();
    if (!n || !s || !bd || !bp || !g) {
      toast.error("LR: compila nome, cognome, data e luogo di nascita, sesso");
      return;
    }
    setLrCfLoading(true);
    try {
      const { data } = await api.post("/utils/fiscal-code", {
        name: n, surname: s, birth_date: bd, birth_place: bp, gender: g,
      });
      setNewClient((prev) => ({ ...prev, legale_rappresentante_fiscal_code: data.fiscal_code }));
      toast.success(`CF Legale Rappresentante: ${data.fiscal_code}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLrCfLoading(false); }
  };

  // 🆕 Feb 2026 — Decodifica CF Legale Rappresentante → popola data/luogo/sesso/provincia
  const decodeLRCF = async () => {
    const cf = (newClient.legale_rappresentante_fiscal_code || "").trim().toUpperCase();
    if (cf.length !== 16) {
      toast.error("Inserisci un CF L.R. di 16 caratteri");
      return;
    }
    setLrCfLoading(true);
    try {
      const { data } = await api.post("/utils/decode-fiscal-code", { fiscal_code: cf });
      if (data.gender) genderManualRef.current.add("legale_rappresentante_gender");
      setNewClient((prev) => ({
        ...prev,
        legale_rappresentante_birth_date: data.birth_date || prev.legale_rappresentante_birth_date,
        legale_rappresentante_gender: data.gender || prev.legale_rappresentante_gender,
        legale_rappresentante_birth_place: (data.birth_place || prev.legale_rappresentante_birth_place || "").toUpperCase(),
        legale_rappresentante_birth_province: (data.birth_province || prev.legale_rappresentante_birth_province || "").toUpperCase(),
      }));
      toast.success(`LR decodificato: ${data.birth_date || "—"} · ${data.gender || "—"} · ${data.birth_place || "—"}${data.birth_province ? ` (${data.birth_province})` : ""}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLrCfLoading(false); }
  };

  // Auto-compute CF when personal data is complete
  useEffect(() => {
    if (cfManual) return;
    const cf = computeFiscalCode({
      name: newClient.name,
      surname: newClient.surname,
      gender: newClient.gender,
      birth_date: newClient.birth_date,
      belfiore_code: newClient.belfiore_code,
    });
    if (cf && cf !== newClient.fiscal_code) {
      setNewClient((prev) => ({ ...prev, fiscal_code: cf }));
    }
  }, [newClient.name, newClient.surname, newClient.gender, newClient.birth_date, newClient.belfiore_code, cfManual, newClient.fiscal_code]);

  // Step 2: client documents uploaded inline
  const [clientDocs, setClientDocs] = useState([]);
  const clientDocInputRef = useRef(null);
  const [docKind, setDocKind] = useState("carta_identita");
  const [uploadingDoc, setUploadingDoc] = useState(false);

  // Step 3: policy data
  const [companies, setCompanies] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [partnerCompanyLinks, setPartnerCompanyLinks] = useState([]);
  const [policyTypes, setPolicyTypes] = useState(POLICY_TYPES); // fallback statico finché non arriva l'API
  const [policy, setPolicy] = useState({
    type: "auto", policy_number: "", company_id: "", broker_id: "",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: (() => {
      const d = new Date();
      d.setFullYear(d.getFullYear() + 1);
      return d.toISOString().slice(0, 10);
    })(),
    duration_years: 1, premium: 0, commission_amount: 0,
    frazionamento: "annuale", notes: "", coverages: [],
    requested_coverages: [], // 🆕 Task 3: garanzie richieste prima del salvataggio
    driving_type: "", // 🆕 Tipo di guida (esclusiva/esperta/libera) — da lookup-list
    profession: "", // 🆕 Professione contraente — da lookup-list "profession"
    marital_status: "", // 🆕 Stato civile contraente — da lookup-list "marital_status"
    vehicle_additional_fuel: "", // 🆕 Alimentazione aggiuntiva veicolo — da lookup-list "additional_fuel"
    vehicle_plate: "", vehicle_brand: "", vehicle_model: "",
    chassis_number: "", // 🆕 Task 1: numero telaio/VIN nativo
    vehicle_first_registration: "", vehicle_fuel: "",
    // 🆕 Feb 2026 — Anno immatricolazione + Data acquisto (passaggio proprietà) dal libretto
    vehicle_year: "", vehicle_purchase_date: "",
    vehicle_kw: "", vehicle_displacement: "",
    last_review_date: "", next_review_date: "", // 🆕 Task 1: data revisione + scadenza
    source: "manuale", source_quote_id: "",
  });
  const [clientQuotes, setClientQuotes] = useState([]);

  // 🆕 Feb 2026 — Garanzie della compagnia selezionata (per auto-precompilazione)
  const [companyGuarantees, setCompanyGuarantees] = useState([]);
  const [guaranteesPickerOpen, setGuaranteesPickerOpen] = useState(false);
  const [guaranteesPickerSelected, setGuaranteesPickerSelected] = useState(() => new Set());

  // 🆕 15/07/2026 — Prodotti dal Catalogo Prodotti (filtrati per compagnia)
  const [companyProducts, setCompanyProducts] = useState([]);
  const [productsPickerOpen, setProductsPickerOpen] = useState(false);
  const [productsPickerSelected, setProductsPickerSelected] = useState(() => new Set());

  // 🆕 Lookup lists per Tipo guida + Garanzie (da admin/lookup-lists)
  const [drivingTypeOptions, setDrivingTypeOptions] = useState([]);
  const [coverageOptions, setCoverageOptions] = useState([]);
  // 🆕 Feb 2026 — Lookup lists Professione, Stato civile, Alimentazione aggiuntiva
  const [professionOptions, setProfessionOptions] = useState([]);
  const [maritalStatusOptions, setMaritalStatusOptions] = useState([]);
  const [additionalFuelOptions, setAdditionalFuelOptions] = useState([]);
  const [fuelOptions, setFuelOptions] = useState([]);
  // 🆕 Feb 2026 — Situazione assicurativa (per wizard preventivo)
  const [insuranceStatusOptions, setInsuranceStatusOptions] = useState([]);
  // 🆕 Feb 2026 — Attestato di rischio altro veicolo (campo obbligatorio)
  const [bersaniSourceOptions, setBersaniSourceOptions] = useState([]);
  useEffect(() => {
    api.get("/lookup-lists/driving_type").then(({ data }) => setDrivingTypeOptions((data || []).filter((x) => x.is_active !== false))).catch(() => {});
    api.get("/lookup-lists/coverages").then(({ data }) => setCoverageOptions((data || []).filter((x) => x.is_active !== false))).catch(() => {});
    api.get("/lookup-lists/profession").then(({ data }) => setProfessionOptions((data || []).filter((x) => x.is_active !== false))).catch(() => {});
    api.get("/lookup-lists/marital_status").then(({ data }) => setMaritalStatusOptions((data || []).filter((x) => x.is_active !== false))).catch(() => {});
    api.get("/lookup-lists/additional_fuel").then(({ data }) => setAdditionalFuelOptions((data || []).filter((x) => x.is_active !== false))).catch(() => {});
    api.get("/lookup-lists/fuel").then(({ data }) => setFuelOptions((data || []).filter((x) => x.is_active !== false))).catch(() => {});
    api.get("/lookup-lists/insurance_status").then(({ data }) => setInsuranceStatusOptions((data || []).filter((x) => x.is_active !== false))).catch(() => {});
    api.get("/lookup-lists/bersani_source").then(({ data }) => setBersaniSourceOptions((data || []).filter((x) => x.is_active !== false))).catch(() => {});
  }, []);

  // Carica marche/modelli per autocomplete (dataset + DB)
  const [dbBrands, setDbBrands] = useState([]);
  const [dbModels, setDbModels] = useState([]);
  useEffect(() => {
    api.get("/utils/vehicle-brands").then(({ data }) => setDbBrands(data.brands || [])).catch(() => {});
  }, []);
  useEffect(() => {
    const b = (policy.vehicle_brand || "").trim();
    if (!b) { setDbModels([]); return; }
    api.get("/utils/vehicle-models", { params: { brand: b } })
      .then(({ data }) => setDbModels(data.models || []))
      .catch(() => setDbModels([]));
  }, [policy.vehicle_brand]);
  const wizardBrandSuggestions = useMemo(() => [...new Set([...COMMON_BRANDS, ...dbBrands])], [dbBrands]);
  const wizardModelSuggestions = useMemo(() => {
    const key = (policy.vehicle_brand || "").toLowerCase();
    const fromDataset = COMMON_MODELS_BY_BRAND[key] || [];
    return [...new Set([...fromDataset, ...dbModels])];
  }, [policy.vehicle_brand, dbModels]);

  // Carica i preventivi del cliente selezionato (per il campo "Origine polizza")
  useEffect(() => {
    if (!selectedClient?.id) {
      setClientQuotes([]);
      return;
    }
    api.get("/quotes")
      .then((r) => {
        const own = (r.data || []).filter((q) => q.client_id === selectedClient.id && q.status !== "rifiutato");
        setClientQuotes(own);
      })
      .catch(() => setClientQuotes([]));
  }, [selectedClient?.id]);

  // 🆕 Feb 2026 — Carica le garanzie della compagnia selezionata
  useEffect(() => {
    const cid = policy.company_id;
    if (!cid) { setCompanyGuarantees([]); return; }
    api.get(`/companies/${cid}/guarantees`)
      .then(({ data }) => setCompanyGuarantees(Array.isArray(data) ? data : []))
      .catch(() => setCompanyGuarantees([]));
  }, [policy.company_id]);

  // 🆕 15/07/2026 — Carica i prodotti dal Catalogo Prodotti per la compagnia selezionata
  useEffect(() => {
    const cid = policy.company_id;
    if (!cid) { setCompanyProducts([]); return; }
    api.get("/products", { params: { company_id: cid, is_active: true } })
      .then(({ data }) => setCompanyProducts(Array.isArray(data) ? data : []))
      .catch(() => setCompanyProducts([]));
  }, [policy.company_id]);

  // Prefill da offerta finale pagata (NotificationBell → "Crea polizza ora")
  useEffect(() => {
    if (!fromOfferId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: offer } = await api.get(`/quote-offers/${fromOfferId}`);
        if (cancelled || !offer) return;
        const [{ data: client }, { data: quote }] = await Promise.all([
          api.get(`/clients/${offer.client_id}`),
          offer.quote_id ? api.get(`/quotes/${offer.quote_id}`).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
        ]);
        if (cancelled || !client) return;
        setSelectedClient(client);
        setClientMode("existing");
        // Pre-popola policy con dati offerta+preventivo
        setPolicy((p) => ({
          ...p,
          type: quote?.type || p.type,
          premium: Number(offer.amount) || 0,
          notes: offer.notes ? `Da offerta ${offer.offer_number}: ${offer.notes}` : `Da offerta ${offer.offer_number}`,
          coverages: (offer.coverages || []).map((c) => {
            // 🆕 Feb 2026 — Propaga % imposte/SSN e calcola premio netto dal lordo
            const impostePct = c._imposte_percent != null ? Number(c._imposte_percent)
              : (c.imposte_percent != null ? Number(c.imposte_percent) : null);
            const ssnPct = c._ssn_percent != null ? Number(c._ssn_percent)
              : (c.ssn_percent != null ? Number(c.ssn_percent) : null);
            const gross = Number(c.premium) || 0;
            let netPremium = null;
            if (gross > 0 && (impostePct != null || ssnPct != null)) {
              const totPct = (impostePct || 0) + (ssnPct || 0);
              netPremium = totPct > 0 ? Math.round((gross / (1 + totPct / 100)) * 100) / 100 : gross;
            }
            return {
              name: c.name,
              sum_insured: c.sum_insured || 0,
              premium: c.premium || 0,
              deductible: c.deductible || "",
              description: c.description || "",
              active: true,
              imposte_percent: impostePct,
              ssn_percent: ssnPct,
              net_premium: netPremium,
            };
          }),
          source: "da_preventivo",
          source_quote_id: offer.quote_id || "",
        }));
        // Salta direttamente allo step 2 (dati polizza)
        setStep(2);
        toast.success(`Dati pre-caricati da offerta ${offer.offer_number}`);
      } catch (e) {
        toast.error(formatApiError(e) || "Impossibile caricare offerta");
      }
    })();
    return () => { cancelled = true; };
  }, [fromOfferId]);

  // Step 4: policy attachments (post-create)
  const [policyAttachments, setPolicyAttachments] = useState([]);
  const policyAttInputRef = useRef(null);
  const [uploadingAtt, setUploadingAtt] = useState(false);

  // Rinomina file allegato (client doc o policy attachment)
  const promptRenameAttachment = async (att, kind /* "client" | "policy" */) => {
    const current = att.original_filename || "";
    // Estrai estensione per preservarla nel suggerimento
    const dotIdx = current.lastIndexOf(".");
    const ext = dotIdx >= 0 ? current.slice(dotIdx) : "";
    const base = dotIdx >= 0 ? current.slice(0, dotIdx) : current;
    const input = window.prompt("Nuovo nome del file (l'estensione viene preservata):", base);
    if (input === null) return;
    const cleaned = input.trim();
    if (!cleaned) { toast.error("Il nome non può essere vuoto"); return; }
    // Riapplica estensione se l'utente non l'ha messa
    let newName = cleaned;
    if (ext && !newName.toLowerCase().endsWith(ext.toLowerCase())) {
      newName = newName + ext;
    }
    if (newName === current) return;
    try {
      const { data } = await api.patch(`/attachments/${att.id}/rename`, { filename: newName });
      const finalName = data?.filename || newName;
      if (kind === "client") {
        setClientDocs((prev) => prev.map((x) => x.id === att.id ? { ...x, original_filename: finalName } : x));
      } else {
        setPolicyAttachments((prev) => prev.map((x) => x.id === att.id ? { ...x, original_filename: finalName } : x));
      }
      toast.success("File rinominato");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore durante la rinomina");
    }
  };

  // Estrazione AI da PDF polizza esistente
  const [polPdfUploading, setPolPdfUploading] = useState(false);
  const [polPdfMismatch, setPolPdfMismatch] = useState(null);
  const [polGuarantees, setPolGuarantees] = useState([]);
  const [policyPdfFile, setPolicyPdfFile] = useState(null); // file PDF analizzato → allegato auto
  const polPdfInputRef = useRef(null);
  const extractFromPolicyPDF = async (file) => {
    if (!file) return;
    setPolPdfUploading(true);
    setPolPdfMismatch(null);
    setPolGuarantees([]);
    setPolicyPdfFile(file);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await uploadWithProgress({
        label: `📄 Analisi PDF polizza: ${file.name}`,
        run: (cfg) => api.post("/utils/extract-pdf?kind=polizza", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 90000,
          onUploadProgress: cfg.onUploadProgress,
        }),
      });
      const x = data?.extracted || {};
      // Normalizza garanzie -> formato PolicyCoverage del backend
      const guarantees = Array.isArray(x.guarantees) ? x.guarantees.filter((g) => g && (g.name || g.code)) : [];
      const toNum = (v) => {
        if (v == null) return null;
        if (typeof v === "number") return v;
        const n = Number(String(v).replace(/[^\d.,-]/g, "").replace(/\.(?=\d{3}(\D|$))/g, "").replace(",", "."));
        return isNaN(n) ? null : n;
      };
      // 🆕 22/07/2026 — Auto-match compagnia + garanzie master (per ereditare Imposte% / SSN%).
      // 🆕 Feb 2026 — Matching più tollerante: normalizza NFD + rimuove forme societarie prima del confronto.
      const stripLegal = (s) => (s || "")
        .replace(/\b(s\s?\.?\s?p\s?\.?\s?a|s\s?\.?\s?r\s?\.?\s?l|s\s?\.?\s?a\s?\.?\s?s|s\s?\.?\s?n\s?\.?\s?c|d\s?\.?\s?o\s?\.?\s?o|d\s?\.?\s?d|a\s?\.?\s?g|gmbh|ltd|plc|inc|corp|co)\b\.?/gi, " ")
        .replace(/\b(assicurazioni|assicurazione|assicura|insurance|compagnia|group|gruppo|italia|spa|srl)\b/gi, " ");
      const normName = (s) => stripLegal(s || "").toString()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      let matchedCompany = null;
      if (x.company && companies.length) {
        matchedCompany = companies.find((c) => c.name.toLowerCase().trim() === String(x.company).toLowerCase().trim())
          || companies.find((c) => normName(c.name) === normName(x.company));
        // 🆕 Feb 2026 — Se compagnia non riconosciuta, avvisa (non blocca il flusso)
        if (!matchedCompany) {
          toast.warning(`Compagnia "${x.company}" non riconosciuta nel listino: le garanzie non erediteranno imposte/SSN dalla compagnia. Verifica il nome nel menù Compagnie.`, { duration: 6000 });
        }
      }
      let masterGuarantees = [];
      if (matchedCompany?.id) {
        try {
          const { data: mg } = await api.get(`/companies/${matchedCompany.id}/guarantees`);
          masterGuarantees = Array.isArray(mg) ? mg : [];
        } catch { /* la master può non essere configurata */ }
      }
      const findMaster = (extractedName) => resolveGuarantee(extractedName, masterGuarantees);
      const coverages = guarantees.map((g) => {
        const extractedName = g.name || g.code || "";
        const master = findMaster(extractedName);
        const grossPremium = toNum(g.premium);
        let netFromPdf = toNum(g.net_premium);
        const taxesFromPdf = toNum(g.taxes_amount);
        const ssnFromPdf = toNum(g.ssn_amount);
        const impostePct = master?.imposte_percent != null ? Number(master.imposte_percent) : null;
        const ssnPct = master?.ssn_percent != null ? Number(master.ssn_percent) : null;
        // Se il PDF non riporta il netto ma abbiamo le % dalla master → calcolo netto = lordo / (1 + (imp+ssn)/100)
        let computedNet = null;
        if (grossPremium != null && grossPremium > 0) {
          if (netFromPdf != null && netFromPdf > 0) {
            computedNet = netFromPdf;
          } else if (impostePct != null || ssnPct != null) {
            const totPct = (impostePct || 0) + (ssnPct || 0);
            computedNet = totPct > 0 ? Math.round((grossPremium / (1 + totPct / 100)) * 100) / 100 : grossPremium;
          }
        }
        return {
          code: buildGuaranteeCode(g.code || (master?.name || extractedName)),
          // 🆕 Feb 2026 — Nome canonico da listino (se agganciato), altrimenti nome estratto.
          name: master?.name || extractedName || "Garanzia",
          extracted_name: master ? extractedName : null,   // 🆕 conserva etichetta originale se rinominata
          _unmatched: !master,                              // 🆕 flag per UI: garanzia non collegata
          active: true,
          premium: grossPremium,
          net_premium: computedNet,
          sum_insured: toNum(g.sum_insured),
          deductible: g.deductible == null ? null : String(g.deductible),
          company_guarantee_id: master?.id || null,
          imposte_percent: impostePct,
          ssn_percent: ssnPct,
          taxes_amount: taxesFromPdf,
          ssn_amount: ssnFromPdf,
        };
      });
      setPolGuarantees(guarantees);
      setPolicy((prev) => ({
        ...prev,
        type: x.type || prev.type,
        policy_number: x.policy_number || prev.policy_number,
        start_date: x.start_date || prev.start_date,
        end_date: x.end_date || prev.end_date,
        premium: x.premium ?? prev.premium,
        frazionamento: x.frazionamento || prev.frazionamento,
        coverages: coverages.length ? coverages : prev.coverages,
        ...(matchedCompany ? { company_id: matchedCompany.id } : {}),
      }));
      // Verifica intestatario polizza vs cliente
      const norm = (s) => (s || "")
        .toString()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // rimuove accenti
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      const polFirst = norm(x.client_name);
      const polLast = norm(x.client_surname);
      const polFullRaw = `${polFirst} ${polLast}`.trim() || norm(x.client_full_name);
      const polCF = (x.client_fiscal_code || "").toUpperCase().replace(/\s+/g, "").trim();
      const cliFirst = norm(selectedClient?.name);
      const cliLast = norm(selectedClient?.surname);
      const cliCF = (selectedClient?.fiscal_code || "").toUpperCase().replace(/\s+/g, "").trim();

      // CF check
      const cfPresent = polCF && cliCF;
      const cfMatch = cfPresent && polCF === cliCF;
      // Match nome: tokenizza entrambi e verifica intersezione (almeno un token comune di >=3 char)
      const polTokens = new Set(polFullRaw.split(" ").filter((t) => t.length >= 3));
      const cliTokens = new Set([cliFirst, cliLast].join(" ").split(" ").filter((t) => t.length >= 3));
      const intersection = [...polTokens].filter((t) => cliTokens.has(t));
      const nameMatch = intersection.length >= 1;
      const noPolData = !polFullRaw && !polCF; // AI non ha trovato proprio nulla → niente warning

      // Mostra warning SOLO quando NESSUN criterio matcha:
      // - CF diversi (se presenti su entrambi) E
      // - nessun token nome in comune
      // Se almeno uno tra CF o nome corrisponde → verde.
      const isMismatch = !noPolData && !cfMatch && !nameMatch;
      if (isMismatch) {
        setPolPdfMismatch({
          color: "amber",
          message: `⚠️ ATTENZIONE: i dati del cliente sulla polizza differiscono da quelli del cliente selezionato (${selectedClient?.surname} ${selectedClient?.name}).`,
          polClient: polFullRaw.toUpperCase() || "—",
          polCF: polCF || "—",
        });
      } else {
        setPolPdfMismatch({
          color: "emerald",
          message: `✅ Dati cliente verificati: la polizza è intestata a ${selectedClient?.surname} ${selectedClient?.name}.`,
          polClient: polFullRaw.toUpperCase() || "—",
          polCF: polCF || "—",
        });
      }
      toast.success(`Dati ${targetName} estratti dall'AI`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPolPdfUploading(false); }
  };
  const onPolPdfSelect = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) extractFromPolicyPDF(f);
  };

  const [creating, setCreating] = useState(false);
  const [createdPolicyId, setCreatedPolicyId] = useState(null);

  // Libretto AI extraction state
  const [librettoFile, setLibrettoFile] = useState(null);
  const [librettoData, setLibrettoData] = useState(null);
  const [librettoMatch, setLibrettoMatch] = useState(null); // { match, message, color }
  const [librettoPending, setLibrettoPending] = useState(null); // dati AI in attesa di scelta utente
  // 🆕 Feb 2026 — Match su preventivo esistente (lead) quando l'anagrafica estratta non è
  // in archivio clienti. Shape: { quote: {quote_number, owner_*}, reason: "cf"|"name" }
  const [librettoQuoteMatch, setLibrettoQuoteMatch] = useState(null);
  const librettoInputRef = useRef(null);
  const [librettoUploading, setLibrettoUploading] = useState(false);
  const [visuraUploading, setVisuraUploading] = useState(false); // 🆕 Feb 2026 — OCR Visura Camerale (azienda)
  const [visuraMessage, setVisuraMessage] = useState(null); // banner riepilogo estrazione

  // Scelta utente quando l'AI non ha trovato il cliente in archivio
  const chooseSearchExisting = () => {
    if (!librettoPending) return;
    setClientMode("existing");
    // Pre-compila la ricerca con il cognome (più affidabile del CF non rilevato)
    setSearchQ((librettoPending.surname || librettoPending.name || "").trim());
    setLibrettoMatch((m) => m ? { ...m, showChoice: false, message: `🔍 Cerca in archivio: pre-popolato "${librettoPending.surname || librettoPending.name}". Se non lo trovi, torna indietro e scegli "Crea nuovo".` } : m);
  };

  const chooseCreateNew = () => {
    if (!librettoPending) return;
    setClientMode("new");
    setNewClient((prev) => ({
      ...prev,
      name: librettoPending.name || prev.name,
      surname: librettoPending.surname || prev.surname,
      fiscal_code: librettoPending.fiscal_code || prev.fiscal_code,
      birth_date: librettoPending.birth_date || prev.birth_date,
      birth_place: librettoPending.birth_place || prev.birth_place,
      belfiore_code: librettoPending.belfiore_code || prev.belfiore_code,
      gender: librettoPending.gender || prev.gender,
      address: librettoPending.address || prev.address,
      house_number: librettoPending.house_number || prev.house_number,
      city: librettoPending.city || prev.city,
      province: librettoPending.province || prev.province,
      cap: librettoPending.cap || prev.cap,
    }));
    setCfManual(!!librettoPending.fiscal_code);
    setLibrettoMatch((m) => m ? { ...m, showChoice: false, message: `✏️ Crea nuovo cliente: dati precompilati dal libretto. Verifica e salva.` } : m);
  };

  const processLibretto = async (file) => {
    if (!file) return;
    setLibrettoUploading(true);
    setLibrettoMatch(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await uploadWithProgress({
        label: `🚗 Analisi libretto: ${file.name}`,
        run: (cfg) => api.post("/utils/extract-libretto", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 90000,
          onUploadProgress: cfg.onUploadProgress,
        }),
      });
      const x = data?.extracted || {};
      setLibrettoData(x);
      setLibrettoFile(file);
      // Cerca cliente per CF
      const cfExtracted = (x.owner_fiscal_code || "").toUpperCase().trim();
      let matched = null;
      if (cfExtracted) {
        try {
          const r = await api.get("/clients", { params: { q: cfExtracted } });
          matched = (r.data || []).find((c) => (c.fiscal_code || "").toUpperCase() === cfExtracted);
        } catch { /* ignore */ }
      }
      if (matched) {
        // Recupera dettaglio completo (in modo da avere TUTTI i campi: email, telefoni, indirizzo, ecc.)
        try {
          const detail = await api.get(`/clients/${matched.id}`);
          // L'endpoint ritorna {client, policies}: estrai solo il cliente
          if (detail.data?.client) matched = detail.data.client;
          else if (detail.data?.id) matched = detail.data;
        } catch { /* fallback al doc dalla lista */ }
        setSelectedClient(matched);
        setClientMode("existing");
        const contactsAvail = [];
        if (matched.email) contactsAvail.push("email");
        if (matched.mobile) contactsAvail.push("cellulare");
        if (matched.phone) contactsAvail.push("telefono");
        const contactsStr = contactsAvail.length ? ` Importati: ${contactsAvail.join(", ")}.` : " Nessun contatto in archivio.";
        setLibrettoMatch({
          match: true,
          color: "emerald",
          message: `✅ Intestatario verificato: ${matched.surname} ${matched.name} (CF ${cfExtracted}). Cliente già in archivio, lo abbiamo selezionato per te.${contactsStr}`,
          ownerName: `${x.owner_name || ""} ${x.owner_surname || ""}`.trim(),
          ownershipDate: x.ownership_date,
        });
      } else {
        const cfFromLibretto = (x.owner_fiscal_code || "").toUpperCase().trim();
        // Se l'AI non ha trovato il CF, prova a risolvere il comune di nascita per abilitare l'auto-calcolo
        let resolvedBelfiore = "";
        if (!cfFromLibretto && x.owner_birth_place) {
          try {
            const r = await api.get("/utils/cities", { params: { q: x.owner_birth_place } });
            const cities = r.data || [];
            const best = cities.find((c) => (c.name || "").toLowerCase().trim() === String(x.owner_birth_place).toLowerCase().trim()) || cities[0];
            if (best && best.code) resolvedBelfiore = best.code;
          } catch { /* ignore */ }
        }
        // Inferisci il sesso dal CF (carattere di genere = 7° gruppo, 1° char delle cifre del giorno: >40 → F)
        let inferredGender = "M";
        if (cfFromLibretto && cfFromLibretto.length >= 11) {
          const dayChars = cfFromLibretto.substring(9, 11);
          const dayNum = parseInt(dayChars, 10);
          if (!isNaN(dayNum) && dayNum > 40) inferredGender = "F";
        }
        // Salva i dati estratti per pre-compilare la ricerca o la creazione, NON imposta ancora il mode
        setLibrettoPending({
          name: x.owner_name || "",
          surname: x.owner_surname || "",
          fiscal_code: cfFromLibretto,
          birth_date: x.owner_birth_date || "",
          birth_place: x.owner_birth_place || "",
          belfiore_code: resolvedBelfiore,
          gender: cfFromLibretto ? inferredGender : "M",
          address: x.owner_address || "",
          house_number: x.owner_house_number || "",
          city: x.owner_city || "",
          province: x.owner_province || "",
          cap: x.owner_cap || "",
          previous: x.previous_owners,
          ownership_date: x.ownership_date,
        });
        const msgCFPart = cfFromLibretto
          ? `CF ${cfFromLibretto}`
          : (resolvedBelfiore ? "CF da calcolare automaticamente dai dati anagrafici" : "CF non rilevato");
        setLibrettoMatch({
          match: false,
          color: "amber",
          message: `⚠️ Intestatario NON trovato automaticamente in archivio: ${(x.owner_name || "").trim()} ${(x.owner_surname || "").trim()} (${msgCFPart}). Scegli come procedere:`,
          ownerName: `${x.owner_name || ""} ${x.owner_surname || ""}`.trim(),
          ownershipDate: x.ownership_date,
          previous: x.previous_owners,
          showChoice: true, // mostra i 2 pulsanti "Cerca in archivio" / "Crea nuovo"
        });
        // 🆕 Feb 2026 — Controllo aggiuntivo: esiste già un preventivo (lead) con lo stesso
        // CF o anagrafica? Serve a prevenire duplicati creati inconsapevolmente dall'utente.
        try {
          const params = {};
          if (cfFromLibretto) params.fiscal_code = cfFromLibretto;
          if (x.owner_surname) params.surname = x.owner_surname;
          if (x.owner_name) params.name = x.owner_name;
          if (x.owner_birth_date) params.birth_date = x.owner_birth_date;
          if (params.fiscal_code || (params.surname && params.name)) {
            const r = await api.get("/quotes/find-by-anagrafica", { params });
            if (r.data && r.data.quote) {
              setLibrettoQuoteMatch({ quote: r.data.quote, reason: r.data.reason });
            }
          }
        } catch { /* 404 o altro errore: niente match su lead, flusso normale */ }
        // NON imposta clientMode: l'utente sceglierà tra "Cerca in archivio" e "Crea nuovo"
      }
      // Precompila step 3 (polizza auto) con dati veicolo
      if (x.vehicle_plate) {
        setPolicy((prev) => ({
          ...prev,
          type: "auto",
          vehicle_plate: (x.vehicle_plate || "").toUpperCase(),
          vehicle_brand: x.vehicle_brand || prev.vehicle_brand,
          vehicle_model: x.vehicle_model || prev.vehicle_model,
          chassis_number: x.chassis_number || prev.chassis_number, // 🆕 Task 1: telaio nativo
          vehicle_first_registration: x.vehicle_first_registration || prev.vehicle_first_registration,
          // 🆕 Feb 2026 — riporta Anno immatricolazione + Data acquisto (passaggio proprietà) dal libretto
          vehicle_year: x.vehicle_year || prev.vehicle_year,
          vehicle_purchase_date: x.vehicle_purchase_date || prev.vehicle_purchase_date,
          vehicle_fuel: x.fuel || prev.vehicle_fuel,
          vehicle_kw: x.kw ?? prev.vehicle_kw,
          vehicle_displacement: x.displacement ?? prev.vehicle_displacement,
          last_review_date: x.last_review_date || prev.last_review_date, // 🆕 data ultima revisione
          next_review_date: x.next_review_date || prev.next_review_date, // 🆕 prossima revisione (scadenza)
          notes: [
            prev.notes,
            x.vehicle_version ? `Versione: ${x.vehicle_version}` : "",
          ].filter(Boolean).join("\n"),
        }));
      }
      toast.success("Libretto analizzato dall'AI");
    } catch (e) {
      // L'AI non è riuscita a leggere il libretto: salva comunque il file per usarlo come allegato
      // e mostra al banner manuale per procedere senza dati AI.
      setLibrettoFile(file);
      setLibrettoData(null);
      setLibrettoMatch({
        match: false,
        color: "amber",
        message: `⚠️ Il libretto non è stato letto correttamente dall'AI: ${formatApiError(e)}. Puoi procedere manualmente — il file rimane allegato e sarà salvato come documento della polizza a fine wizard.`,
        manualFallback: true,
      });
      toast.error("Libretto non leggibile, procedi manualmente. Il file rimane allegato.");
    }
    finally { setLibrettoUploading(false); }
  };

  // 🆕 Feb 2026 — Estrazione dati aziendali da Visura Camerale / PDF societario generico
  const processVisura = async (file) => {
    if (!file) return;
    setVisuraUploading(true);
    setVisuraMessage(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await uploadWithProgress({
        label: `🏢 Analisi visura camerale: ${file.name}`,
        run: (cfg) => api.post("/utils/extract-visura", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
          onUploadProgress: cfg.onUploadProgress,
        }),
      });
      const x = data?.extracted || {};
      // Precompila i campi Azienda (senza sovrascrivere quelli già valorizzati manualmente)
      setNewClient((prev) => ({
        ...prev,
        subject_type: "company",
        ragione_sociale: prev.ragione_sociale || x.ragione_sociale || "",
        partita_iva: prev.partita_iva || x.partita_iva || "",
        fiscal_code: prev.fiscal_code || x.codice_fiscale_societario || "",
        sdi_code: prev.sdi_code || x.sdi_code || "",
        pec_email: prev.pec_email || x.pec_email || "",
        email: prev.email || x.company_email || "",
        mobile: prev.mobile || x.company_phone || "",
        address: prev.address || x.sede_legale_address || "",
        house_number: prev.house_number || x.sede_legale_house_number || "",
        city: prev.city || (x.sede_legale_city || "").toUpperCase(),
        province: prev.province || (x.sede_legale_province || "").toUpperCase().slice(0, 2),
        cap: prev.cap || x.sede_legale_cap || "",
        legale_rappresentante_name: prev.legale_rappresentante_name || (x.legale_rappresentante_name || "").toUpperCase(),
        legale_rappresentante_surname: prev.legale_rappresentante_surname || (x.legale_rappresentante_surname || "").toUpperCase(),
        legale_rappresentante_fiscal_code: prev.legale_rappresentante_fiscal_code || (x.legale_rappresentante_fiscal_code || "").toUpperCase(),
        legale_rappresentante_birth_date: prev.legale_rappresentante_birth_date || x.legale_rappresentante_birth_date || "",
        legale_rappresentante_birth_place: prev.legale_rappresentante_birth_place || (x.legale_rappresentante_birth_place || "").toUpperCase(),
        legale_rappresentante_birth_province: prev.legale_rappresentante_birth_province || (x.legale_rappresentante_birth_province || "").toUpperCase().slice(0, 2),
        legale_rappresentante_gender: prev.legale_rappresentante_gender || (x.legale_rappresentante_gender || "").toUpperCase(),
        legale_rappresentante_role: prev.legale_rappresentante_role || x.legale_rappresentante_role || "",
        legale_rappresentante_nationality: prev.legale_rappresentante_nationality || (x.legale_rappresentante_nationality || "IT").toUpperCase(),
      }));
      const filled = Object.entries(x).filter(([, v]) => v !== null && v !== "" && v !== undefined).length;
      setVisuraMessage({
        color: "emerald",
        text: `🤖 Estratti ${filled} campi dalla Visura/PDF aziendale. Verifica e completa i dati mancanti prima di salvare.`,
      });
      toast.success(`Visura letta: ${filled} campi estratti`);
    } catch (e) {
      const errMsg = formatApiError(e) || "Errore estrazione Visura";
      setVisuraMessage({ color: "amber", text: `⚠️ ${errMsg}. Procedi con compilazione manuale.` });
      toast.error(errMsg);
    } finally {
      setVisuraUploading(false);
    }
  };

  const onLibrettoSelect = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) processLibretto(f);
  };

  // 🆕 20/07/2026 — Il libretto NON viene più salvato nell'anagrafica cliente.
  // Viene mantenuto solo in memoria (librettoFile) e allegato ESCLUSIVAMENTE alla polizza
  // al termine del wizard (vedi punti di allega in `createPolicy` con description="Libretto veicolo").

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/brokers").then((r) => setBrokers(r.data)).catch(() => {});
    api.get("/partner-company-links").then((r) => setPartnerCompanyLinks(r.data)).catch(() => {});
    api.get("/policy-types").then((r) => {
      if (Array.isArray(r.data) && r.data.length) {
        // Dedup by `code` — protegge dal bug UI "MotoMoto" quando due record hanno lo stesso code
        // (in shadcn/Radix Select duplicati con stesso value causano label concatenati nel trigger).
        const seen = new Set();
        const deduped = [];
        for (const t of r.data) {
          const code = t.code;
          if (!code || seen.has(code)) continue;
          seen.add(code);
          deduped.push({ v: code, l: t.label, icon: t.icon || "shield", is_pinned: !!t.is_pinned });
        }
        setPolicyTypes(deduped);
      }
    }).catch(() => {});
    // 🆕 Toast di conferma al ritorno dalla pagina Brokers dopo configurazione compagnie
    try {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get("from_brokers") === "1") {
        toast.success("Compagnie aggiornate — dropdown ricaricato");
        sp.delete("from_brokers");
        const q = sp.toString();
        window.history.replaceState(null, "", window.location.pathname + (q ? `?${q}` : ""));
      }
    } catch { /* silent */ }
  }, []);

  // === Step 1 handlers ===
  const doSearch = useMemo(() => debounce(async (q) => {
    if (q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get("/clients", { params: { q } });
      setSearchResults(data.slice(0, 12));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSearching(false); }
  }, 350), []);

  useEffect(() => { if (clientMode === "existing") doSearch(searchQ); }, [searchQ, clientMode, doSearch]);

  // 🆕 Helpers Broker→Compagnia filtering + linked-count badge (allineati a Policies.jsx / QuoteOffers.jsx)
  const getCompaniesForBroker = (brokerId) => {
    if (!brokerId) return companies;
    const links = (partnerCompanyLinks || []).filter(
      (l) => l.partner_id === brokerId && l.active !== false
    );
    if (links.length === 0) return companies;
    const ids = new Set(links.map((l) => l.linked_company_id));
    return (companies || []).filter((c) => ids.has(c.id));
  };
  const getLinkedCompaniesCount = (brokerId) => {
    if (!brokerId) return null;
    const count = (partnerCompanyLinks || []).filter(
      (l) => l.partner_id === brokerId && l.active !== false
    ).length;
    return count > 0 ? count : null;
  };

  const pickExistingClient = async (c) => {
    // Recupera scheda completa: la lista a volte ha campi minimi
    setSelectedClient(c); // optimistic
    try {
      const { data } = await api.get(`/clients/${c.id}`);
      // L'endpoint ritorna {client, policies}: estrai solo il cliente
      const fullClient = data?.client || (data?.id ? data : null);
      if (fullClient) setSelectedClient(fullClient);
    } catch { /* keep optimistic */ }
  };

  // 🆕 Estrae dati cliente da un PDF polizza e precompila il form "Nuovo cliente"
  const extractClientFromPolicyFile = async (file) => {
    if (!file) return;
    setExtractingClientFromPolicy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await uploadWithProgress({
        label: `👤 Estrazione cliente da PDF: ${file.name}`,
        run: (cfg) => api.post("/utils/extract-pdf?kind=polizza", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 90000,
          onUploadProgress: cfg.onUploadProgress,
        }),
      });
      const f = data?.extracted || {};
      const patch = {
        name: f.client_name || "",
        surname: f.client_surname || "",
        fiscal_code: (f.client_fiscal_code || "").toUpperCase().trim(),
        birth_date: f.client_birth_date || "",
        birth_place: f.client_birth_place || "",
        gender: f.client_gender === "F" ? "F" : "M",
        address: f.client_address || "",
        house_number: f.client_house_number || "",
        city: f.client_city || "",
        province: (f.client_province || "").toUpperCase().slice(0, 2),
        cap: f.client_cap || "",
        email: f.client_email || "",
        mobile: f.client_mobile || "",
      };
      const filled = Object.values(patch).filter((v) => v && String(v).trim()).length;
      if (filled === 0) {
        toast.warning("Nessun dato cliente estratto dal PDF. Compila manualmente.");
        setClientMode("new");
        return;
      }
      setCfManual(!!patch.fiscal_code); // se CF già presente, non ricalcolare automaticamente
      setNewClient((c) => ({ ...c, ...patch }));
      setExtractedFromPolicy(true);
      setClientMode("new");
      toast.success(`🤖 Dati cliente estratti dal PDF polizza (${filled} campi compilati). Verifica e completa se necessario.`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "errore sconosciuto";
      toast.error(`Estrazione fallita: ${msg}`);
    } finally {
      setExtractingClientFromPolicy(false);
    }
  };

  // 🆕 Task 2: cerca duplicati per CF / email / phone / mobile / nome+cognome+data (include LEAD)
  const findExistingDuplicate = async () => {
    try {
      const params = {
        fiscal_code: newClient.fiscal_code || undefined,
        email: newClient.email || undefined,
        phone: newClient.phone || undefined,
        mobile: newClient.mobile || undefined,
        surname: newClient.surname || undefined,
        name: newClient.name || undefined,
        birth_date: newClient.birth_date || undefined,
      };
      const r = await api.get("/clients/find-duplicate", { params });
      if (r.data?.client) return { client: r.data.client, reason: r.data.reason };
    } catch (e) {
      // 404 = nessun duplicato → ok
      if (e?.response?.status !== 404) {
        console.warn("find-duplicate error", e?.response?.status);
      }
    }
    return null;
  };

  // 🆕 Task 2: salvataggio "forzato" (utente ha confermato di voler creare un duplicato)
  const forceCreateClient = async () => {
    setDupClientDialog(null);
    setCreatingClient(true);
    try {
      const asLeadQuery = mode === "quote" ? "?as_lead=true&allow_duplicate=true" : "?allow_duplicate=true";
      const { data } = await api.post(`/clients${asLeadQuery}`, newClient);
      setSelectedClient(data);
      toast.success(mode === "quote" ? "Lead creato (forzato)" : "Cliente creato (forzato)");
      setStep(1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreatingClient(false);
    }
  };

  // Usa il cliente esistente al posto di crearne uno nuovo
  const useExistingDuplicate = async () => {
    if (!dupClientDialog?.client) return;
    setSelectedClient(dupClientDialog.client);
    setDupClientDialog(null);
    toast.success(`Cliente esistente selezionato: ${dupClientDialog.client.surname} ${dupClientDialog.client.name}`);
    setStep(1);
  };

  // 🆕 Feb 2026 — Crea il nuovo cliente forzando la duplicazione (email/cellulare condivisi)
  // E lo collega all'area privata del cliente duplicato esistente (uni-direzionale).
  // Utile per gestione familiare: es. moglie/figli con contatti in comune con il capo-famiglia.
  const forceCreateAndLinkPortal = async () => {
    if (!dupClientDialog?.client?.id) return;
    const existingClient = dupClientDialog.client;
    setDupClientDialog(null);
    setCreatingClient(true);
    try {
      const asLeadQuery = mode === "quote" ? "?as_lead=true&allow_duplicate=true" : "?allow_duplicate=true";
      const { data } = await api.post(`/clients${asLeadQuery}`, newClient);
      // Collega il nuovo cliente all'area privata del cliente esistente
      try {
        await api.post(`/clients/${existingClient.id}/portal-links`, { client_id: data.id });
        toast.success(`Cliente creato e collegato all'area privata di ${existingClient.surname} ${existingClient.name}`);
      } catch (linkErr) {
        toast.warning(`Cliente creato ma collegamento portale fallito: ${formatApiError(linkErr)}`);
      }
      setSelectedClient(data);
      setStep(1);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreatingClient(false);
    }
  };

  const createNewClient = async () => {
    // 🆕 Feb 2026 — Validazione differenziata Privato/Azienda
    if (!newClient.subject_type) { toast.error("Seleziona il tipo soggetto (Privato o Azienda)"); return; }
    if (newClient.subject_type === "company") {
      if (!(newClient.ragione_sociale || "").trim()) { toast.error("Ragione Sociale obbligatoria"); return; }
      const piva = (newClient.partita_iva || "").trim();
      if (!piva || piva.length !== 11 || !/^\d{11}$/.test(piva)) {
        toast.error("Partita IVA obbligatoria: 11 cifre numeriche");
        return;
      }
    } else {
      if (!newClient.name || !newClient.surname) { toast.error("Nome e cognome obbligatori"); return; }
    }
    // 🆕 Email obbligatoria salvo flag "Non disponibile"
    if (!newClient.email_unavailable && !(newClient.email || "").trim()) {
      toast.error("Email obbligatoria — oppure spunta 'Non disponibile' accanto al campo");
      return;
    }
    setCreatingClient(true);
    try {
      // 🆕 Task 2: cerca duplicati e, se trovati, mostra DIALOG (non auto-usa più)
      const dup = await findExistingDuplicate();
      if (dup) {
        setDupClientDialog(dup);
        setCreatingClient(false);
        return; // attendi scelta utente
      }

      // 🆕 Feb 2026 — Per Azienda, mappa ragione_sociale → name (per compatibilità con
      // le viste esistenti che usano name/surname); surname = "-" per soddisfare vincolo modello.
      const payload = { ...newClient };
      if (newClient.subject_type === "company") {
        payload.name = (newClient.ragione_sociale || "").trim();
        payload.surname = "-";
        // Per aziende non servono i campi anagrafici della persona fisica
        payload.gender = null;
        payload.birth_date = null;
        payload.birth_place = null;
        payload.belfiore_code = null;
      } else {
        // 🆕 Feb 2026 — Se il subject_type non è "company", azzera tutti i campi
        // aziendali del payload per evitare che valori residui/legacy vengano inviati al backend
        // (es. stringhe vuote su Literal/EmailStr → validation error).
        payload.ragione_sociale = null;
        payload.partita_iva = null;
        payload.codice_fiscale_societario = null;
        payload.sdi_code = null;
        payload.pec_email = null;
        payload.legale_rappresentante_name = null;
        payload.legale_rappresentante_surname = null;
        payload.legale_rappresentante_fiscal_code = null;
        payload.legale_rappresentante_gender = null;
        payload.legale_rappresentante_birth_date = null;
        payload.legale_rappresentante_birth_place = null;
        payload.legale_rappresentante_birth_province = null;
        payload.legale_rappresentante_role = null;
        payload.legale_rappresentante_nationality = null;
        // 🆕 Feb 2026 — Azzera anche contatti diretti LR (firma OTP azienda) per privati
        payload.legale_rappresentante_email = null;
        payload.legale_rappresentante_mobile = null;
      }

      // 🆕 Se il wizard è in modalità "quote" (preventivo), il nuovo cliente viene salvato come LEAD,
      //    NON apparirà in /clients finché un'offerta collegata non risulta pagata.
      const asLeadQuery = mode === "quote" ? "?as_lead=true" : "";
      const { data } = await api.post(`/clients${asLeadQuery}`, payload);
      setSelectedClient(data);
      toast.success(mode === "quote" ? "Lead creato (verrà promosso a cliente al pagamento)" : "Cliente creato");
      setStep(1);
    } catch (e) {
      // Se il backend rifiuta per duplicato (CF/email/phone/mobile) → recupera il cliente esistente
      // (incluso eventuale lead) tramite /clients/find-duplicate e mostralo nel dialog
      const errMsg = formatApiError(e);
      if (/già\s*esistente|already exists|duplicate|in uso/i.test(errMsg || "")) {
        const dup = await findExistingDuplicate();
        if (dup) {
          setDupClientDialog({ ...dup, reason: dup.reason || "backend", backendMessage: errMsg });
          return;
        }
      }
      toast.error(errMsg);
    } finally {
      setCreatingClient(false);
    }
  };

  // === Step 2: upload client documents ===
  const uploadClientDoc = async (file) => {
    if (!file || !selectedClient) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await uploadWithProgress({
        label: `📎 ${file.name} → ${selectedClient.surname || selectedClient.ragione_sociale || "cliente"}`,
        run: (cfg) => api.post(
          `/clients/${selectedClient.id}/documents?kind=${docKind}`,
          fd,
          { headers: { "Content-Type": "multipart/form-data" }, onUploadProgress: cfg.onUploadProgress },
        ),
      });
      if (data && data.id) {
        setClientDocs((prev) => [...prev, data]);
      } else {
        toast.error("Upload non riuscito, riprova");
      }
      toast.success(`${file.name} caricato`);

      // 🆕 Auto-estrazione AI scadenze documenti (patente / CI / tessera sanitaria)
      const EXPIRY_DOCS = {
        patente: {
          extractKind: "patente",
          expiryField: "driving_license_expires_at",
          extraFields: ["driving_license_issued_at", "driving_license_renewed_at"],
          aiField: "driving_license_ai_extracted_at",
          label: "patente",
        },
        carta_identita: {
          extractKind: "carta_identita",
          expiryField: "id_card_expires_at",
          extraFields: [],
          aiField: "id_card_ai_extracted_at",
          label: "carta d'identità",
        },
        tessera_sanitaria: {
          extractKind: "tessera_sanitaria",
          expiryField: "health_card_expires_at",
          extraFields: [],
          aiField: "health_card_ai_extracted_at",
          label: "tessera sanitaria",
        },
      };
      const cfg = EXPIRY_DOCS[docKind];
      if (cfg) {
        try {
          const fdAi = new FormData();
          fdAi.append("file", file);
          const ex = await uploadWithProgress({
            label: `📎 Analisi ${cfg.extractKind}: ${file.name}`,
            run: (progressCfg) => api.post(`/utils/extract-pdf?kind=${cfg.extractKind}`, fdAi, {
              headers: { "Content-Type": "multipart/form-data" },
              onUploadProgress: progressCfg.onUploadProgress,
            }),
          });
          const extracted = ex?.data?.extracted || ex?.data || {};
          const expires = extracted[cfg.expiryField];
          if (expires && /^\d{4}-\d{2}-\d{2}$/.test(expires) && selectedClient[cfg.expiryField] !== expires) {
            const aiTs = new Date().toISOString();
            const updatedBody = { ...selectedClient, [cfg.expiryField]: expires, [cfg.aiField]: aiTs };
            for (const field of cfg.extraFields) {
              const val = extracted[field];
              if (val && /^\d{4}-\d{2}-\d{2}$/.test(val) && !selectedClient[field]) {
                updatedBody[field] = val;
              }
            }
            delete updatedBody.id;
            delete updatedBody.created_at;
            delete updatedBody.updated_at;
            delete updatedBody.phone_norm;
            delete updatedBody.mobile_norm;
            try {
              await api.put(`/clients/${selectedClient.id}`, updatedBody);
              setSelectedClient((prev) => ({ ...prev, ...updatedBody }));
              const itDate = expires.split("-").reverse().join("/");
              toast.success(`📅 ${cfg.label}: scadenza ${itDate} → salvata in anagrafica`);
            } catch (errSave) {
              toast.error("Scadenza estratta ma impossibile aggiornare l'anagrafica: " + formatApiError(errSave));
            }
          }
        } catch (errAi) {
          console.warn(`Estrazione AI ${cfg.label} fallita:`, errAi);
        }
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploadingDoc(false); }
  };

  const handleClientDocsInput = async (e) => {
    const files = Array.from(e.target.files || []);
    await uploadClientDocs(files);
    if (clientDocInputRef.current) clientDocInputRef.current.value = "";
  };

  // Helper unico: accetta sia un singolo file sia un array (per drag&drop)
  const uploadClientDocs = async (filesOrArray) => {
    const files = Array.isArray(filesOrArray) ? filesOrArray : [filesOrArray];
    const processed = await askMultiFileAction(files);
    for (const f of processed) {
      if (f) await uploadClientDoc(f);
    }
  };

  // === Step 3: create policy / quote ===
  const createPolicy = async () => {
    // 🆕 Validazione campo obbligatorio "Attestato di rischio altro veicolo" — solo se visibile
    // (visibile solo quando la voce Situazione Assicurativa ha flag `enables_other_attestato`).
    const _attestatoVisible = isQuote && bersaniSourceOptions.length > 0
      && !!insuranceStatusOptions.find((o) => o.code === policy.insurance_status)?.enables_other_attestato;
    if (_attestatoVisible && !policy.bersani_source) {
      toast.error("Seleziona il campo \"Attestato di rischio altro veicolo\" prima di procedere.");
      return;
    }
    // 🆕 Validazione formato targa
    if ((policy.vehicle_plate || "").trim() && !policy.vehicle_plate_override) {
      const _pv = validatePlate(policy.vehicle_plate);
      if (!_pv.ok) {
        toast.error(`Formato targa non valido: ${_pv.message}. Spunta "Formato speciale" per forzare il salvataggio.`);
        return;
      }
    }
    setCreating(true);
    try {
      const toNum = (v) => (v === "" || v == null ? null : Number(v));
      // 🆕 28/07/2026 — Sincronizza profession/marital_status del contraente sul cliente selezionato
      // (stesso pattern usato per le scadenze documenti sopra, righe ~1080-1093).
      const syncClientProfessionMarital = async () => {
        if (!selectedClient?.id) return;
        const newProf = policy.profession || null;
        const newMar = policy.marital_status || null;
        const changed = (newProf && newProf !== (selectedClient.profession || null))
          || (newMar && newMar !== (selectedClient.marital_status || null));
        if (!changed) return;
        const updatedBody = { ...selectedClient };
        if (newProf) updatedBody.profession = newProf;
        if (newMar) updatedBody.marital_status = newMar;
        delete updatedBody.id;
        delete updatedBody.created_at;
        delete updatedBody.updated_at;
        delete updatedBody.phone_norm;
        delete updatedBody.mobile_norm;
        try {
          await api.put(`/clients/${selectedClient.id}`, updatedBody);
          setSelectedClient((prev) => (prev ? { ...prev, profession: newProf || prev.profession, marital_status: newMar || prev.marital_status } : prev));
        } catch (e) { console.warn("Sync profession/marital_status fallita:", e); }
      };
      // 🆕 28/07/2026 — Sincronizza professione/stato civile sul cliente prima della creazione
      // (vale sia per preventivo che per polizza; non blocca in caso di errore).
      await syncClientProfessionMarital();
      if (isQuote) {
        // 🆕 Task 3: le garanzie richieste vengono mandate SOLO via details.requested_coverages
        // (la scheda PDF/email partner ha una sezione dedicata "Garanzie da quotare").
        // Il `coverage_request` rimane libero per le note dell'agente.
        // Mappa i dati polizza in QuoteIn
        // 🆕 Feb 2026 — popola owner_* dal cliente selezionato: così quando il preventivo
        // viene riaperto in Quotes dialog i dati anagrafici sono visibili nei campi.
        // Distinzione Persona vs Azienda: se il cliente è "company" NON scriviamo la ragione
        // sociale in owner_name, ma popoliamo i campi aziendali dedicati + legale_rappresentante_*.
        const _c = selectedClient || {};
        const _isCompany = _c.subject_type === "company";
        const details = {
          policy_number: policy.policy_number || null,
          company: policy.company || null,
          company_id: policy.company_id || null,
          broker_id: policy.broker_id || null,
          frazionamento: policy.frazionamento || null,
          driving_type: policy.driving_type || null, // 🆕 Tipo di guida
          profession: policy.profession || null, // 🆕 Professione contraente
          marital_status: policy.marital_status || null, // 🆕 Stato civile contraente
          vehicle_additional_fuel: policy.vehicle_additional_fuel || null, // 🆕 Alimentazione aggiuntiva
          // 🆕 Feb 2026 — Classe di merito del preventivo (viene poi propagata in policy alla conversione)
          merit_class: policy.merit_class || null,
          // 🆕 Feb 2026 — Situazione assicurativa + campi Bersani/RC Familiare (visibili solo se "new_vehicle")
          insurance_status: policy.insurance_status || null,
          bersani_source: policy.bersani_source || null,
          other_vehicle_plate: policy.other_vehicle_plate || null,
          other_vehicle_brand: policy.other_vehicle_brand || null,
          other_vehicle_model: policy.other_vehicle_model || null,
          other_vehicle_first_registration: policy.other_vehicle_first_registration || null,
          other_vehicle_notes: policy.other_vehicle_notes || null,
          bersani_type: policy.bersani_type || null,
          bersani_ref_plate: policy.bersani_ref_plate || null,
          bersani_ref_company: policy.bersani_ref_company || null,
          bersani_ref_merit_class: policy.bersani_ref_merit_class || null,
          bersani_ref_owner: policy.bersani_ref_owner || null,
          bersani_ref_owner_fiscal_code: policy.bersani_ref_owner_fiscal_code || null,
          bersani_ref_owner_birth_date: policy.bersani_ref_owner_birth_date || null,
          // 🆕 Feb 2026 — Campi anagrafici intestatario di riferimento per generatore CF
          bersani_ref_owner_name: policy.bersani_ref_owner_name || null,
          bersani_ref_owner_surname: policy.bersani_ref_owner_surname || null,
          bersani_ref_owner_birth_place: policy.bersani_ref_owner_birth_place || null,
          bersani_ref_owner_gender: policy.bersani_ref_owner_gender || null,
          start_date: policy.start_date || null,
          end_date: policy.end_date || null,
          // 🆕 Distinzione persona/azienda
          subject_type: _c.subject_type || "private",
          // 🆕 Dati anagrafici del contraente (proprietario) — necessari per riapertura in Quotes dialog e stampa PDF
          //    Per aziende: owner_name/surname NON contengono la ragione sociale (che sta in ragione_sociale)
          owner_name: _isCompany ? null : (_c.name || null),
          owner_surname: _isCompany ? null : (_c.surname || null),
          owner_fiscal_code: _isCompany ? null : (_c.fiscal_code || null),
          owner_birth_date: _isCompany ? null : (_c.birth_date || null),
          owner_birth_place: _isCompany ? null : (_c.birth_place || null),
          owner_gender: _isCompany ? null : (_c.gender || null),
          owner_marital_status: _isCompany ? null : (policy.marital_status || _c.marital_status || null),
          owner_address: _c.address || null,
          owner_house_number: _c.house_number || null,
          owner_city: _c.city || null,
          owner_province: _c.province || null,
          owner_cap: _c.cap || null,
          owner_email: _c.email || null,
          owner_phone: _c.mobile || _c.phone || null,
          // 🆕 Feb 2026 — Campi Azienda (popolati solo se subject_type === "company")
          ragione_sociale: _isCompany ? (_c.ragione_sociale || _c.name || null) : null,
          partita_iva: _isCompany ? (_c.partita_iva || null) : null,
          pec_email: _isCompany ? (_c.pec_email || null) : null,
          sdi_code: _isCompany ? (_c.sdi_code || null) : null,
          // Dati del Legale Rappresentante (Azienda)
          legale_rappresentante_name: _isCompany ? (_c.legale_rappresentante_name || null) : null,
          legale_rappresentante_surname: _isCompany ? (_c.legale_rappresentante_surname || null) : null,
          legale_rappresentante_fiscal_code: _isCompany ? (_c.legale_rappresentante_fiscal_code || null) : null,
          legale_rappresentante_birth_date: _isCompany ? (_c.legale_rappresentante_birth_date || null) : null,
          legale_rappresentante_birth_place: _isCompany ? (_c.legale_rappresentante_birth_place || null) : null,
          legale_rappresentante_birth_province: _isCompany ? (_c.legale_rappresentante_birth_province || null) : null,
          legale_rappresentante_gender: _isCompany ? (_c.legale_rappresentante_gender || null) : null,
          legale_rappresentante_role: _isCompany ? (_c.legale_rappresentante_role || null) : null,
          legale_rappresentante_nationality: _isCompany ? (_c.legale_rappresentante_nationality || null) : null,
          vehicle_plate: (policy.vehicle_plate || "").trim().toUpperCase() || null,
          vehicle_brand: policy.vehicle_brand || null,
          vehicle_model: policy.vehicle_model || null,
          chassis_number: (policy.chassis_number || "").trim() || null, // 🆕 Task 1
          vehicle_first_registration: policy.vehicle_first_registration || null,
          // 🆕 Feb 2026 — Anno immatricolazione + Data acquisto veicolo (dal libretto)
          vehicle_year: policy.vehicle_year ? parseInt(policy.vehicle_year, 10) || null : null,
          vehicle_purchase_date: policy.vehicle_purchase_date || null,
          vehicle_fuel: policy.vehicle_fuel || null,
          vehicle_kw: toNum(policy.vehicle_kw),
          vehicle_displacement: toNum(policy.vehicle_displacement),
          // 🆕 Retro-compat: duplichiamo su chiavi legacy usate da QuoteDetailsForm (`fuel`, `kw`, `displacement`)
          //    così il dialog in /quotes mostra correttamente i dati anche senza aggiornare i componenti legacy.
          fuel: policy.vehicle_fuel || null,
          kw: toNum(policy.vehicle_kw),
          displacement: toNum(policy.vehicle_displacement),
          last_review_date: policy.last_review_date || null, // 🆕 data revisione
          next_review_date: policy.next_review_date || null, // 🆕 scadenza revisione
          // 🆕 30/07/2026 — Natante (attivi solo se type === "natante", ma inviati sempre per semplicità)
          boat_maker_model: policy.boat_maker_model || null,
          boat_hull_material: policy.boat_hull_material || null,
          boat_length_m: policy.boat_length_m || null,
          boat_beam_m: policy.boat_beam_m || null,
          boat_build_year: policy.boat_build_year || null,
          boat_registration_number: policy.boat_registration_number || null,
          boat_engine_maker: policy.boat_engine_maker || null,
          boat_engine_model: policy.boat_engine_model || null,
          boat_engine_serial: policy.boat_engine_serial || null,
          boat_engine_install: policy.boat_engine_install || null,
          boat_engine_fuel: policy.boat_engine_fuel || null,
          boat_engine_kw: policy.boat_engine_kw || null,
          boat_engine_mass_kg: policy.boat_engine_mass_kg || null,
          boat_declaration_number: policy.boat_declaration_number || null,
          boat_declaration_date: policy.boat_declaration_date || null,
          // 🆕 30/07/2026 — Multi-motore
          boat_engines: buildBoatEnginesPayload(policy) || [],
          requested_coverages: (policy.requested_coverages || [])
            .filter((c) => (c.name || "").trim())
            .map((c) => {
              // Strip metadata FE-only (_source_guarantee_id, _buy_price, ...) prima dell'invio
              const clean = {};
              for (const k of Object.keys(c)) {
                if (!k.startsWith("_")) clean[k] = c[k];
              }
              return clean;
            }), // 🆕 Task 3
          notes: policy.notes || null,
        };
        const baseReq = (policy.notes || "").trim() ||
          `Preventivo ${policy.type || "auto"} per ${selectedClient.surname || ""} ${selectedClient.name || ""}`.trim();
        const coverageReq = baseReq;
        const payload = {
          client_id: selectedClient.id,
          type: policy.type || "auto",
          coverage_request: coverageReq,
          estimated_premium: toNum(policy.premium),
          details,
        };
        const { data } = await api.post("/quotes", payload);
        setCreatedPolicyId(data.id);
        // 🆕 Feb 2026 — Collega i documenti personali del cliente (Step 2) al preventivo appena creato
        //   così patente, libretto, carta identità appaiono direttamente negli allegati del preventivo.
        if (clientDocs.length > 0) {
          try {
            const r = await api.post(`/quotes/${data.id}/link-client-docs`, {
              attachment_ids: clientDocs.map((d) => d.id),
            });
            if (r.data?.linked > 0) {
              try {
                const list = await api.get(`/quotes/${data.id}/attachments`);
                setPolicyAttachments(list.data || []);
              } catch { /* ignore */ }
              toast.success(`${r.data.linked} document${r.data.linked === 1 ? "o" : "i"} del cliente collegat${r.data.linked === 1 ? "o" : "i"} al preventivo`);
            }
          } catch (e) {
            toast.error("Collegamento doc cliente al preventivo fallito: " + formatApiError(e));
          }
        }
        // 🆕 Feb 2026 — Allega automaticamente TUTTI i file usati per l'estrazione AI
        //    (libretto veicolo + eventuale polizza vecchia), così l'utente non deve
        //    ricaricarli manualmente in Step 3 e li ritrova già come allegati del preventivo.
        const extractionFiles = [
          { file: policyPdfFile, description: "PDF preventivo (sorgente estrazione AI)" },
          { file: librettoFile, description: "Libretto veicolo (sorgente estrazione AI)" },
          { file: bersaniLibrettoFile, description: `Libretto veicolo Bersani${policy.bersani_ref_plate ? ` (targa ${policy.bersani_ref_plate})` : ""}` },
        ].filter((x) => !!x.file);
        for (const { file: extFile, description } of extractionFiles) {
          try {
            const fd = new FormData();
            fd.append("files", extFile, extFile.name);
            const att = await uploadWithProgress({
              label: `📎 ${extFile.name} → Preventivo`,
              run: (cfg) => api.post(
                `/quotes/${data.id}/attachments/batch?description=${encodeURIComponent(description)}`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" }, onUploadProgress: cfg.onUploadProgress },
              ),
            });
            const saved = att.data?.saved || [];
            if (saved.length) setPolicyAttachments((prev) => [...prev, ...saved]);
          } catch (uploadErr) {
            console.warn(`[Wizard] Impossibile allegare "${extFile.name}" al preventivo:`, uploadErr);
            toast.error(`Preventivo creato ma allegato "${extFile.name}" NON caricato: ${formatApiError(uploadErr)}`);
          }
        }
        toast.success(extractionFiles.length > 0
          ? `Preventivo creato · ${extractionFiles.length} file dell'estrazione AI allegat${extractionFiles.length === 1 ? "o" : "i"} automaticamente`
          : "Preventivo creato",
        );
        setStep(3);
        return;
      }
      // 🆕 Task 3: se non ci sono coperture operanti ma ci sono garanzie richieste, le pre-popola
      const reqCovs = (policy.requested_coverages || []).filter((c) => (c.name || "").trim());
      const initialCoverages = (Array.isArray(policy.coverages) && policy.coverages.length)
        ? policy.coverages
        : reqCovs.map((c) => {
            const base = {
              code: buildGuaranteeCode(c.name),
              name: c.name,
              active: true,
              sum_insured: c.sum_insured ? Number(c.sum_insured) : null,
              deductible: c.deductible || null,
            };
            // 🆕 Feb 2026 — Se la garanzia arriva dal catalogo compagnia, propaga Acquisto/Vendita
            if (c._buy_price != null) base.buy_cost = Number(c._buy_price);
            if (c._sell_price != null) base.sell_cost = Number(c._sell_price);
            // 🆕 Feb 2026 — Propaga % imposte/SSN e calcola premio netto dal lordo (sell_price/premium)
            const impostePct = c._imposte_percent != null ? Number(c._imposte_percent) : null;
            const ssnPct = c._ssn_percent != null ? Number(c._ssn_percent) : null;
            if (impostePct != null) base.imposte_percent = impostePct;
            if (ssnPct != null) base.ssn_percent = ssnPct;
            const gross = Number(c.premium) || Number(c._sell_price) || 0;
            if (gross > 0 && (impostePct != null || ssnPct != null)) {
              const totPct = (impostePct || 0) + (ssnPct || 0);
              base.net_premium = totPct > 0 ? Math.round((gross / (1 + totPct / 100)) * 100) / 100 : gross;
            }
            return base;
          });
      const payload = {
        ...policy,
        client_id: selectedClient.id,
        premium: Number(policy.premium) || 0,
        commission_amount: Number(policy.commission_amount) || 0,
        broker_id: policy.broker_id || null,
        source: policy.source || "manuale",
        source_quote_id: policy.source === "da_preventivo" ? (policy.source_quote_id || null) : null,
        coverages: initialCoverages, // 🆕 Task 3
        notes: (policy.notes || "").trim() || null, // 🆕 Note libere, niente duplicazione garanzie
        // Veicolo: converti stringhe vuote in null per Pydantic
        vehicle_plate: (policy.vehicle_plate || "").trim().toUpperCase() || null,
        vehicle_brand: policy.vehicle_brand || null,
        vehicle_model: policy.vehicle_model || null,
        chassis_number: (policy.chassis_number || "").trim() || null, // 🆕 Task 1: telaio
        vehicle_first_registration: policy.vehicle_first_registration || null,
        vehicle_year: policy.vehicle_year ? parseInt(policy.vehicle_year, 10) || null : null,
        vehicle_purchase_date: policy.vehicle_purchase_date || null,
        vehicle_fuel: policy.vehicle_fuel || null,
        vehicle_kw: toNum(policy.vehicle_kw),
        vehicle_displacement: toNum(policy.vehicle_displacement),
        last_review_date: policy.last_review_date || null, // 🆕 Task 1
        next_review_date: policy.next_review_date || null, // 🆕 Task 1
        // 🆕 30/07/2026 — Campi Natante
        boat_maker_model: policy.boat_maker_model || null,
        boat_hull_material: policy.boat_hull_material || null,
        boat_length_m: policy.boat_length_m || null,
        boat_beam_m: policy.boat_beam_m || null,
        boat_build_year: policy.boat_build_year || null,
        boat_registration_number: policy.boat_registration_number || null,
        boat_engine_maker: policy.boat_engine_maker || null,
        boat_engine_model: policy.boat_engine_model || null,
        boat_engine_serial: policy.boat_engine_serial || null,
        boat_engine_install: policy.boat_engine_install || null,
        boat_engine_fuel: policy.boat_engine_fuel || null,
        boat_engine_kw: policy.boat_engine_kw || null,
        boat_engine_mass_kg: policy.boat_engine_mass_kg || null,
        boat_declaration_number: policy.boat_declaration_number || null,
        boat_declaration_date: policy.boat_declaration_date || null,
      };
      // 🆕 30/07/2026 — Payload multi-motore natante
      const _boat_engines_payload = buildBoatEnginesPayload(policy);
      if (_boat_engines_payload !== undefined) payload.boat_engines = _boat_engines_payload;
      // 🆕 Feb 2026 — Payload multi-veicolo
      const _vehicles_payload = buildVehiclesPayload(policy);
      if (_vehicles_payload !== undefined) payload.vehicles = _vehicles_payload;
      payload.multi_vehicle_kind = (_vehicles_payload && _vehicles_payload.length >= 2 && ["storica_cumulativa", "flotta"].includes(policy.multi_vehicle_kind))
        ? policy.multi_vehicle_kind
        : null;
      // rimuovi i campi UI-only prima di POSTare
      delete payload.requested_coverages;
      delete payload.duration_years;
      const { data } = await api.post("/policies", payload);
      setCreatedPolicyId(data.id);
      // Collega i documenti personali del cliente (Step 2) alla nuova polizza
      if (clientDocs.length > 0) {
        try {
          const r = await api.post(`/policies/${data.id}/link-client-docs`, {
            attachment_ids: clientDocs.map((d) => d.id),
          });
          if (r.data?.linked > 0) {
            // Rilegge gli allegati polizza completi (includono ora anche i doc cliente)
            try {
              const list = await api.get(`/policies/${data.id}/attachments`);
              setPolicyAttachments(list.data || []);
            } catch { /* ignore */ }
            toast.success(`${r.data.linked} document${r.data.linked === 1 ? "o" : "i"} del cliente collegat${r.data.linked === 1 ? "o" : "i"} alla polizza`);
          }
        } catch (e) {
          toast.error("Collegamento doc cliente fallito: " + formatApiError(e));
        }
      }
      // Auto-allega TUTTI i file usati per l'estrazione AI (PDF polizza vecchia + libretto)
      const extractionFiles = [
        { file: policyPdfFile, description: "PDF polizza (sorgente estrazione AI)" },
        { file: librettoFile, description: "Libretto veicolo (sorgente estrazione AI)" },
      ].filter((x) => !!x.file);
      let uploadedCount = 0;
      for (const { file: extFile, description } of extractionFiles) {
        try {
          const fd = new FormData();
          fd.append("files", extFile, extFile.name);
          const att = await uploadWithProgress({
            label: `📎 ${extFile.name} → Polizza`,
            run: (cfg) => api.post(
              `/policies/${data.id}/attachments/batch?description=${encodeURIComponent(description)}`,
              fd,
              { headers: { "Content-Type": "multipart/form-data" }, onUploadProgress: cfg.onUploadProgress },
            ),
          });
          const saved = att.data?.saved || [];
          if (saved.length) {
            setPolicyAttachments((prev) => [...prev, ...saved]);
            uploadedCount += saved.length;
          }
        } catch (attErr) {
          console.warn(`[Wizard] Impossibile allegare "${extFile.name}" alla polizza:`, attErr);
          toast.error(`Polizza creata ma allegato "${extFile.name}" NON caricato: ${formatApiError(attErr)}`);
        }
      }
      toast.success(uploadedCount > 0
        ? `Polizza creata · ${uploadedCount} file dell'estrazione AI allegat${uploadedCount === 1 ? "o" : "i"} automaticamente`
        : "Polizza creata",
      );
      setStep(3);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };

  // 🆕 Feb 2026 — Prima di caricare un file, chiede all'utente se vuole rinominarlo.
  // Restituisce un nuovo File con nome aggiornato, oppure lo stesso file se l'utente rifiuta o annulla.
  const askRenameBeforeUpload = (file) => {
    if (!file) return file;
    const currentName = file.name || "allegato";
    const wantsRename = window.confirm(`Vuoi rinominare "${currentName}" prima di caricarlo?\n\n· Ok = rinomina\n· Annulla = mantieni il nome originale`);
    if (!wantsRename) return file;
    const dotIdx = currentName.lastIndexOf(".");
    const base = dotIdx > 0 ? currentName.slice(0, dotIdx) : currentName;
    const ext = dotIdx > 0 ? currentName.slice(dotIdx) : "";
    const nextBase = window.prompt("Nuovo nome file (senza estensione):", base);
    if (nextBase === null) return file; // annulla dentro prompt
    const clean = (nextBase || "").trim().replace(/[\\/:*?"<>|]/g, "_");
    if (!clean || clean === base) return file;
    const newName = `${clean}${ext}`;
    try {
      return new File([file], newName, { type: file.type, lastModified: file.lastModified });
    } catch {
      return file; // ambiente senza File constructor: mantieni originale
    }
  };

  // === Step 4: upload policy/quote attachments ===
  const uploadPolicyAtt = async (file) => {
    if (!file) return;
    if (!createdPolicyId) {
      // 🆕 Feb 2026 — non lasciare l'utente senza feedback quando il preventivo non è ancora stato creato
      toast.error(`Salva prima ${isQuote ? "il preventivo" : "la polizza"} (step precedente) prima di allegare "${file.name}"`);
      return;
    }
    setUploadingAtt(true);
    try {
      const fd = new FormData();
      fd.append("files", file, file.name);
      const { data } = await api.post(
        `${attachBase}/${createdPolicyId}/attachments/batch`,
        fd, { headers: { "Content-Type": "multipart/form-data" } },
      );
      const saved = data.saved || [];
      const dups = data.duplicates || [];
      setPolicyAttachments((prev) => [...prev, ...saved]);
      if (saved.length) toast.success(`${file.name} caricato`);
      if (dups.length) {
        const names = dups.map((d) => d.existing_filename || d.filename).join(", ");
        toast.warning(`File già presente, non riallegato: ${names}`);
      }
      if (data.errors?.length) toast.error(`${data.errors.length} file scartati: ${(data.errors[0]?.error || "")}`);
    } catch (e) {
      // 🆕 mostra dettagli errore reale (max 200 char) invece che fallire silenziosamente
      toast.error(`Upload "${file.name}" fallito: ${formatApiError(e)}`);
      console.error(`[Wizard] Upload attachment failed for "${file.name}":`, e);
    }
    finally { setUploadingAtt(false); }
  };

  const handlePolicyAttInput = async (e) => {
    const files = Array.from(e.target.files || []);
    const processed = await askMultiFileAction(files);
    for (const f of processed) {
      const renamed = askRenameBeforeUpload(f);
      await uploadPolicyAtt(renamed);
    }
    if (policyAttInputRef.current) policyAttInputRef.current.value = "";
  };

  // === Send to partner (broker/company) ===
  const [sendPartnerOpen, setSendPartnerOpen] = useState(false);
  // 🆕 Selezione multi-partner: array di {target_type, target_id, name, email}
  const [partnerSelections, setPartnerSelections] = useState([]);
  const [partnerNotes, setPartnerNotes] = useState("");
  const [partnerUrgent, setPartnerUrgent] = useState(false);  // 🆕 10/07/2026 — Flag urgenza
  const [sendingPartner, setSendingPartner] = useState(false);
  // 🆕 Feb 2026 — Lock sincrono anti-doppio invio (setState è asincrono: un doppio-click rapido
  // o un trigger multiplo di `onConfirm` dell'anteprima email può bypassare `disabled={sendingPartner}`).
  const partnerSendLockRef = useRef(false);
  // 🆕 Anteprima email prima dell'invio (unificata via EmailPreviewDialog)
  const [emailPreviewCtx, setEmailPreviewCtx] = useState(null);
  // 🆕 Allegati selezionabili al momento dell'invio
  const [availableAttachments, setAvailableAttachments] = useState([]);
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);

  // Carica gli allegati disponibili quando si apre il dialog
  useEffect(() => {
    if (!sendPartnerOpen || !createdPolicyId) return;
    setLoadingAttachments(true);
    const param = isQuote ? `quote_id=${createdPolicyId}` : `policy_id=${createdPolicyId}`;
    api.get(`/partners/available-attachments?${param}`)
      .then(({ data }) => {
        setAvailableAttachments(data || []);
        // Default: tutti selezionati
        setSelectedAttachmentIds((data || []).map((a) => a.id));
      })
      .catch(() => { setAvailableAttachments([]); setSelectedAttachmentIds([]); })
      .finally(() => setLoadingAttachments(false));
  }, [sendPartnerOpen, createdPolicyId, isQuote]);

  const toggleAttachment = (id) => {
    setSelectedAttachmentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };
  const selectAllAttachments = () => setSelectedAttachmentIds(availableAttachments.map((a) => a.id));
  const deselectAllAttachments = () => setSelectedAttachmentIds([]);

  const togglePartnerSelection = (target_type, target_id, name, email) => {
    setPartnerSelections((prev) => {
      const idx = prev.findIndex((p) => p.target_type === target_type && p.target_id === target_id);
      if (idx >= 0) {
        const copy = [...prev];
        copy.splice(idx, 1);
        return copy;
      }
      return [...prev, { target_type, target_id, name, email }];
    });
  };

  const isPartnerSelected = (target_type, target_id) =>
    partnerSelections.some((p) => p.target_type === target_type && p.target_id === target_id);

  const sendToPartner = async () => {
    if (!createdPolicyId) {
      toast.error(`Crea prima ${isQuote ? "il preventivo" : "la polizza"}`);
      return;
    }
    if (partnerSelections.length === 0) {
      toast.error("Seleziona almeno un partner");
      return;
    }
    if (isQuote && policy.type === "auto" && !policy.driving_type) {
      toast.error("Invio bloccato: il campo \"Tipo di guida\" è obbligatorio per i preventivi auto. Compilalo e salva il preventivo prima di inviarlo.");
      return;
    }
    const payload = {
      targets: partnerSelections.map((p) => ({ target_type: p.target_type, target_id: p.target_id })),
      extra_notes: partnerNotes || null,
      include_attachments: selectedAttachmentIds.length > 0,
      attachment_ids: selectedAttachmentIds.length > 0 ? selectedAttachmentIds : null,
      urgent: partnerUrgent,  // 🆕 10/07/2026
    };
    if (isQuote) {
      payload.quote_id = createdPolicyId;
    } else {
      payload.policy_id = createdPolicyId;
    }
    const doActualSend = async () => {
      // 🆕 Lock sincrono anti-doppio invio
      if (partnerSendLockRef.current) return;
      partnerSendLockRef.current = true;
      setSendingPartner(true);
      try {
        const r = await api.post("/partners/send-quote-multi", payload);
        const okCount = r.data?.sent_count || 0;
        const total = r.data?.total || partnerSelections.length;
        const failed = (r.data?.results || []).filter((x) => !x.ok);
        if (okCount === total) {
          toast.success(`Richiesta inviata a ${okCount} partner`);
        } else if (okCount > 0) {
          toast.warning(`Inviata a ${okCount}/${total}. Falliti: ${failed.map((f) => f.error).join("; ")}`);
        } else {
          toast.error(`Invio fallito. ${failed.map((f) => f.error).join("; ")}`);
        }
        if (okCount > 0) {
          setSendPartnerOpen(false);
          setPartnerNotes("");
          setPartnerUrgent(false);
          setPartnerSelections([]);
        }
      } catch (e) { toast.error(formatApiError(e)); }
      finally {
        setSendingPartner(false);
        partnerSendLockRef.current = false;
      }
    };
    if (shouldSkipEmailPreview("quote_to_partner_multi")) {
      await doActualSend();
      return;
    }
    // 🆕 Mostra prima l'anteprima (usa il primo destinatario come rappresentativo)
    setEmailPreviewCtx({
      typeKey: "quote_to_partner_multi",
      title: `Anteprima richiesta preventivo a partner${partnerSelections.length > 1 ? ` (1° di ${partnerSelections.length})` : ""}`,
      fetchPreview: async () => {
        const { data } = await api.post("/partners/send-quote-multi?dry_run=true", payload);
        return data;
      },
      onConfirm: doActualSend,
    });
  };

  // === Navigation guards ===
  const canGoNext = () => {
    if (step === 0) return !!selectedClient;
    if (step === 1) return true; // documents optional
    if (step === 2) return true; // policy fields validated server-side
    if (step === 3) return true; // attachments optional
    return false;
  };

  // 🆕 Feb 2026 — Riconoscimento tipo soggetto (Azienda vs Persona Fisica).
  // Un cliente è considerato "azienda" quando subject_type === "company"
  // (con fallback su ragione_sociale/partita_iva valorizzati per record legacy).
  const isCompanyClient = (c) => {
    if (!c) return false;
    if (c.subject_type === "company") return true;
    if (c.subject_type === "private") return false;
    return !!((c.ragione_sociale || "").trim() || (c.partita_iva || "").trim());
  };
  // 🆕 Etichetta principale del cliente (Ragione Sociale per aziende, Cognome+Nome per privati)
  const clientDisplayName = (c) => {
    if (!c) return "";
    if (isCompanyClient(c)) return (c.ragione_sociale || "").trim() || `${c.surname || ""} ${c.name || ""}`.trim();
    return `${c.surname || ""} ${c.name || ""}`.trim();
  };

  const goNext = () => {
    if (step === 2) createPolicy();
    else setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  // Reset stato polizza per aggiungere una nuova polizza allo stesso cliente
  const addAnotherPolicy = () => {
    setPolicy({
      type: "auto", policy_number: "", company_id: "", broker_id: "",
      start_date: new Date().toISOString().slice(0, 10),
      end_date: "", premium: 0, commission_amount: 0,
      frazionamento: "annuale", notes: "", coverages: [], requested_coverages: [],
      driving_type: "",
      profession: "",
      marital_status: "",
      vehicle_additional_fuel: "",
      vehicle_plate: "", vehicle_brand: "", vehicle_model: "",
      chassis_number: "",
      vehicle_first_registration: "", vehicle_fuel: "",
      vehicle_kw: "", vehicle_displacement: "",
      last_review_date: "", next_review_date: "",
    });
    setPolicyAttachments([]);
    setCreatedPolicyId(null);
    setPolPdfMismatch(null);
    setPolGuarantees([]);
    setPolicyPdfFile(null);
    setStep(2);
    toast.success("Pronto per inserire una nuova polizza per " + (clientDisplayName(selectedClient) || "questo cliente"));
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto animate-fade-up" data-testid={isQuote ? "quote-wizard" : "policy-wizard"}>
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-2">Nuov{isQuote ? "o" : "a"} {targetName}</p>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Wizard inserimento {targetName}</h1>
        <p className="text-slate-500 mt-1">Procedura guidata in 5 passi.</p>
      </header>

      {/* Stepper */}
      <ol className="flex items-center gap-2 flex-wrap" data-testid="wizard-steps">
        {STEPS.map((label, i) => {
          const active = i === step;
          const done = i < step;
          return (
            <li key={label} className="flex items-center gap-2">
              <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${
                done ? "bg-emerald-600 text-white"
                  : active ? "bg-blue-600 text-white"
                  : "bg-slate-200 text-slate-500"
              }`}>
                {done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
              </div>
              <span className={`text-sm ${active ? "font-semibold" : "text-slate-500"}`}>{label}</span>
              {i < STEPS.length - 1 && <div className={`w-8 h-0.5 ${done ? "bg-emerald-600" : "bg-slate-200"}`} />}
            </li>
          );
        })}
      </ol>

      {/* Banner sticky con il cliente attivo: visibile in tutti gli step dopo la selezione */}
      {selectedClient && (
        <div
          data-testid="wizard-client-sticky"
          className="sticky top-0 z-30 -mx-2 px-3 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-md shadow-md flex items-center justify-between gap-3 flex-wrap"
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div
              className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0"
              data-testid={`wizard-client-sticky-icon-${isCompanyClient(selectedClient) ? "company" : "person"}`}
              title={isCompanyClient(selectedClient) ? "Azienda" : "Persona fisica"}
            >
              {isCompanyClient(selectedClient) ? <Building2 className="h-5 w-5" /> : <User className="h-5 w-5" />}
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-white/70">Cliente attivo · {isCompanyClient(selectedClient) ? "Azienda" : "Persona fisica"}</p>
              <p className="font-bold text-sm sm:text-base truncate" data-testid="wizard-client-sticky-name">
                {clientDisplayName(selectedClient) || "—"}
              </p>
            </div>
            <div className="hidden md:flex flex-wrap items-center gap-2 ml-4 text-xs text-white/90" data-testid="wizard-client-sticky-details">
              {(() => {
                // 🆕 Feb 2026 — Editor rapido: ogni campo è cliccabile.
                //   • Se valorizzato → chip con valore + hover (matita).
                //   • Se vuoto → chip tratteggiato "+ Etichetta" per aggiungerlo.
                const chipBase = "inline-flex items-center gap-1 rounded-md px-2 py-0.5 transition cursor-pointer";
                const chipFilled = `${chipBase} bg-white/10 hover:bg-white/25 border border-white/20`;
                const chipMissing = `${chipBase} bg-transparent hover:bg-white/15 border border-dashed border-white/40 text-white/70 italic`;
                const renderField = (key, label, value, icon, opts = {}) => {
                  const testid = opts.testid;
                  const type = opts.type || "text";
                  if (value) {
                    return (
                      <button
                        type="button"
                        data-testid={testid}
                        onClick={() => openClientQuickEdit(key, label, type)}
                        className={chipFilled}
                        title={`Modifica ${label.toLowerCase()}`}
                      >
                        <span>{icon}</span>
                        <span className={opts.mono ? "font-mono uppercase" : ""}>{value}</span>
                        <Pencil className="h-3 w-3 opacity-60" />
                      </button>
                    );
                  }
                  return (
                    <button
                      type="button"
                      data-testid={testid ? `${testid}-add` : undefined}
                      onClick={() => openClientQuickEdit(key, label, type)}
                      className={chipMissing}
                      title={`Aggiungi ${label.toLowerCase()}`}
                    >
                      <Plus className="h-3 w-3" /> {label}
                    </button>
                  );
                };
                if (isCompanyClient(selectedClient)) {
                  return (
                    <>
                      {renderField("partita_iva", "P.IVA", selectedClient.partita_iva, "🏢", { testid: "wizard-client-sticky-piva", mono: true })}
                      {renderField("pec_email", "PEC", selectedClient.pec_email, "✉", { testid: "wizard-client-sticky-pec" })}
                    </>
                  );
                }
                // Persona fisica — ordine: Data nascita → CF → Cellulare → Indirizzo
                const addrShown = [
                  [selectedClient.address, selectedClient.house_number].filter(Boolean).join(" ").trim(),
                  selectedClient.cap,
                  selectedClient.city,
                  selectedClient.province ? `(${selectedClient.province})` : null,
                ].filter(Boolean).join(", ");
                return (
                  <>
                    {renderField("birth_date", "Data di nascita", selectedClient.birth_date ? formatDate(selectedClient.birth_date) : "", "🎂", { testid: "wizard-client-sticky-birthdate", type: "date" })}
                    {renderField("fiscal_code", "Codice fiscale", selectedClient.fiscal_code, "CF:", { testid: "wizard-client-sticky-cf", mono: true })}
                    {renderField("mobile", "Cellulare", selectedClient.mobile, "📱", { testid: "wizard-client-sticky-mobile", type: "tel" })}
                    {renderField("address_group", "Indirizzo", addrShown, "📍", { testid: "wizard-client-sticky-address" })}
                  </>
                );
              })()}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="wizard-client-sticky-edit-full"
              onClick={openClientFullEdit}
              className="text-xs bg-white text-blue-700 hover:bg-blue-50 px-2.5 py-1 rounded transition whitespace-nowrap font-semibold inline-flex items-center gap-1 shadow-sm"
              title="Modifica tutti i dati del cliente"
            >
              <Pencil className="h-3 w-3" /> Modifica
            </button>
            <button
              data-testid="wizard-client-sticky-change"
              onClick={() => { setSelectedClient(null); setClientMode(null); setClientDocs([]); setStep(0); }}
              className="text-xs bg-white/15 hover:bg-white/25 px-2.5 py-1 rounded transition whitespace-nowrap"
            >
              Cambia cliente
            </button>
          </div>
        </div>
      )}

      {/* 🆕 Feb 2026 — Dialog "Modifica cliente" completo (tutti i campi) — sovrapposto al wizard */}
      <Dialog open={!!clientFullEdit} onOpenChange={(o) => { if (!o && !clientFullEditBusy) setClientFullEdit(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="client-full-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-800">
              <Pencil className="h-4 w-4" /> Modifica dati cliente
            </DialogTitle>
          </DialogHeader>
          {clientFullEdit && (() => {
            const isCompany = isCompanyClient(selectedClient);
            const setF = (patch) => setClientFullEdit((s) => s ? { ...s, ...patch } : s);
            return (
              <div className="space-y-4 pt-2" data-testid="client-full-edit-form">
                {/* ============ ANAGRAFICA ============ */}
                <div className="rounded-md border border-blue-200 bg-blue-50/40 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-blue-800 font-bold mb-2">Anagrafica</p>
                  {isCompany ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="sm:col-span-2">
                        <Label className="text-xs">Ragione sociale</Label>
                        <Input data-testid="cfe-ragione-sociale" value={clientFullEdit.ragione_sociale} onChange={(e) => setF({ ragione_sociale: e.target.value })} />
                      </div>
                      <div>
                        <Label className="text-xs">Partita IVA</Label>
                        <Input data-testid="cfe-piva" value={clientFullEdit.partita_iva} onChange={(e) => setF({ partita_iva: (e.target.value || "").replace(/\s+/g, "") })} className="font-mono" maxLength={11} />
                      </div>
                      <div>
                        <Label className="text-xs">Codice fiscale</Label>
                        <Input data-testid="cfe-cf" value={clientFullEdit.fiscal_code} onChange={(e) => setF({ fiscal_code: (e.target.value || "").toUpperCase() })} className="font-mono uppercase" maxLength={16} />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-xs">PEC</Label>
                        <Input data-testid="cfe-pec" type="email" value={clientFullEdit.pec_email} onChange={(e) => setF({ pec_email: e.target.value })} />
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Nome</Label>
                        <Input data-testid="cfe-name" value={clientFullEdit.name} onChange={(e) => setF({ name: e.target.value, ...(clientFullEdit.gender ? {} : autoGenderPatch(e.target.value, "gender", new Set())) })} />
                      </div>
                      <div>
                        <Label className="text-xs">Cognome</Label>
                        <Input data-testid="cfe-surname" value={clientFullEdit.surname} onChange={(e) => setF({ surname: e.target.value })} />
                      </div>
                      <div>
                        <Label className="text-xs">Codice fiscale</Label>
                        <Input data-testid="cfe-cf" value={clientFullEdit.fiscal_code} onChange={(e) => setF({ fiscal_code: (e.target.value || "").toUpperCase() })} className="font-mono uppercase" maxLength={16} />
                      </div>
                      <div>
                        <Label className="text-xs">Data di nascita</Label>
                        <Input data-testid="cfe-birth-date" type="date" value={clientFullEdit.birth_date} onChange={(e) => setF({ birth_date: e.target.value })} />
                      </div>
                      <div>
                        <Label className="text-xs">Luogo di nascita</Label>
                        <CityAutocomplete
                          testid="cfe-birth-place"
                          value={clientFullEdit.birth_place}
                          onChange={(v) => setF({ birth_place: v })}
                          onSelect={(o) => setF({ birth_place: o?.name || "", birth_province: o?.province || clientFullEdit.birth_province })}
                          placeholder="es. Roma"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Provincia nascita</Label>
                        <Input data-testid="cfe-birth-province" value={clientFullEdit.birth_province} onChange={(e) => setF({ birth_province: (e.target.value || "").toUpperCase() })} maxLength={2} className="uppercase" />
                      </div>
                      <div>
                        <Label className="text-xs">Sesso</Label>
                        <Select value={clientFullEdit.gender || ""} onValueChange={(v) => setF({ gender: v })}>
                          <SelectTrigger data-testid="cfe-gender"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="M">Maschio</SelectItem>
                            <SelectItem value="F">Femmina</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>

                {/* ============ CONTATTI ============ */}
                <div className="rounded-md border border-emerald-200 bg-emerald-50/40 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-emerald-800 font-bold mb-2">Contatti</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Email</Label>
                      <Input data-testid="cfe-email" type="email" value={clientFullEdit.email} onChange={(e) => setF({ email: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-xs">Telefono fisso</Label>
                      <Input data-testid="cfe-phone" type="tel" value={clientFullEdit.phone} onChange={(e) => setF({ phone: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-xs">Cellulare</Label>
                      <Input data-testid="cfe-mobile" type="tel" value={clientFullEdit.mobile} onChange={(e) => setF({ mobile: e.target.value })} />
                    </div>
                  </div>
                </div>

                {/* ============ RESIDENZA / SEDE ============ */}
                <div className="rounded-md border border-amber-200 bg-amber-50/40 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-amber-800 font-bold mb-2">{isCompany ? "Sede legale" : "Residenza"}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Indirizzo</Label>
                      <AddressAutocomplete
                        testid="cfe-address"
                        value={clientFullEdit.address}
                        onChange={(v) => setF({ address: v })}
                        city={clientFullEdit.city}
                        placeholder={clientFullEdit.city ? "Inizia a digitare la via…" : "Inserisci prima la città"}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Civico</Label>
                      <Input data-testid="cfe-house-number" value={clientFullEdit.house_number} onChange={(e) => setF({ house_number: e.target.value })} />
                    </div>
                    <div>
                      <Label className="text-xs">CAP</Label>
                      <Input data-testid="cfe-cap" value={clientFullEdit.cap} onChange={(e) => setF({ cap: (e.target.value || "").replace(/\D/g, "").slice(0, 5) })} maxLength={5} className="font-mono" />
                    </div>
                    <div>
                      <Label className="text-xs">Città</Label>
                      <CityAutocomplete
                        testid="cfe-city"
                        value={clientFullEdit.city}
                        onChange={(v) => setF({ city: v })}
                        onSelect={(o) => setF({ city: o?.name || "", province: o?.province || clientFullEdit.province, cap: o?.cap || clientFullEdit.cap })}
                        placeholder="es. Roma"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Provincia</Label>
                      <Input data-testid="cfe-province" value={clientFullEdit.province} onChange={(e) => setF({ province: (e.target.value || "").toUpperCase() })} maxLength={2} className="uppercase" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => setClientFullEdit(null)} disabled={clientFullEditBusy} data-testid="cfe-cancel">Annulla</Button>
            <Button type="button" onClick={saveClientFullEdit} disabled={clientFullEditBusy} data-testid="cfe-save">
              {clientFullEditBusy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Salvataggio…</> : "Salva modifiche"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Feb 2026 — Dialog Editor rapido campi cliente (invocato dai chip della barra sticky) */}
      <Dialog open={!!clientQuickEdit} onOpenChange={(o) => { if (!o && !clientQuickEditBusy) setClientQuickEdit(null); }}>
        <DialogContent className="max-w-md" data-testid="client-quick-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-800">
              <Pencil className="h-4 w-4" /> Modifica {clientQuickEdit?.label?.toLowerCase() || "campo"}
            </DialogTitle>
          </DialogHeader>
          {clientQuickEdit && (
            <div className="space-y-3">
              {clientQuickEdit.type === "address" ? (
                <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
                  <div className="sm:col-span-4">
                    <Label className="text-xs">Via</Label>
                    <AddressAutocomplete
                      value={clientQuickEdit.address.address}
                      onChange={(v) => setClientQuickEdit((s) => ({ ...s, address: { ...s.address, address: v } }))}
                      onSelect={(item) => setClientQuickEdit((s) => ({
                        ...s,
                        address: {
                          ...s.address,
                          address: item.road || s.address.address,
                          house_number: item.house_number || s.address.house_number,
                          cap: item.postcode || s.address.cap,
                          city: item.city || s.address.city,
                          province: item.province || s.address.province,
                        },
                      }))}
                      city={clientQuickEdit.address.city}
                      testid="client-quick-edit-address"
                      placeholder={clientQuickEdit.address.city ? "Inizia a digitare la via…" : "Inserisci prima la città"}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs">Civico</Label>
                    <Input
                      data-testid="client-quick-edit-house_number"
                      value={clientQuickEdit.address.house_number}
                      onChange={(e) => setClientQuickEdit((s) => ({ ...s, address: { ...s.address, house_number: e.target.value } }))}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-xs">CAP</Label>
                    <Input
                      data-testid="client-quick-edit-cap"
                      value={clientQuickEdit.address.cap}
                      onChange={(e) => setClientQuickEdit((s) => ({ ...s, address: { ...s.address, cap: e.target.value } }))}
                    />
                  </div>
                  <div className="sm:col-span-3">
                    <Label className="text-xs">Città</Label>
                    <CityAutocomplete
                      value={clientQuickEdit.address.city}
                      onChange={(v) => setClientQuickEdit((s) => ({ ...s, address: { ...s.address, city: v } }))}
                      onSelect={(item) => setClientQuickEdit((s) => ({
                        ...s,
                        address: {
                          ...s.address,
                          city: item.name || s.address.city,
                          province: item.province || s.address.province,
                          cap: item.cap || s.address.cap,
                        },
                      }))}
                      testid="client-quick-edit-city"
                      placeholder="Inizia a digitare (auto-compila CAP e provincia)…"
                    />
                  </div>
                  <div className="sm:col-span-1">
                    <Label className="text-xs">Prov.</Label>
                    <Input
                      data-testid="client-quick-edit-province"
                      maxLength={2}
                      value={clientQuickEdit.address.province}
                      onChange={(e) => setClientQuickEdit((s) => ({ ...s, address: { ...s.address, province: (e.target.value || "").toUpperCase() } }))}
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <Label className="text-xs">{clientQuickEdit.label}</Label>
                  {clientQuickEdit.key === "fiscal_code" ? (
                    <div className="flex gap-2">
                      <Input
                        autoFocus
                        type="text"
                        data-testid="client-quick-edit-value"
                        value={clientQuickEdit.value || ""}
                        onChange={(e) => setClientQuickEdit((s) => ({ ...s, value: (e.target.value || "").toUpperCase(), decoded: null }))}
                        onKeyDown={(e) => { if (e.key === "Enter" && !clientQuickEditBusy) saveClientQuickEdit(); }}
                        maxLength={16}
                        className="font-mono uppercase flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={decodeCfInQuickEdit}
                        disabled={cfDecodingInQuickEdit || ((clientQuickEdit.value || "").length !== 16)}
                        data-testid="client-quick-edit-cf-decode"
                        title="Estrai data e luogo di nascita dal codice fiscale"
                        className="whitespace-nowrap"
                      >
                        {cfDecodingInQuickEdit ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                        Decodifica
                      </Button>
                    </div>
                  ) : (
                    <Input
                      autoFocus
                      type={clientQuickEdit.type || "text"}
                      data-testid="client-quick-edit-value"
                      value={clientQuickEdit.value || ""}
                      onChange={(e) => setClientQuickEdit((s) => ({ ...s, value: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter" && !clientQuickEditBusy) saveClientQuickEdit(); }}
                    />
                  )}
                  {clientQuickEdit.key === "fiscal_code" && clientQuickEdit.decoded && (
                    <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="client-quick-edit-cf-preview">
                      <div className="font-semibold mb-1 flex items-center gap-1">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Dati decodificati (verranno salvati)
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        {clientQuickEdit.decoded.birth_date && (<div>📅 Nascita: <strong>{formatDate(clientQuickEdit.decoded.birth_date)}</strong></div>)}
                        {clientQuickEdit.decoded.birth_place && (<div>📍 Luogo: <strong>{clientQuickEdit.decoded.birth_place}{clientQuickEdit.decoded.birth_province ? ` (${clientQuickEdit.decoded.birth_province})` : ""}</strong></div>)}
                        {clientQuickEdit.decoded.gender && (<div>Sesso: <strong>{clientQuickEdit.decoded.gender}</strong></div>)}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <p className="text-[11px] text-slate-500">Le modifiche vengono salvate nel profilo del cliente.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setClientQuickEdit(null)} disabled={clientQuickEditBusy} data-testid="client-quick-edit-cancel">
              Annulla
            </Button>
            <Button onClick={saveClientQuickEdit} disabled={clientQuickEditBusy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="client-quick-edit-save">
              {clientQuickEditBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="bg-white border border-slate-200 rounded-lg p-6">
        {/* STEP 0 — Client */}
        {step === 0 && (
          <div className="space-y-4">
            <h2 className="font-display text-xl font-semibold flex items-center gap-2"><User className="h-5 w-5 text-blue-600" /> Dati del cliente</h2>

            {/* IMPORTAZIONE LIBRETTO */}
            <DropZone
              testid="dropzone-libretto"
              onFiles={(files) => { if (files[0]) processLibretto(files[0]); }}
              accept={["application/pdf", "image/jpeg", "image/png", "image/webp"]}
              multiple={false}
              disabled={librettoUploading}
              className="bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 rounded-lg p-4"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-emerald-900">📕 Importa da libretto di circolazione</p>
                  <p className="text-xs text-emerald-800 mt-0.5">
                    Carica PDF o foto del libretto (o <strong>trascina il file qui</strong>): l&apos;AI estrae automaticamente l&apos;<strong>ultimo intestatario</strong>, lo cerca in archivio (per Codice Fiscale) e precompila tutti i campi del cliente e del veicolo.
                  </p>
                </div>
                <input
                  ref={librettoInputRef}
                  type="file"
                  accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif"
                  className="hidden"
                  data-testid="wizard-libretto-input"
                  onChange={onLibrettoSelect}
                />
                <Button
                  data-testid="wizard-import-libretto"
                  onClick={() => librettoInputRef.current?.click()}
                  disabled={librettoUploading}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {librettoUploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                  {librettoUploading ? "Analisi AI…" : "Carica documenti"}
                </Button>
              </div>
              {librettoMatch && (
                <div
                  data-testid="wizard-libretto-banner"
                  className={`mt-3 rounded-md p-3 text-sm bg-${librettoMatch.color}-50 border border-${librettoMatch.color}-300 text-${librettoMatch.color}-900`}
                >
                  <p className="font-semibold">{librettoMatch.message}</p>
                  {librettoMatch.ownershipDate && (
                    <p className="text-xs mt-1">📅 Ultimo aggiornamento PRA: <strong>{formatDate(librettoMatch.ownershipDate)}</strong></p>
                  )}
                  {librettoMatch.previous && Array.isArray(librettoMatch.previous) && librettoMatch.previous.length > 0 && (
                    <p className="text-[11px] mt-1 opacity-80">
                      Precedenti intestatari (ignorati): {librettoMatch.previous.map((x) => typeof x === "string" ? x : (x.name || JSON.stringify(x))).join(" · ")}
                    </p>
                  )}
                  {/* 🆕 Feb 2026 — Banner "Preventivo già presente tra i lead" con 3 opzioni */}
                  {librettoQuoteMatch && librettoQuoteMatch.quote && (
                    <div
                      className="mt-3 rounded-md p-3 bg-indigo-50 border-2 border-indigo-400 text-indigo-900"
                      data-testid="wizard-libretto-quote-match"
                    >
                      <p className="font-bold flex items-start gap-2">
                        <span>ℹ️</span>
                        <span>
                          Trovato un preventivo esistente tra i lead con{" "}
                          {librettoQuoteMatch.reason === "cf"
                            ? "lo stesso Codice Fiscale"
                            : "la stessa anagrafica"}
                          : <span className="underline">{librettoQuoteMatch.quote.quote_number}</span>
                        </span>
                      </p>
                      <ul className="mt-1 text-[12px] leading-relaxed">
                        <li>👤 <strong>{(librettoQuoteMatch.quote.owner_surname || "") + " " + (librettoQuoteMatch.quote.owner_name || "")}</strong>{librettoQuoteMatch.quote.owner_fiscal_code ? ` · CF ${librettoQuoteMatch.quote.owner_fiscal_code}` : ""}</li>
                        {librettoQuoteMatch.quote.owner_birth_date && <li>🎂 Nato il: <strong>{formatDate(librettoQuoteMatch.quote.owner_birth_date)}</strong>{librettoQuoteMatch.quote.owner_birth_place ? ` a ${librettoQuoteMatch.quote.owner_birth_place}` : ""}</li>}
                        {(librettoQuoteMatch.quote.owner_email || librettoQuoteMatch.quote.owner_phone || librettoQuoteMatch.quote.owner_mobile) && (
                          <li>📞 {[librettoQuoteMatch.quote.owner_email, librettoQuoteMatch.quote.owner_phone, librettoQuoteMatch.quote.owner_mobile].filter(Boolean).join(" · ")}</li>
                        )}
                        {librettoQuoteMatch.quote.vehicle_plate && <li>🚗 Veicolo preventivo: <strong>{librettoQuoteMatch.quote.vehicle_plate}</strong></li>}
                      </ul>
                      <p className="text-[11px] mt-2 italic opacity-85">Vuoi riutilizzare i dati di questo preventivo oppure creare comunque un nuovo record?</p>
                      <div className="mt-2 flex gap-2 flex-wrap">
                        <Button
                          data-testid="libretto-quote-use"
                          size="sm"
                          className="bg-indigo-600 hover:bg-indigo-700 text-white"
                          onClick={() => {
                            const qd = librettoQuoteMatch.quote || {};
                            // Riempi librettoPending con i dati del preventivo esistente (hanno priorità) +
                            // tieni i campi del libretto come fallback.
                            setLibrettoPending((prev) => ({
                              ...(prev || {}),
                              name: qd.owner_name || (prev && prev.name) || "",
                              surname: qd.owner_surname || (prev && prev.surname) || "",
                              fiscal_code: qd.owner_fiscal_code || (prev && prev.fiscal_code) || "",
                              birth_date: qd.owner_birth_date || (prev && prev.birth_date) || "",
                              birth_place: qd.owner_birth_place || (prev && prev.birth_place) || "",
                              address: qd.owner_address || (prev && prev.address) || "",
                              house_number: qd.owner_house_number || (prev && prev.house_number) || "",
                              city: qd.owner_city || (prev && prev.city) || "",
                              province: qd.owner_province || (prev && prev.province) || "",
                              cap: qd.owner_cap || (prev && prev.cap) || "",
                              email: qd.owner_email || (prev && prev.email) || "",
                              phone: qd.owner_phone || (prev && prev.phone) || "",
                              mobile: qd.owner_mobile || (prev && prev.mobile) || "",
                            }));
                            toast.success(`Dati del preventivo ${qd.quote_number || ""} caricati nel form`);
                            // Chiama direttamente il flusso "Crea nuovo cliente" con i dati del preventivo
                            setLibrettoQuoteMatch(null);
                            // Pre-compila il form del nuovo cliente con i dati del preventivo trovato
                            setNewClient((prev) => ({
                              ...prev,
                              name: qd.owner_name || prev.name,
                              surname: qd.owner_surname || prev.surname,
                              fiscal_code: qd.owner_fiscal_code || prev.fiscal_code,
                              birth_date: qd.owner_birth_date || prev.birth_date,
                              birth_place: qd.owner_birth_place || prev.birth_place,
                              address: qd.owner_address || prev.address,
                              house_number: qd.owner_house_number || prev.house_number,
                              city: qd.owner_city || prev.city,
                              province: qd.owner_province || prev.province,
                              cap: qd.owner_cap || prev.cap,
                              email: qd.owner_email || prev.email,
                              phone: qd.owner_phone || prev.phone,
                              mobile: qd.owner_mobile || prev.mobile,
                            }));
                            setCfManual(!!qd.owner_fiscal_code);
                            setClientMode("new");
                            setLibrettoMatch((m) => m ? { ...m, showChoice: false, message: `✏️ Dati pre-compilati dal preventivo ${qd.quote_number || ""}. Verifica e salva come nuovo cliente.` } : m);
                          }}
                        >
                          Usa i dati del preventivo
                        </Button>
                        <Button
                          data-testid="libretto-quote-create-anyway"
                          size="sm"
                          variant="outline"
                          className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                          onClick={() => { setLibrettoQuoteMatch(null); toast.message("Procedi scegliendo 'Crea nuovo cliente' qui sotto"); }}
                        >
                          Crea comunque un nuovo record
                        </Button>
                        <Button
                          data-testid="libretto-quote-cancel"
                          size="sm"
                          variant="ghost"
                          className="text-slate-600 hover:bg-slate-100"
                          onClick={() => {
                            setLibrettoFile(null);
                            setLibrettoData(null);
                            setLibrettoMatch(null);
                            setLibrettoPending(null);
                            setLibrettoQuoteMatch(null);
                            setClientMode(null);
                            if (librettoInputRef.current) librettoInputRef.current.value = "";
                          }}
                        >
                          Annulla
                        </Button>
                      </div>
                    </div>
                  )}
                  {librettoMatch.showChoice && (
                    <div className="mt-3 flex gap-2 flex-wrap" data-testid="libretto-choice-buttons">
                      <Button
                        data-testid="libretto-choose-search"
                        onClick={chooseSearchExisting}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <Search className="h-4 w-4 mr-2" />
                        Cerca in archivio
                      </Button>
                      <Button
                        data-testid="libretto-choose-create"
                        onClick={chooseCreateNew}
                        size="sm"
                        variant="outline"
                        className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                      >
                        <UserPlus className="h-4 w-4 mr-2" />
                        Crea nuovo cliente
                      </Button>
                      <Button
                        data-testid="libretto-choose-back"
                        onClick={() => {
                          setLibrettoFile(null);
                          setLibrettoData(null);
                          setLibrettoMatch(null);
                          setLibrettoPending(null);
                          setClientMode(null);
                          if (librettoInputRef.current) librettoInputRef.current.value = "";
                        }}
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:bg-red-50"
                      >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Torna indietro
                      </Button>
                    </div>
                  )}
                  {librettoMatch.manualFallback && (
                    <div className="mt-3" data-testid="libretto-manual-fallback">
                      <p className="text-xs mb-2 opacity-90">
                        Il file <span className="font-mono">{librettoFile?.name}</span> resta selezionato e verrà allegato <strong>alla polizza</strong> a fine wizard (non al cliente).
                        Scegli come procedere:
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        <Button
                          data-testid="libretto-manual-search"
                          onClick={() => { setClientMode("existing"); setLibrettoMatch((m) => m ? { ...m, manualFallback: false, message: "🔍 Cerca il cliente in archivio o crea nuovo manualmente." } : m); }}
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <Search className="h-4 w-4 mr-2" />
                          Cerca cliente in archivio
                        </Button>
                        <Button
                          data-testid="libretto-manual-create"
                          onClick={() => { setClientMode("new"); setCfManual(false); setLibrettoMatch((m) => m ? { ...m, manualFallback: false, message: "✏️ Inserisci manualmente i dati del cliente. Il libretto allegato verrà salvato come documento." } : m); }}
                          size="sm"
                          variant="outline"
                          className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                        >
                          <UserPlus className="h-4 w-4 mr-2" />
                          Crea cliente manualmente
                        </Button>
                        <Button
                          data-testid="libretto-manual-retry"
                          onClick={() => { if (librettoFile) processLibretto(librettoFile); }}
                          size="sm"
                          variant="ghost"
                          className="text-slate-700"
                          disabled={librettoUploading}
                        >
                          <Sparkles className="h-4 w-4 mr-2" />
                          Riprova lettura AI
                        </Button>
                        <Button
                          data-testid="libretto-manual-back"
                          onClick={() => {
                            // Reset completo: torna alla schermata iniziale dello Step 1
                            setLibrettoFile(null);
                            setLibrettoData(null);
                            setLibrettoMatch(null);
                            setLibrettoPending(null);
                            setClientMode(null);
                            if (librettoInputRef.current) librettoInputRef.current.value = "";
                          }}
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:bg-red-50"
                        >
                          <ArrowLeft className="h-4 w-4 mr-2" />
                          Torna indietro
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {librettoData?.vehicle_plate && (
                <div className="mt-3 text-xs bg-white border border-slate-200 rounded p-2 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="libretto-vehicle-summary">
                  <div><span className="text-slate-500">Targa:</span> <strong className="font-mono">{librettoData.vehicle_plate}</strong></div>
                  <div><span className="text-slate-500">Marca:</span> <strong>{librettoData.vehicle_brand || "—"}</strong></div>
                  <div><span className="text-slate-500">Modello:</span> <strong>{librettoData.vehicle_model || "—"}</strong></div>
                  <div><span className="text-slate-500">Anno:</span> <strong>{librettoData.vehicle_year || "—"}</strong></div>
                  <div><span className="text-slate-500">Alim.:</span> <strong>{librettoData.fuel || "—"}</strong></div>
                  <div><span className="text-slate-500">kW:</span> <strong>{librettoData.kw || "—"}</strong></div>
                  <div><span className="text-slate-500">Cilindrata:</span> <strong>{librettoData.displacement ? `${librettoData.displacement}cc` : "—"}</strong></div>
                  <div><span className="text-slate-500">Revisione:</span> <strong>{librettoData.next_review_date ? formatDate(librettoData.next_review_date) : "—"}</strong></div>
                </div>
              )}
            </DropZone>


            {selectedClient ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4" data-testid="selected-client">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-emerald-900 text-base flex items-center gap-2">
                      {isCompanyClient(selectedClient) ? (
                        <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-purple-100 text-purple-700 flex-shrink-0" title="Azienda"><Building2 className="h-3.5 w-3.5" /></span>
                      ) : (
                        <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-700 flex-shrink-0" title="Persona fisica"><User className="h-3.5 w-3.5" /></span>
                      )}
                      ✓ {clientDisplayName(selectedClient) || "—"}
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded-full border font-semibold uppercase tracking-wider ${isCompanyClient(selectedClient) ? "bg-purple-50 text-purple-700 border-purple-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}
                        data-testid="selected-client-badge"
                      >
                        {isCompanyClient(selectedClient) ? "Azienda" : "Privato"}
                      </span>
                    </p>
                    {isCompanyClient(selectedClient) ? (
                      <>
                        {selectedClient.partita_iva && (
                          <p className="text-xs font-mono uppercase text-slate-700 mt-0.5" data-testid="sel-cli-piva">
                            P.IVA: <strong>{selectedClient.partita_iva}</strong>
                          </p>
                        )}
                        {selectedClient.fiscal_code && selectedClient.fiscal_code !== selectedClient.partita_iva && (
                          <p className="text-xs font-mono uppercase text-slate-600 mt-0.5">
                            CF: <strong>{selectedClient.fiscal_code}</strong>
                          </p>
                        )}
                      </>
                    ) : (
                      selectedClient.fiscal_code && (
                        <p className="text-xs font-mono uppercase text-slate-700 mt-0.5">
                          CF: <strong>{selectedClient.fiscal_code}</strong>
                        </p>
                      )
                    )}
                  </div>
                  <button onClick={() => { setSelectedClient(null); setClientMode(null); setClientDocs([]); }} className="text-xs text-red-600 hover:underline whitespace-nowrap">Cambia</button>
                </div>
                {/* Dati di contatto + anagrafica importati dall'archivio */}
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                  {selectedClient.email && (
                    <div data-testid="sel-cli-email">
                      <span className="text-slate-500">📧 Email:</span>{" "}
                      <a href={`mailto:${selectedClient.email}`} className="font-medium text-blue-700 hover:underline">{selectedClient.email}</a>
                    </div>
                  )}
                  {isCompanyClient(selectedClient) && selectedClient.pec_email && (
                    <div data-testid="sel-cli-pec">
                      <span className="text-slate-500">✉ PEC:</span>{" "}
                      <a href={`mailto:${selectedClient.pec_email}`} className="font-medium text-blue-700 hover:underline">{selectedClient.pec_email}</a>
                    </div>
                  )}
                  {isCompanyClient(selectedClient) && selectedClient.sdi_code && (
                    <div data-testid="sel-cli-sdi">
                      <span className="text-slate-500">🏷 Codice SDI:</span>{" "}
                      <span className="font-mono font-medium">{selectedClient.sdi_code}</span>
                    </div>
                  )}
                  {selectedClient.mobile && (
                    <div data-testid="sel-cli-mobile">
                      <span className="text-slate-500">📱 Cellulare:</span>{" "}
                      <a href={`tel:${selectedClient.mobile}`} className="font-medium text-blue-700 hover:underline">{selectedClient.mobile}</a>
                    </div>
                  )}
                  {selectedClient.phone && (
                    <div data-testid="sel-cli-phone">
                      <span className="text-slate-500">☎️ Telefono:</span>{" "}
                      <a href={`tel:${selectedClient.phone}`} className="font-medium text-blue-700 hover:underline">{selectedClient.phone}</a>
                    </div>
                  )}
                  {!isCompanyClient(selectedClient) && selectedClient.birth_date && (
                    <div>
                      <span className="text-slate-500">🎂 Nato il:</span>{" "}
                      <span className="font-medium">{formatDate(selectedClient.birth_date)}</span>
                      {selectedClient.birth_place && <span className="text-slate-500"> a {selectedClient.birth_place}</span>}
                    </div>
                  )}
                  {(selectedClient.address || selectedClient.city) && (
                    <div className="sm:col-span-2">
                      <span className="text-slate-500">🏠 {isCompanyClient(selectedClient) ? "Sede legale" : "Residenza"}:</span>{" "}
                      <span className="font-medium">
                        {[selectedClient.address, selectedClient.house_number].filter(Boolean).join(" ")}
                        {selectedClient.cap || selectedClient.city ? `, ${[selectedClient.cap, selectedClient.city, selectedClient.province].filter(Boolean).join(" ")}` : ""}
                      </span>
                    </div>
                  )}
                  {!isCompanyClient(selectedClient) && selectedClient.marital_status && (
                    <div>
                      <span className="text-slate-500">💍 Stato civile:</span>{" "}
                      <span className="font-medium">{selectedClient.marital_status}</span>
                    </div>
                  )}
                  {!isCompanyClient(selectedClient) && selectedClient.gender && (
                    <div>
                      <span className="text-slate-500">⚧ Sesso:</span>{" "}
                      <span className="font-medium">{selectedClient.gender === "M" ? "Maschio" : selectedClient.gender === "F" ? "Femmina" : selectedClient.gender}</span>
                    </div>
                  )}
                </div>
                {/* 🆕 Feb 2026 — Sezione Legale Rappresentante (solo per aziende) */}
                {isCompanyClient(selectedClient) && (
                  (selectedClient.legale_rappresentante_name || selectedClient.legale_rappresentante_surname || selectedClient.legale_rappresentante_fiscal_code) ? (
                    <div className="mt-3 border-t border-emerald-200 pt-3" data-testid="sel-cli-legal-rep">
                      <p className="text-[11px] uppercase tracking-wider text-purple-700 font-bold mb-1.5 flex items-center gap-1.5">
                        <User className="h-3 w-3" /> Legale Rappresentante
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                        <div>
                          <span className="text-slate-500">Nome:</span>{" "}
                          <span className="font-medium">{`${selectedClient.legale_rappresentante_surname || ""} ${selectedClient.legale_rappresentante_name || ""}`.trim() || "—"}</span>
                        </div>
                        {selectedClient.legale_rappresentante_fiscal_code && (
                          <div>
                            <span className="text-slate-500">CF:</span>{" "}
                            <span className="font-mono font-medium uppercase">{selectedClient.legale_rappresentante_fiscal_code}</span>
                          </div>
                        )}
                        {selectedClient.legale_rappresentante_role && (
                          <div>
                            <span className="text-slate-500">Ruolo:</span>{" "}
                            <span className="font-medium">{selectedClient.legale_rappresentante_role}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="mt-2 text-[11px] text-amber-700">⚠️ Nessun Legale Rappresentante impostato per questa azienda.</p>
                  )
                )}
                {!selectedClient.email && !selectedClient.mobile && !selectedClient.phone && (
                  <p className="text-[11px] text-amber-700 mt-2">
                    ⚠️ Questo cliente non ha email né numeri di telefono salvati. Considera di aggiornarli dalla scheda cliente.
                  </p>
                )}
              </div>
            ) : !clientMode ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <button
                  data-testid="mode-existing"
                  onClick={() => setClientMode("existing")}
                  className="border-2 border-slate-200 hover:border-blue-500 rounded-lg p-6 text-left transition-all"
                >
                  <Search className="h-6 w-6 text-blue-600 mb-2" />
                  <p className="font-semibold">Cliente già in archivio</p>
                  <p className="text-xs text-slate-500 mt-1">Cerca per nome, CF, email, telefono…</p>
                </button>
                <button
                  data-testid="mode-new"
                  onClick={() => setClientMode("new")}
                  className="border-2 border-slate-200 hover:border-blue-500 rounded-lg p-6 text-left transition-all"
                >
                  <UserPlus className="h-6 w-6 text-emerald-600 mb-2" />
                  <p className="font-semibold">Nuovo cliente</p>
                  <p className="text-xs text-slate-500 mt-1">Inserisci ora un cliente non presente</p>
                </button>
                {/* 🆕 Estrai dati cliente da PDF polizza */}
                <label
                  data-testid="mode-extract-from-policy"
                  className={`border-2 rounded-lg p-6 text-left transition-all block ${extractingClientFromPolicy ? "opacity-60 pointer-events-none border-slate-200" : isDraggingClientPdf ? "border-indigo-500 bg-indigo-50 ring-4 ring-indigo-200 cursor-copy" : "border-slate-200 hover:border-indigo-500 cursor-pointer"}`}
                  title="Carica un PDF polizza (o trascinalo qui): l'AI estrae i dati del cliente e li precompila"
                  onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); if (!extractingClientFromPolicy) setIsDraggingClientPdf(true); }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer) e.dataTransfer.dropEffect = "copy"; }}
                  onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); if (e.currentTarget === e.target) setIsDraggingClientPdf(false); }}
                  onDrop={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDraggingClientPdf(false);
                    if (extractingClientFromPolicy) return;
                    const f = e.dataTransfer?.files?.[0];
                    if (!f) return;
                    const type = f.type || "";
                    const name = (f.name || "").toLowerCase();
                    const isAccepted = /^(application\/pdf|image\/)/.test(type) || name.endsWith(".pdf") || /\.(png|jpe?g|webp|heic|heif)$/i.test(name);
                    if (!isAccepted) {
                      toast.error("Trascina un file PDF o un'immagine (JPG/PNG/WEBP)");
                      return;
                    }
                    await extractClientFromPolicyFile(f);
                  }}
                >
                  {extractingClientFromPolicy ? (
                    <Loader2 className="h-6 w-6 text-indigo-600 mb-2 animate-spin" />
                  ) : (
                    <FileText className="h-6 w-6 text-indigo-600 mb-2" />
                  )}
                  <p className="font-semibold">Estrai da PDF polizza <span className="inline-block text-[9px] font-bold uppercase tracking-wider bg-indigo-100 text-indigo-800 px-1.5 py-0.5 rounded ml-1">🤖 AI</span></p>
                  <p className="text-xs text-slate-500 mt-1">
                    {isDraggingClientPdf
                      ? <span className="text-indigo-700 font-semibold">📥 Rilascia qui per estrarre i dati…</span>
                      : <>Trascina qui o clicca per caricare una polizza. Nome, CF, indirizzo verranno precompilati.</>}
                  </p>
                  <input
                    type="file"
                    accept="application/pdf,image/*"
                    className="hidden"
                    disabled={extractingClientFromPolicy}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) extractClientFromPolicyFile(f);
                      e.target.value = ""; // reset per permettere re-upload dello stesso file
                    }}
                  />
                </label>
              </div>
            ) : clientMode === "existing" ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-500">Cerca un cliente già in archivio.</p>
                  <Button
                    data-testid="wizard-search-back"
                    onClick={() => { setClientMode(null); setSearchQ(""); setSearchResults([]); }}
                    size="sm"
                    variant="ghost"
                    className="text-red-600 hover:bg-red-50 h-7 px-2 text-xs"
                  >
                    <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                    Torna indietro
                  </Button>
                </div>
                <Input
                  data-testid="wizard-search-client"
                  autoFocus
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  placeholder="Cerca cliente (almeno 2 caratteri)…"
                />
                {searching && <p className="text-xs text-slate-400">Ricerca in corso…</p>}
                <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="search-results">
                  {[...searchResults].sort((a, b) => {
                    const sa = clientDisplayName(a).toLowerCase();
                    const sb = clientDisplayName(b).toLowerCase();
                    return sa.localeCompare(sb, "it");
                  }).map((c) => {
                    const _isCompany = isCompanyClient(c);
                    return (
                    <button
                      key={c.id}
                      data-testid={`result-${c.id}`}
                      onClick={() => pickExistingClient(c)}
                      className="w-full text-left bg-white border border-slate-200 rounded-md px-3 py-2 hover:border-blue-400 hover:bg-blue-50/40"
                    >
                      <div className="font-medium text-sm flex items-center gap-2">
                        {_isCompany ? (
                          <span
                            data-testid={`result-icon-company-${c.id}`}
                            className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-purple-100 text-purple-700 flex-shrink-0"
                            title="Azienda"
                          >
                            <Building2 className="h-3.5 w-3.5" />
                          </span>
                        ) : (
                          <span
                            data-testid={`result-icon-person-${c.id}`}
                            className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-700 flex-shrink-0"
                            title="Persona fisica"
                          >
                            <User className="h-3.5 w-3.5" />
                          </span>
                        )}
                        <span className="truncate">{clientDisplayName(c)}</span>
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-full border font-semibold uppercase tracking-wider ${_isCompany ? "bg-purple-50 text-purple-700 border-purple-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}
                          data-testid={`result-badge-${c.id}`}
                        >
                          {_isCompany ? "Azienda" : "Privato"}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 pl-8">
                        {_isCompany ? (
                          <>{c.partita_iva ? <span className="font-mono">P.IVA {c.partita_iva}</span> : "P.IVA —"} · {c.email || "—"} · {c.mobile || c.phone || "—"}</>
                        ) : (
                          <>{c.fiscal_code ? <span className="font-mono">{c.fiscal_code}</span> : "—"} · {c.email || "—"} · {c.mobile || c.phone || "—"}</>
                        )}
                      </div>
                    </button>
                    );
                  })}
                  {searchResults.length === 0 && searchQ.length >= 2 && !searching && (
                    <div className="text-center py-6 text-sm text-slate-500 bg-amber-50 border border-amber-200 rounded-md">
                      <p className="font-semibold text-amber-800 mb-2">Nessun cliente trovato per &quot;{searchQ}&quot;</p>
                      <div className="flex gap-2 justify-center flex-wrap">
                        <Button
                          data-testid="wizard-search-no-results-create"
                          onClick={() => setClientMode("new")}
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          <UserPlus className="h-4 w-4 mr-1.5" />
                          Crea nuovo cliente
                        </Button>
                        <Button
                          data-testid="wizard-search-no-results-back"
                          onClick={() => { setClientMode(null); setSearchQ(""); setSearchResults([]); }}
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:bg-red-100"
                        >
                          <ArrowLeft className="h-4 w-4 mr-1.5" />
                          Torna indietro
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="col-span-2 flex items-center justify-between flex-wrap gap-2">
                  <p className="text-xs text-slate-500">
                    Inserisci i dati del nuovo cliente.
                    {extractedFromPolicy && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-indigo-100 text-indigo-800 border border-indigo-300 px-1.5 py-0.5 rounded-full font-semibold" data-testid="extracted-from-policy-badge">
                        🤖 Precompilato da PDF polizza — verifica i campi
                      </span>
                    )}
                  </p>
                  <Button
                    data-testid="wizard-newclient-back"
                    onClick={() => { setClientMode(null); setExtractedFromPolicy(false); }}
                    size="sm"
                    variant="ghost"
                    className="text-red-600 hover:bg-red-50 h-7 px-2 text-xs"
                  >
                    <ArrowLeft className="h-3.5 w-3.5 mr-1" />
                    Torna indietro
                  </Button>
                </div>

                {/* 🆕 Feb 2026 — Selezione obbligatoria del tipo soggetto */}
                <div className="col-span-2">
                  <Label className="text-xs font-bold uppercase tracking-wide text-slate-700">Tipo soggetto <span className="text-red-500">*</span></Label>
                  <div className="grid grid-cols-2 gap-3 mt-1.5">
                    <button
                      type="button"
                      data-testid="subject-type-private"
                      onClick={() => setNewClient({ ...newClient, subject_type: "private" })}
                      className={`border-2 rounded-lg p-3 text-left transition-all ${newClient.subject_type === "private" ? "border-blue-600 bg-blue-50 ring-2 ring-blue-200" : "border-slate-200 hover:border-blue-400"}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xl">👤</span>
                        <div>
                          <p className="font-semibold text-sm">Privato</p>
                          <p className="text-[11px] text-slate-500">Persona fisica</p>
                        </div>
                      </div>
                    </button>
                    <button
                      type="button"
                      data-testid="subject-type-company"
                      onClick={() => setNewClient({ ...newClient, subject_type: "company" })}
                      className={`border-2 rounded-lg p-3 text-left transition-all ${newClient.subject_type === "company" ? "border-blue-600 bg-blue-50 ring-2 ring-blue-200" : "border-slate-200 hover:border-blue-400"}`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xl">🏢</span>
                        <div>
                          <p className="font-semibold text-sm">Azienda</p>
                          <p className="text-[11px] text-slate-500">Persona giuridica</p>
                        </div>
                      </div>
                    </button>
                  </div>
                  {!newClient.subject_type && (
                    <p className="text-[11px] text-amber-700 mt-2 flex items-center gap-1" data-testid="subject-type-required-hint">
                      ⚠️ Seleziona il tipo di soggetto per continuare
                    </p>
                  )}
                </div>

                {/* 🆕 Feb 2026 — Layout CAMPI condizionale in base al tipo soggetto */}
                {newClient.subject_type === "private" && (<>
                {/* DATI PERSONALI */}
                <div><Label>Cognome <span className="text-red-500">*</span></Label><Input data-testid="new-cli-surname" className="uppercase" placeholder="Dato obbligatorio" value={newClient.surname} onChange={(e) => setNewClient({ ...newClient, surname: e.target.value.toUpperCase() })} /></div>
                <div><Label>Nome <span className="text-red-500">*</span></Label><Input data-testid="new-cli-name" className="uppercase" placeholder="Dato obbligatorio" value={newClient.name} onChange={(e) => { const v = e.target.value.toUpperCase(); setNewClient({ ...newClient, name: v, ...autoGenderPatch(v, "gender", genderManualRef.current) }); }} /></div>
                <div><Label>Sesso <span className="text-red-500">*</span></Label>
                  <Select value={newClient.gender} onValueChange={(v) => { genderManualRef.current.add("gender"); setNewClient({ ...newClient, gender: v }); }}>
                    <SelectTrigger><SelectValue placeholder="Dato obbligatorio" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">Maschio</SelectItem>
                      <SelectItem value="F">Femmina</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Data nascita <span className="text-red-500">*</span></Label><DatePicker data-testid="new-cli-birth-date" placeholder="Dato obbligatorio" value={newClient.birth_date} onChange={(e) => setNewClient({ ...newClient, birth_date: e.target.value })} /></div>
                <div className="col-span-2">
                  <Label>Luogo di nascita <span className="text-red-500">*</span></Label>
                  <CityAutocomplete
                    value={newClient.birth_place}
                    onChange={(v) => setNewClient({ ...newClient, birth_place: (v || "").toUpperCase(), belfiore_code: "" })}
                    onSelect={(c) => setNewClient({ ...newClient, birth_place: (c.name || "").toUpperCase(), belfiore_code: c.code || "" })}
                    placeholder="Dato obbligatorio"
                    inputClassName="uppercase"
                    testid="new-cli-birth-place"
                  />
                  {newClient.belfiore_code && (
                    <p className="text-[11px] text-emerald-700 mt-1">Codice catastale: <span className="font-mono font-semibold">{newClient.belfiore_code}</span></p>
                  )}
                </div>

                {/* CODICE FISCALE — auto-calcolato dopo i dati personali */}
                <div className="col-span-2">
                  <Label>
                    Codice Fiscale <span className="text-red-500">*</span>
                    {!cfManual && newClient.fiscal_code && (
                      <span className="ml-2 text-[10px] uppercase tracking-wide text-emerald-700 font-bold">✓ auto-calcolato</span>
                    )}
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      className="font-mono uppercase flex-1"
                      data-testid="new-cli-cf"
                      value={newClient.fiscal_code}
                      onChange={(e) => { setCfManual(true); setNewClient({ ...newClient, fiscal_code: e.target.value.toUpperCase() }); }}
                      placeholder="Dato obbligatorio · si compila automaticamente dai dati personali"
                    />
                    {cfManual && (
                      <button
                        type="button"
                        data-testid="cf-reset-auto"
                        onClick={() => setCfManual(false)}
                        className="text-xs px-3 py-1.5 rounded border border-blue-300 text-blue-700 hover:bg-blue-50"
                        title="Torna al calcolo automatico"
                      >
                        Auto
                      </button>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">
                    {newClient.belfiore_code
                      ? "Compilato automaticamente da: nome · cognome · sesso · data e luogo di nascita."
                      : "Seleziona prima il luogo di nascita per abilitare il calcolo automatico."}
                  </p>
                </div>

                {/* CONTATTI */}
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <Label>Email {!newClient.email_unavailable && <span className="text-rose-500">*</span>}</Label>
                    <label className="inline-flex items-center gap-1.5 text-[10px] text-slate-600 cursor-pointer select-none" title="Spunta se il cliente non ha un indirizzo email">
                      <input
                        type="checkbox"
                        data-testid="new-cli-email-unavailable"
                        checked={!!newClient.email_unavailable}
                        onChange={(e) => setNewClient({ ...newClient, email_unavailable: e.target.checked })}
                        className="h-3 w-3"
                      />
                      Non disponibile
                    </label>
                  </div>
                  <Input
                    data-testid="new-cli-email"
                    type="email"
                    value={newClient.email}
                    onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                    className={!newClient.email_unavailable && !(newClient.email || "").trim() ? "bg-rose-50/40 border-rose-200 focus-visible:border-rose-400" : ""}
                  />
                  {!newClient.email_unavailable && <p className="text-[10px] text-rose-600 mt-0.5 italic">⚠️ campo obbligatorio</p>}
                </div>
                <div>
                  <Label>Cellulare</Label>
                  <MobileInputWithWA
                    value={newClient.mobile}
                    onChange={(v) => setNewClient({ ...newClient, mobile: v })}
                    onBlur={(v) => setNewClient({ ...newClient, mobile: v })}
                    testid="new-cli-mobile"
                    clientName={`${newClient.name || ""} ${newClient.surname || ""}`.trim()}
                    placeholder="Es. 3331234567"
                  />
                </div>

                {/* 🆕 Task 1: RESIDENZA — campi nativi (non più nel campo note) */}
                <div className="col-span-2 mt-2 pt-3 border-t border-slate-200">
                  <p className="text-xs uppercase tracking-wide text-blue-700 font-bold mb-2">🏠 Residenza</p>
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                    <div className="sm:col-span-6">
                      <Label>Indirizzo (via/piazza) <span className="text-[10px] text-slate-400 normal-case">(auto-completata)</span></Label>
                      <AddressAutocomplete
                        testid="new-cli-address"
                        value={newClient.address || ""}
                        city={newClient.city || ""}
                        onChange={(v) => setNewClient({ ...newClient, address: v })}
                        onSelect={(item) => setNewClient((c) => ({
                          ...c,
                          address: item.road || "",
                          house_number: item.house_number || c.house_number || "",
                          cap: item.postcode || c.cap || "",
                          city: c.city || (item.city || "").toUpperCase(),
                          province: c.province || (item.province || "").toUpperCase().slice(0, 2),
                        }))}
                        placeholder="es. Via Roma"
                      />
                    </div>
                    <div className="sm:col-span-3">
                      <Label>Numero civico</Label>
                      <Input
                        data-testid="new-cli-house-number"
                        value={newClient.house_number || ""}
                        onChange={(e) => setNewClient({ ...newClient, house_number: e.target.value })}
                        placeholder="es. 12/A"
                      />
                    </div>
                    <div className="sm:col-span-3">
                      <Label>CAP</Label>
                      <Input
                        data-testid="new-cli-cap"
                        value={newClient.cap || ""}
                        onChange={(e) => {
                          const v = (e.target.value || "").replace(/\D/g, "").slice(0, 5);
                          setNewClient({ ...newClient, cap: v });
                        }}
                        placeholder="es. 00100"
                        maxLength={5}
                        inputMode="numeric"
                        pattern="[0-9]{5}"
                        className={
                          newClient.cap && newClient.cap.length > 0 && newClient.cap.length < 5
                            ? "border-rose-500 bg-rose-50/30"
                            : newClient.cap && newClient.cap.length === 5
                            ? "border-emerald-500 bg-emerald-50/30"
                            : ""
                        }
                      />
                      {newClient.cap && newClient.cap.length > 0 && newClient.cap.length < 5 && (
                        <p className="text-[10px] text-rose-600 mt-0.5">Il CAP deve avere 5 cifre.</p>
                      )}
                    </div>
                    <div className="sm:col-span-10">
                      <Label>Comune di residenza</Label>
                      <CityAutocomplete
                        value={newClient.city || ""}
                        onChange={(v) => setNewClient({ ...newClient, city: (v || "").toUpperCase() })}
                        onSelect={(c) => setNewClient({ ...newClient, city: (c.name || "").toUpperCase(), province: c.province || newClient.province, cap: c.cap || newClient.cap })}
                        placeholder="es. Roma"
                        inputClassName="uppercase"
                        testid="new-cli-city"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label>Provincia (sigla)</Label>
                      <Input
                        data-testid="new-cli-province"
                        value={newClient.province || ""}
                        onChange={(e) => setNewClient({ ...newClient, province: e.target.value.toUpperCase().slice(0, 2) })}
                        placeholder="es. RM"
                        maxLength={2}
                        className="uppercase"
                      />
                    </div>
                  </div>
                </div>
                </>)}

                {/* 🆕 Feb 2026 — Layout AZIENDA (Persona Giuridica) */}
                {newClient.subject_type === "company" && (<>
                  <div className="col-span-2">
                    <p className="text-xs uppercase tracking-wide text-blue-700 font-bold mb-2 flex items-center gap-1">🏢 Dati Azienda</p>
                  </div>

                  {/* 🆕 Feb 2026 — Zona AI upload Visura Camerale / PDF aziendale */}
                  <div className="col-span-2 rounded-lg border-2 border-purple-200 bg-purple-50/40 p-3 space-y-2" data-testid="visura-ai-zone">
                    <div className="flex items-center gap-2 text-sm font-semibold text-purple-900">
                      <Sparkles className="h-4 w-4" /> Auto-compila dai documenti aziendali
                    </div>
                    <p className="text-[11px] text-slate-600">
                      Carica una <strong>Visura Camerale</strong> (PDF o foto) o un altro PDF con dati societari (atto costitutivo, carta intestata): l&apos;AI estrarrà Ragione Sociale, P.IVA, CF, SDI, PEC, Sede Legale e Legale Rappresentante.
                    </p>
                    <div className="flex flex-wrap gap-2 items-center">
                      <label className={`inline-flex items-center gap-1.5 text-xs ${visuraUploading ? "cursor-wait bg-amber-100 border-amber-400 text-amber-900 animate-pulse" : "cursor-pointer bg-white border-purple-300 hover:bg-purple-100 text-purple-800"} border px-3 py-1.5 rounded-md font-medium transition-colors`}>
                        <input
                          type="file"
                          accept="application/pdf,image/jpeg,image/png,image/webp"
                          className="hidden"
                          disabled={visuraUploading}
                          data-testid="visura-file-input"
                          onChange={(e) => { processVisura(e.target.files?.[0]); e.target.value = ""; }}
                        />
                        {visuraUploading ? (
                          <span className="inline-flex items-center gap-1.5">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Estrazione in corso…
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5">
                            <Upload className="h-3.5 w-3.5" />
                            Carica Visura o PDF aziendale
                          </span>
                        )}
                      </label>
                      {visuraMessage && (
                        <span
                          data-testid="visura-message"
                          className={`text-[11px] px-2 py-1 rounded-md font-medium ${visuraMessage.color === "emerald" ? "bg-emerald-100 text-emerald-800 border border-emerald-300" : "bg-amber-100 text-amber-800 border border-amber-300"}`}
                        >
                          {visuraMessage.text}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="col-span-2">
                    <Label>Ragione Sociale <span className="text-red-500">*</span></Label>
                    <Input
                      data-testid="new-cli-ragione-sociale"
                      className="uppercase"
                      placeholder="es. ROSSI COSTRUZIONI SRL"
                      value={newClient.ragione_sociale || ""}
                      onChange={(e) => setNewClient({ ...newClient, ragione_sociale: e.target.value.toUpperCase() })}
                    />
                  </div>
                  <div>
                    <Label>Partita IVA <span className="text-red-500">*</span></Label>
                    {(() => {
                      const piva = (newClient.partita_iva || "").trim();
                      const validLen = piva.length === 11 && /^\d{11}$/.test(piva);
                      let checksumOk = false;
                      if (validLen) {
                        let s = 0;
                        for (let i = 0; i < 11; i++) {
                          let n = parseInt(piva[i], 10);
                          if (i % 2 === 1) { n *= 2; if (n > 9) n -= 9; }
                          s += n;
                        }
                        checksumOk = (s % 10) === 0;
                      }
                      const invalid = piva.length > 0 && (!validLen || !checksumOk);
                      const valid = piva.length === 11 && checksumOk;
                      return (
                        <>
                          <Input
                            data-testid="new-cli-partita-iva"
                            className={`font-mono ${invalid ? "border-rose-500 bg-rose-50/30" : valid ? "border-emerald-500 bg-emerald-50/30" : ""}`}
                            placeholder="11 cifre"
                            value={piva}
                            onChange={(e) => setNewClient({ ...newClient, partita_iva: (e.target.value || "").replace(/\D/g, "").slice(0, 11) })}
                            maxLength={11}
                            inputMode="numeric"
                          />
                          {invalid && <p className="text-[10px] text-rose-600 mt-0.5">P.IVA non valida (11 cifre, checksum errato)</p>}
                          {valid && <p className="text-[10px] text-emerald-700 mt-0.5">✓ P.IVA formalmente valida</p>}
                        </>
                      );
                    })()}
                  </div>
                  <div>
                    <Label>Codice Fiscale Societario</Label>
                    <Input
                      data-testid="new-cli-cf-societario"
                      className="font-mono uppercase"
                      placeholder="Se diverso dalla P.IVA"
                      value={newClient.fiscal_code || ""}
                      onChange={(e) => setNewClient({ ...newClient, fiscal_code: (e.target.value || "").toUpperCase().trim() })}
                    />
                    <p className="text-[10px] text-slate-500 mt-0.5">Compila solo se diverso dalla P.IVA</p>
                  </div>
                  <div>
                    <Label>Codice Univoco SDI</Label>
                    <Input
                      data-testid="new-cli-sdi"
                      className="font-mono uppercase"
                      placeholder="7 caratteri (fatturazione elettronica)"
                      value={newClient.sdi_code || ""}
                      onChange={(e) => setNewClient({ ...newClient, sdi_code: (e.target.value || "").toUpperCase().slice(0, 7) })}
                      maxLength={7}
                    />
                  </div>
                  <div>
                    <Label>PEC Aziendale</Label>
                    <Input
                      data-testid="new-cli-pec"
                      type="email"
                      placeholder="pec@aziendarossi.it"
                      value={newClient.pec_email || ""}
                      onChange={(e) => setNewClient({ ...newClient, pec_email: e.target.value })}
                    />
                  </div>

                  {/* Contatti Aziendali */}
                  <div>
                    <div className="flex items-center justify-between gap-2">
                      <Label>Email Aziendale {!newClient.email_unavailable && <span className="text-rose-500">*</span>}</Label>
                      <label className="inline-flex items-center gap-1.5 text-[10px] text-slate-600 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          data-testid="new-cli-company-email-unavailable"
                          checked={!!newClient.email_unavailable}
                          onChange={(e) => setNewClient({ ...newClient, email_unavailable: e.target.checked })}
                          className="h-3 w-3"
                        />
                        Non disponibile
                      </label>
                    </div>
                    <Input
                      data-testid="new-cli-company-email"
                      type="email"
                      value={newClient.email}
                      onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                      className={!newClient.email_unavailable && !(newClient.email || "").trim() ? "bg-rose-50/40 border-rose-200" : ""}
                    />
                  </div>
                  <div>
                    <Label>Telefono Aziendale</Label>
                    <Input
                      data-testid="new-cli-company-phone"
                      value={newClient.mobile || newClient.phone || ""}
                      onChange={(e) => setNewClient({ ...newClient, mobile: e.target.value })}
                      placeholder="es. 0212345678 o 3331234567"
                    />
                  </div>

                  {/* Sede Legale */}
                  <div className="col-span-2 mt-2 pt-3 border-t border-slate-200">
                    <p className="text-xs uppercase tracking-wide text-blue-700 font-bold mb-2">🏛️ Sede Legale</p>
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                      <div className="sm:col-span-6">
                        <Label>Indirizzo (via/piazza)</Label>
                        <AddressAutocomplete
                          testid="new-cli-company-address"
                          value={newClient.address || ""}
                          city={newClient.city || ""}
                          onChange={(v) => setNewClient({ ...newClient, address: v })}
                          onSelect={(item) => setNewClient((c) => ({
                            ...c,
                            address: item.road || "",
                            house_number: item.house_number || c.house_number || "",
                            cap: item.postcode || c.cap || "",
                            city: c.city || (item.city || "").toUpperCase(),
                            province: c.province || (item.province || "").toUpperCase().slice(0, 2),
                          }))}
                          placeholder="es. Via Milano"
                        />
                      </div>
                      <div className="sm:col-span-3">
                        <Label>Numero civico</Label>
                        <Input
                          data-testid="new-cli-company-house-number"
                          value={newClient.house_number || ""}
                          onChange={(e) => setNewClient({ ...newClient, house_number: e.target.value })}
                        />
                      </div>
                      <div className="sm:col-span-3">
                        <Label>CAP</Label>
                        <Input
                          data-testid="new-cli-company-cap"
                          value={newClient.cap || ""}
                          onChange={(e) => setNewClient({ ...newClient, cap: (e.target.value || "").replace(/\D/g, "").slice(0, 5) })}
                          maxLength={5}
                          inputMode="numeric"
                        />
                      </div>
                      <div className="sm:col-span-10">
                        <Label>Comune (sede legale)</Label>
                        <CityAutocomplete
                          value={newClient.city || ""}
                          onChange={(v) => setNewClient({ ...newClient, city: (v || "").toUpperCase() })}
                          onSelect={(c) => setNewClient({ ...newClient, city: (c.name || "").toUpperCase(), province: c.province || newClient.province, cap: c.cap || newClient.cap })}
                          inputClassName="uppercase"
                          testid="new-cli-company-city"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label>Provincia (sigla)</Label>
                        <Input
                          data-testid="new-cli-company-province"
                          value={newClient.province || ""}
                          onChange={(e) => setNewClient({ ...newClient, province: e.target.value.toUpperCase().slice(0, 2) })}
                          maxLength={2}
                          className="uppercase"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Legale Rappresentante */}
                  <div className="col-span-2 mt-2 pt-3 border-t border-slate-200">
                    <p className="text-xs uppercase tracking-wide text-blue-700 font-bold mb-2">👔 Legale Rappresentante</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <Label>Cognome L.R.</Label>
                        <Input
                          data-testid="new-cli-lr-surname"
                          className="uppercase"
                          value={newClient.legale_rappresentante_surname || ""}
                          onChange={(e) => setNewClient({ ...newClient, legale_rappresentante_surname: e.target.value.toUpperCase() })}
                        />
                      </div>
                      <div>
                        <Label>Nome L.R.</Label>
                        <Input
                          data-testid="new-cli-lr-name"
                          className="uppercase"
                          value={newClient.legale_rappresentante_name || ""}
                          onChange={(e) => { const v = e.target.value.toUpperCase(); setNewClient({ ...newClient, legale_rappresentante_name: v, ...autoGenderPatch(v, "legale_rappresentante_gender", genderManualRef.current) }); }}
                        />
                      </div>
                      <div>
                        <Label>Codice Fiscale L.R.</Label>
                        <div className="flex gap-1.5">
                          <Input
                            data-testid="new-cli-lr-cf"
                            className="font-mono uppercase flex-1"
                            maxLength={16}
                            value={newClient.legale_rappresentante_fiscal_code || ""}
                            onChange={(e) => setNewClient({ ...newClient, legale_rappresentante_fiscal_code: (e.target.value || "").toUpperCase().trim() })}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={decodeLRCF}
                            disabled={lrCfLoading}
                            data-testid="new-cli-lr-cf-decode"
                            title="Decodifica CF L.R. e popola data/luogo/sesso/provincia"
                            className="border-purple-300 text-purple-700 hover:bg-purple-50 shrink-0"
                          >
                            <Sparkles className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={calculateLRCF}
                            disabled={lrCfLoading}
                            data-testid="new-cli-lr-cf-calc"
                            title="Calcola CF dai dati anagrafici del Legale Rappresentante"
                            className="border-indigo-300 text-indigo-700 hover:bg-indigo-50 shrink-0"
                          >
                            <Calculator className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      <div>
                        <Label>Data di Nascita L.R.</Label>
                        <DatePicker
                          data-testid="new-cli-lr-birth-date"
                          value={newClient.legale_rappresentante_birth_date || ""}
                          onChange={(e) => setNewClient({ ...newClient, legale_rappresentante_birth_date: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Luogo di Nascita L.R.</Label>
                        <Input
                          data-testid="new-cli-lr-birth-place"
                          className="uppercase"
                          placeholder="Es. ROMA"
                          value={newClient.legale_rappresentante_birth_place || ""}
                          onChange={(e) => setNewClient({ ...newClient, legale_rappresentante_birth_place: e.target.value.toUpperCase() })}
                        />
                      </div>
                      <div>
                        <Label>Provincia Nascita L.R.</Label>
                        <Input
                          data-testid="new-cli-lr-birth-province"
                          className="uppercase"
                          maxLength={2}
                          placeholder="RM"
                          value={newClient.legale_rappresentante_birth_province || ""}
                          onChange={(e) => setNewClient({ ...newClient, legale_rappresentante_birth_province: e.target.value.toUpperCase().slice(0, 2) })}
                        />
                      </div>
                      <div>
                        <Label>Sesso L.R.</Label>
                        <Select value={newClient.legale_rappresentante_gender || ""} onValueChange={(v) => { genderManualRef.current.add("legale_rappresentante_gender"); setNewClient({ ...newClient, legale_rappresentante_gender: v }); }}>
                          <SelectTrigger data-testid="new-cli-lr-gender"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="M">Maschio</SelectItem>
                            <SelectItem value="F">Femmina</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Carica</Label>
                        <Select value={newClient.legale_rappresentante_role || ""} onValueChange={(v) => setNewClient({ ...newClient, legale_rappresentante_role: v })}>
                          <SelectTrigger data-testid="new-cli-lr-role"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Amministratore Unico">Amministratore Unico</SelectItem>
                            <SelectItem value="Presidente CDA">Presidente CDA</SelectItem>
                            <SelectItem value="Amministratore Delegato">Amministratore Delegato</SelectItem>
                            <SelectItem value="Consigliere Delegato">Consigliere Delegato</SelectItem>
                            <SelectItem value="Legale Rappresentante">Legale Rappresentante</SelectItem>
                            <SelectItem value="Titolare">Titolare (ditta individuale)</SelectItem>
                            <SelectItem value="Socio Accomandatario">Socio Accomandatario</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Nazionalità</Label>
                        <Input
                          data-testid="new-cli-lr-nationality"
                          className="uppercase"
                          maxLength={2}
                          placeholder="IT"
                          value={newClient.legale_rappresentante_nationality || ""}
                          onChange={(e) => setNewClient({ ...newClient, legale_rappresentante_nationality: e.target.value.toUpperCase().slice(0, 2) })}
                        />
                      </div>
                      {/* 🆕 Feb 2026 — Contatti diretti Legale Rappresentante (usati per firma OTP azienda) */}
                      <div>
                        <Label>📱 Cellulare LR <span className="text-[10px] text-purple-700 font-medium">(firma OTP)</span></Label>
                        <Input
                          data-testid="new-cli-lr-mobile"
                          type="tel"
                          placeholder="+39 333 1234567"
                          value={newClient.legale_rappresentante_mobile || ""}
                          onChange={(e) => setNewClient({ ...newClient, legale_rappresentante_mobile: e.target.value })}
                        />
                        <p className="text-[10px] text-slate-500 mt-1">Se compilato, l&apos;OTP di firma SMS verrà inviato a questo numero invece che a quello aziendale.</p>
                      </div>
                      <div>
                        <Label>📧 Email LR</Label>
                        <Input
                          data-testid="new-cli-lr-email"
                          type="email"
                          placeholder="mario.rossi@azienda.it"
                          value={newClient.legale_rappresentante_email || ""}
                          onChange={(e) => setNewClient({ ...newClient, legale_rappresentante_email: e.target.value })}
                        />
                        <p className="text-[10px] text-slate-500 mt-1">Se compilata, l&apos;OTP di firma via email arriva al Legale Rappresentante.</p>
                      </div>
                    </div>
                  </div>
                </>)}

                {/* Pulsante crea cliente — abilitato solo dopo aver scelto il tipo soggetto */}
                {newClient.subject_type && (
                  <div className="col-span-2 flex gap-2 pt-2 border-t border-slate-200 mt-2">
                    <Button data-testid="create-new-cli" onClick={createNewClient} disabled={creatingClient} className="bg-[#0F172A] hover:bg-[#1E293B]">
                      {creatingClient ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
                      Crea cliente e prosegui
                    </Button>
                    <Button variant="outline" onClick={() => setClientMode(null)}>Indietro</Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* STEP 1 — Client docs */}
        {step === 1 && (
          <DropZone
            testid="dropzone-client-docs"
            onFiles={(files) => uploadClientDocs(files)}
            accept={["application/pdf", "image/jpeg", "image/png", "image/webp"]}
            multiple
            disabled={uploadingDoc}
            className="space-y-4 p-2 rounded-md"
          >
            <h2 className="font-display text-xl font-semibold flex items-center gap-2"><FileText className="h-5 w-5 text-blue-600" /> Documenti personali del cliente <span className="text-xs text-slate-400 font-normal">(opzionale)</span></h2>
            <p className="text-xs text-blue-700">💡 Suggerimento: puoi anche <strong>trascinare i file</strong> direttamente in questa zona.</p>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label>Tipo documento</Label>
                <Select value={docKind} onValueChange={setDocKind}>
                  <SelectTrigger data-testid="client-doc-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CLIENT_DOC_KINDS.map((k) => <SelectItem key={k.v} value={k.v}>{k.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button
                data-testid="upload-client-docs"
                onClick={() => clientDocInputRef.current?.click()}
                disabled={uploadingDoc}
              >
                {uploadingDoc ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                Carica file (anche multipli)
              </Button>
              <ScanButton
                testid="scan-wizard-client-docs"
                label="Importa da scanner"
                variant="outline"
                disabled={uploadingDoc}
                onFile={(f) => uploadClientDocs([f])}
              />
<CameraButton testid="cam-scan-wizard-client-docs" label="Foto da fotocamera" variant="outline" disabled={uploadingDoc} onFile={(f) => uploadClientDocs([f])} />
              <input ref={clientDocInputRef} type="file" multiple accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden" onChange={handleClientDocsInput} />
            </div>
            <div className="space-y-1.5" data-testid="client-docs-list">
              {clientDocs.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Nessun documento caricato ancora. Trascina i file qui o usa il pulsante sopra.</p>
              ) : clientDocs.map((d) => (
                <div key={d.id} className="bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm flex items-center gap-3">
                  <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                  <span className="flex-1 truncate" data-testid={`client-doc-name-${d.id}`}>{d.original_filename}</span>
                  <span className="text-xs text-slate-500 shrink-0">{(d.kind || "").replace(/_/g, " ")}</span>
                  <button
                    type="button"
                    data-testid={`client-doc-rename-${d.id}`}
                    onClick={() => promptRenameAttachment(d, "client")}
                    className="p-1 rounded hover:bg-slate-200 text-slate-500 hover:text-blue-600 shrink-0"
                    title="Rinomina file"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </DropZone>
        )}

        {/* STEP 2 — Policy data */}
        {step === 2 && (
          <div className="space-y-4">
            <h2 className="font-display text-xl font-semibold flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-blue-600" /> {isQuote ? "Dati preventivo" : "Dati polizza"}</h2>

            {/* AI: estrazione dati da PDF polizza */}
            <DropZone
              testid="dropzone-policy-pdf"
              onFiles={(files) => { if (files[0]) extractFromPolicyPDF(files[0]); }}
              accept={["application/pdf"]}
              multiple={false}
              disabled={polPdfUploading}
              className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4"
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-blue-900">📄 Importa dati da PDF polizza o preventivo</p>
                  <p className="text-xs text-blue-800 mt-0.5">
                    Carica un PDF di polizza o preventivo esistente (o <strong>trascina il file qui</strong>): l&apos;AI estrae automaticamente numero, compagnia, premio, decorrenza, scadenza e frazionamento.
                    Verifica anche che l&apos;intestatario corrisponda al cliente <strong>{selectedClient?.surname} {selectedClient?.name}</strong>.
                  </p>
                  {policyPdfFile && (
                    <p data-testid="policy-pdf-attach-note" className="text-[11px] text-emerald-700 font-semibold mt-1">
                      📎 Il file <span className="font-mono">{policyPdfFile.name}</span> sarà allegato automaticamente alla polizza al momento della creazione.
                    </p>
                  )}
                </div>
                <input
                  ref={polPdfInputRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  data-testid="wizard-policy-pdf-input"
                  onChange={onPolPdfSelect}
                />
                <Button
                  data-testid="wizard-import-policy-pdf"
                  onClick={() => polPdfInputRef.current?.click()}
                  disabled={polPdfUploading}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {polPdfUploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                  {polPdfUploading ? "Analisi AI…" : "Carica PDF polizza o preventivo"}
                </Button>
              </div>
              {polPdfMismatch && (
                <div
                  data-testid="wizard-policy-pdf-banner"
                  className={`mt-3 rounded-md p-3 text-sm bg-${polPdfMismatch.color}-50 border border-${polPdfMismatch.color}-300 text-${polPdfMismatch.color}-900`}
                >
                  <p className="font-semibold">{polPdfMismatch.message}</p>
                  <p className="text-xs mt-1">
                    Intestatario rilevato sulla polizza: <strong>{polPdfMismatch.polClient}</strong>
                    {polPdfMismatch.polCF && polPdfMismatch.polCF !== "—" && <> · CF <span className="font-mono">{polPdfMismatch.polCF}</span></>}
                  </p>
                </div>
              )}
              {/* Garanzie estratte dal PDF */}
              {polGuarantees.length > 0 && (
                <div
                  data-testid="wizard-policy-guarantees"
                  className="mt-3 rounded-md bg-white border border-indigo-200 p-3"
                >
                  <p className="text-xs uppercase tracking-wide text-indigo-700 font-bold mb-2">
                    🛡️ Garanzie rilevate ({polGuarantees.length})
                  </p>
                  <ul className="space-y-1.5">
                    {polGuarantees.map((g, idx) => {
                      // 🆕 22/07/2026 — Badge "Master" / "Custom" in base all'aggancio effettivo del coverage
                      const cov = policy.coverages?.[idx];
                      const isMaster = !!cov?.company_guarantee_id;
                      const masterName = cov?.name || g.name;
                      return (
                      <li
                        key={`gar-${idx}`}
                        className="flex items-start justify-between gap-2 text-xs border-b border-slate-100 pb-1.5 last:border-b-0 last:pb-0"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="font-semibold text-slate-800">{masterName || g.code || "Garanzia"}</p>
                            {isMaster ? (
                              <span
                                data-testid={`gar-master-badge-${idx}`}
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 text-[9px] font-bold uppercase tracking-wide"
                                title={`Agganciata a "${masterName}" del listino compagnia · Imposte ${cov?.imposte_percent ?? "—"}% · SSN ${cov?.ssn_percent ?? "—"}%`}
                              >
                                🎯 Master
                              </span>
                            ) : (
                              <span
                                data-testid={`gar-custom-badge-${idx}`}
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 text-[9px] font-bold uppercase tracking-wide"
                                title="Non collegata al listino compagnia · Netto/Imposte/SSN non calcolati automaticamente"
                              >
                                ⚠️ Custom
                              </span>
                            )}
                          </div>
                          {(g.sum_insured || g.deductible) && (
                            <p className="text-[11px] text-slate-500 mt-0.5">
                              {g.sum_insured && <>Massimale: <strong>{typeof g.sum_insured === "number" ? formatCurrency(g.sum_insured) : g.sum_insured}</strong></>}
                              {g.sum_insured && g.deductible && " · "}
                              {g.deductible && <>Franchigia: <strong>{typeof g.deductible === "number" ? formatCurrency(g.deductible) : g.deductible}</strong></>}
                            </p>
                          )}
                        </div>
                        {g.premium != null && (
                          <span className="text-xs font-mono font-semibold text-emerald-700 whitespace-nowrap">
                            {typeof g.premium === "number" ? formatCurrency(g.premium) : g.premium}
                          </span>
                        )}
                      </li>
                      );
                    })}
                  </ul>
                  <p className="text-[10px] text-slate-500 mt-2 italic">
                    Le garanzie sono salvate insieme alla polizza al momento della creazione.
                    {" "}Le garanzie <strong>Master</strong> ereditano Imposte % e SSN % dal listino compagnia.
                  </p>
                </div>
              )}
            </DropZone>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="col-span-2 bg-violet-50 border border-violet-200 rounded-md p-3">
                <Label className="text-violet-800 font-semibold text-xs uppercase tracking-wide">📌 Origine polizza</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-1.5">
                  <Select value={policy.source || "manuale"} onValueChange={(v) => setPolicy({ ...policy, source: v, source_quote_id: v === "da_preventivo" ? policy.source_quote_id : "" })}>
                    <SelectTrigger data-testid="pol-source" className="bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manuale">Manuale</SelectItem>
                      <SelectItem value="da_preventivo">Da preventivo</SelectItem>
                      <SelectItem value="rinnovo">Rinnovo</SelectItem>
                    </SelectContent>
                  </Select>
                  {policy.source === "da_preventivo" && (
                    <div className="col-span-2">
                      <Select value={policy.source_quote_id || "none"} onValueChange={(v) => setPolicy({ ...policy, source_quote_id: v === "none" ? "" : v })}>
                        <SelectTrigger data-testid="pol-source-quote" className="bg-white"><SelectValue placeholder="Seleziona preventivo…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Nessuno —</SelectItem>
                          {clientQuotes.length === 0 && (
                            <SelectItem value="empty" disabled>Nessun preventivo per questo cliente</SelectItem>
                          )}
                          {clientQuotes.map((q) => (
                            <SelectItem key={q.id} value={q.id}>
                              {q.quote_number || q.id.slice(0, 8)} · {q.type} {q.status === "accettato" ? "✓" : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                {policy.source === "da_preventivo" && policy.source_quote_id && (
                  <p className="text-[11px] text-violet-700 mt-1.5">
                    ℹ Salvando, il preventivo verrà marcato come <strong>accettato</strong> e linkato a questa polizza.
                  </p>
                )}
              </div>
              <div className="col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label>Tipo *</Label>
                  <Select value={policy.type} onValueChange={(v) => setPolicy({ ...policy, type: v })}>
                    <SelectTrigger data-testid="pol-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{policyTypes.map((t) => {
                      const isEmoji = !TYPE_ICONS[t.icon] && !!t.icon;
                      const I = TYPE_ICONS[t.icon] || Shield;
                      return (
                        <SelectItem key={t.v} value={t.v}>
                          <span className="inline-flex items-center gap-2">
                            {isEmoji ? (
                              <span className="text-base leading-none w-4 text-center">{t.icon}</span>
                            ) : (
                              <I className="h-4 w-4 text-slate-600" />
                            )}
                            <span>{t.l}</span>
                            {t.is_pinned && <span className="text-amber-500 text-xs">★</span>}
                          </span>
                        </SelectItem>
                      );
                    })}</SelectContent>
                  </Select>
                </div>
                {/* 🆕 Feb 2026 — Situazione assicurativa (solo wizard preventivo) — da lookup-list "insurance_status" */}
                {isQuote && insuranceStatusOptions.length > 0 && (
                  <div>
                    <Label>Situazione assicurativa <span className="text-[10px] font-normal text-slate-500">(stato del veicolo)</span></Label>
                    <Select
                      value={policy.insurance_status || "_none"}
                      onValueChange={(v) => setPolicy((p) => ({ ...p, insurance_status: v === "_none" ? "" : v }))}
                    >
                      <SelectTrigger data-testid="pol-insurance-status"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">— Non specificata —</SelectItem>
                        {insuranceStatusOptions.map((o) => (
                          <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-slate-500 mt-0.5">Voci gestibili in <em>Liste preventivo → Situazione assicurativa</em>.</p>
                  </div>
                )}
                {/* 🆕 Feb 2026 — Attestato di rischio altro veicolo (visibile solo se la voce Situazione Assicurativa ha flag `enables_other_attestato`) */}
                {isQuote && bersaniSourceOptions.length > 0 && !!insuranceStatusOptions.find((o) => o.code === policy.insurance_status)?.enables_other_attestato && (
                  <div>
                    <Label>Attestato di rischio altro veicolo? <span className="text-red-600">*</span></Label>
                    <Select
                      value={policy.bersani_source || "_none"}
                      onValueChange={(v) => setPolicy((p) => ({ ...p, bersani_source: v === "_none" ? "" : v }))}
                    >
                      <SelectTrigger
                        data-testid="pol-bersani-source"
                        className={!policy.bersani_source ? "bg-amber-100 border-amber-400 ring-2 ring-amber-300 focus-visible:border-amber-500 shadow-sm" : ""}
                      >
                        <SelectValue placeholder="Seleziona…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">— Seleziona… —</SelectItem>
                        {bersaniSourceOptions.map((o) => (
                          <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!policy.bersani_source
                      ? <p className="text-[10px] text-amber-700 font-medium mt-0.5">⚠ Campo obbligatorio</p>
                      : <p className="text-[10px] text-slate-500 mt-0.5">Voci gestibili in <em>Liste preventivo → Attestato altro veicolo</em>.</p>
                    }
                  </div>
                )}
              </div>
              {/* 🆕 Feb 2026 — Mini-form "Dati veicolo di origine" quando la voce selezionata richiede targa altro veicolo */}
              {isQuote && !!bersaniSourceOptions.find((o) => o.code === policy.bersani_source)?.requires_other_plate && (
                <div data-testid="pol-other-vehicle-box" className="col-span-2 rounded-lg border-2 border-sky-300 bg-gradient-to-r from-sky-50/70 to-cyan-50/70 p-4">
                  <div className="flex items-center gap-2 text-sky-800 font-semibold text-sm mb-3">
                    🚘 Dati veicolo di origine
                    <span className="text-[10px] font-normal text-sky-700">({(bersaniSourceOptions.find((o) => o.code === policy.bersani_source)?.label) || ""})</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                    <div className="sm:col-span-2">
                      <Label>Targa veicolo di origine *</Label>
                      <Input
                        value={policy.other_vehicle_plate || ""}
                        onChange={(e) => setPolicy((p) => ({ ...p, other_vehicle_plate: (e.target.value || "").toUpperCase() }))}
                        placeholder="es. AB123CD"
                        maxLength={10}
                        className="h-9 text-sm font-mono uppercase"
                        data-testid="pol-other-vehicle-plate"
                      />
                    </div>
                    <div>
                      <Label>Marca</Label>
                      <Input
                        value={policy.other_vehicle_brand || ""}
                        onChange={(e) => setPolicy((p) => ({ ...p, other_vehicle_brand: e.target.value }))}
                        placeholder="es. Fiat"
                        className="h-9 text-sm"
                        data-testid="pol-other-vehicle-brand"
                      />
                    </div>
                    <div>
                      <Label>Modello</Label>
                      <Input
                        value={policy.other_vehicle_model || ""}
                        onChange={(e) => setPolicy((p) => ({ ...p, other_vehicle_model: e.target.value }))}
                        placeholder="es. Panda"
                        className="h-9 text-sm"
                        data-testid="pol-other-vehicle-model"
                      />
                    </div>
                    <div>
                      <Label>Anno 1ª immatricolazione</Label>
                      <Input
                        type="date"
                        value={policy.other_vehicle_first_registration || ""}
                        onChange={(e) => setPolicy((p) => ({ ...p, other_vehicle_first_registration: e.target.value }))}
                        className="h-9 text-sm"
                        data-testid="pol-other-vehicle-first-reg"
                      />
                    </div>
                    <div className="sm:col-span-3">
                      <Label>Note aggiuntive</Label>
                      <Input
                        value={policy.other_vehicle_notes || ""}
                        onChange={(e) => setPolicy((p) => ({ ...p, other_vehicle_notes: e.target.value }))}
                        placeholder="es. Data cessione, demolizione, denuncia furto…"
                        className="h-9 text-sm"
                        data-testid="pol-other-vehicle-notes"
                      />
                    </div>
                  </div>
                </div>
              )}
              {/* 🆕 Feb 2026 — Sezione Bersani/RC Familiare: visibile se la voce `bersani_source` ha `opens_bersani_section=true`.
                  Fallback retrocompatibile: se bersani_source non è configurato, usa il vecchio flag `insurance_status.triggers_bersani`. */}
              {isQuote && (
                (!!bersaniSourceOptions.find((o) => o.code === policy.bersani_source)?.opens_bersani_section)
                || (!policy.bersani_source && !!insuranceStatusOptions.find((o) => o.code === policy.insurance_status)?.triggers_bersani)
              ) && (
                <div data-testid="pol-bersani-section" className="col-span-2 rounded-lg border-2 border-emerald-300 bg-gradient-to-r from-emerald-50/70 to-teal-50/70 p-4">
                  <div className="flex items-center gap-2 text-emerald-800 font-semibold text-sm mb-3">
                    <Sparkles className="h-4 w-4" /> Agevolazione: Legge Bersani o RC Familiare
                  </div>

                  {/* 🆕 Feb 2026 — Ricerca cliente esistente per pre-compilazione automatica dei dati anagrafici */}
                  <div className="mb-4 rounded-md border border-emerald-200 bg-white/70 p-3 relative">
                    <Label className="text-xs text-emerald-800 font-semibold flex items-center gap-1 mb-1">
                      <Search className="h-3 w-3" /> Cerca cliente esistente (nome, cognome, CF)
                    </Label>
                    <Input
                      data-testid="pol-bersani-client-search"
                      value={bersaniSearchQ}
                      onChange={(e) => { setBersaniSearchQ(e.target.value); searchBersaniClients(e.target.value); }}
                      placeholder="Inizia a digitare per cercare…"
                      className="bg-white"
                    />
                    {bersaniSearching && <div className="text-[11px] text-slate-500 mt-1">Ricerca in corso…</div>}
                    {bersaniSearchResults.length > 0 && (
                      <div className="absolute left-3 right-3 top-full mt-1 z-30 max-h-56 overflow-auto rounded-md border border-emerald-300 bg-white shadow-lg" data-testid="pol-bersani-client-results">
                        {bersaniSearchResults.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => applyBersaniClient(c)}
                            className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b border-slate-100 last:border-b-0 text-sm"
                            data-testid={`pol-bersani-client-${c.id}`}
                          >
                            <div className="font-medium text-slate-800">{c.name} {c.surname}</div>
                            <div className="text-[11px] text-slate-500 font-mono">{c.fiscal_code || "— senza CF"} · {c.birth_place || "—"} · {formatDate(c.birth_date) || "—"}</div>
                          </button>
                        ))}
                      </div>
                    )}
                    <p className="text-[11px] text-slate-500 mt-1">Se trovato, importo automaticamente Nome, Cognome, CF, Data e Luogo di nascita.</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label>Tipo di agevolazione</Label>
                      <Select
                        value={policy.bersani_type || "_none"}
                        onValueChange={(v) => setPolicy((p) => ({ ...p, bersani_type: v === "_none" ? "" : v }))}
                      >
                        <SelectTrigger data-testid="pol-bersani-type"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">— Seleziona —</SelectItem>
                          <SelectItem value="legge_bersani">Legge Bersani</SelectItem>
                          <SelectItem value="rc_familiare">RC Familiare</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Targa del veicolo di riferimento</Label>
                      <PlateInput
                        testid="pol-bersani-ref-plate"
                        value={policy.bersani_ref_plate || ""}
                        onChange={(v) => setPolicy((p) => ({ ...p, bersani_ref_plate: v }))}
                        overrideFlag={bersaniPlateOverride}
                        onOverrideChange={setBersaniPlateOverride}
                        placeholder="AB123CD"
                      />
                    </div>
                    <div className="relative">
                      <Label>Compagnia assicurativa del veicolo di riferimento</Label>
                      {/* 🆕 Feb 2026 — Autocomplete predittivo su compagnie CRM (filtro "contiene", top 8) */}
                      <Input
                        data-testid="pol-bersani-ref-company"
                        value={policy.bersani_ref_company || ""}
                        onChange={(e) => { setPolicy((p) => ({ ...p, bersani_ref_company: e.target.value })); setBersaniCompanyOpen(true); }}
                        onFocus={() => setBersaniCompanyOpen(true)}
                        onBlur={() => setTimeout(() => setBersaniCompanyOpen(false), 150)}
                        placeholder="Digita per suggerimenti…"
                        autoComplete="off"
                      />
                      {bersaniCompanyOpen && (() => {
                        const q = (policy.bersani_ref_company || "").trim().toLowerCase();
                        const list = (companies || [])
                          .filter((c) => !q || (c.name || "").toLowerCase().includes(q))
                          .slice(0, 8);
                        if (list.length === 0) return null;
                        return (
                          <div className="absolute left-0 right-0 top-full mt-1 z-30 max-h-56 overflow-auto rounded-md border border-emerald-300 bg-white shadow-lg" data-testid="pol-bersani-ref-company-suggestions">
                            {list.map((c) => (
                              <button
                                key={c.id}
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); setPolicy((p) => ({ ...p, bersani_ref_company: c.name })); setBersaniCompanyOpen(false); }}
                                className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b border-slate-100 last:border-b-0 text-sm"
                                data-testid={`pol-bersani-ref-company-${c.id}`}
                              >
                                {c.name}
                              </button>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                    <div>
                      <Label>Classe di merito del veicolo di riferimento</Label>
                      <Select
                        value={policy.bersani_ref_merit_class || "_none"}
                        onValueChange={(v) => setPolicy((p) => ({ ...p, bersani_ref_merit_class: v === "_none" ? "" : v }))}
                      >
                        <SelectTrigger data-testid="pol-bersani-ref-merit-class"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                        <SelectContent className="max-h-72">
                          <SelectItem value="_none">— Non specificata —</SelectItem>
                          {["1","2","3","4","5","6","7","8","9","10","11","12","13","14",
                            "CU1","CU2","CU3","CU4","CU5","CU6","CU7","CU8","CU9",
                            "CU10","CU11","CU12","CU13","CU14","CU15","CU16","CU17","CU18"]
                            .map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* 🆕 Feb 2026 — Dati anagrafici intestatario per generatore CF */}
                    <div>
                      <Label>Nome intestatario</Label>
                      <Input
                        data-testid="pol-bersani-ref-owner-name"
                        value={policy.bersani_ref_owner_name || ""}
                        onChange={(e) => {
                          const name = e.target.value;
                          setPolicy((p) => ({
                            ...p,
                            bersani_ref_owner_name: name,
                            bersani_ref_owner: [name, p.bersani_ref_owner_surname].filter(Boolean).join(" ").trim(),
                          }));
                        }}
                        placeholder="es. Mario"
                      />
                    </div>
                    <div>
                      <Label>Cognome intestatario</Label>
                      <Input
                        data-testid="pol-bersani-ref-owner-surname"
                        value={policy.bersani_ref_owner_surname || ""}
                        onChange={(e) => {
                          const surname = e.target.value;
                          setPolicy((p) => ({
                            ...p,
                            bersani_ref_owner_surname: surname,
                            bersani_ref_owner: [p.bersani_ref_owner_name, surname].filter(Boolean).join(" ").trim(),
                          }));
                        }}
                        placeholder="es. Rossi"
                      />
                    </div>
                    <div>
                      <Label>Luogo di nascita intestatario</Label>
                      <CityAutocomplete
                        testid="pol-bersani-ref-owner-birth-place"
                        value={policy.bersani_ref_owner_birth_place || ""}
                        onChange={(v) => setPolicy((p) => ({ ...p, bersani_ref_owner_birth_place: v }))}
                        placeholder="es. Roma"
                      />
                    </div>
                    <div>
                      <Label>Data di nascita intestatario</Label>
                      <DatePicker
                        data-testid="pol-bersani-ref-owner-birth-date"
                        value={policy.bersani_ref_owner_birth_date || ""}
                        onChange={(e) => setPolicy((p) => ({ ...p, bersani_ref_owner_birth_date: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Sesso intestatario</Label>
                      <Select
                        value={policy.bersani_ref_owner_gender || "_none"}
                        onValueChange={(v) => setPolicy((p) => ({ ...p, bersani_ref_owner_gender: v === "_none" ? "" : v }))}
                      >
                        <SelectTrigger data-testid="pol-bersani-ref-owner-gender"><SelectValue placeholder="M/F" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">— Seleziona —</SelectItem>
                          <SelectItem value="M">M</SelectItem>
                          <SelectItem value="F">F</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-2">
                      <Label>Codice fiscale intestatario</Label>
                      <div className="flex gap-2">
                        <Input
                          data-testid="pol-bersani-ref-owner-cf"
                          value={policy.bersani_ref_owner_fiscal_code || ""}
                          onChange={(e) => setPolicy((p) => ({ ...p, bersani_ref_owner_fiscal_code: (e.target.value || "").toUpperCase() }))}
                          placeholder="es. RSSMRA80A01H501U"
                          maxLength={16}
                          className="font-mono uppercase flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={generateBersaniCf}
                          disabled={bersaniCfBusy}
                          data-testid="pol-bersani-generate-cf"
                          title="Calcola il CF da nome, cognome, sesso, data e luogo di nascita"
                          className="whitespace-nowrap"
                        >
                          {bersaniCfBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                          Genera codice fiscale
                        </Button>
                      </div>
                    </div>
                    {/* 🆕 Feb 2026 — Upload libretto di circolazione del veicolo di riferimento */}
                    <div className="sm:col-span-2">
                      <Label className="flex flex-wrap items-center gap-2">
                        <FileText className="h-3.5 w-3.5 text-emerald-700" />
                        Libretto di circolazione del veicolo di riferimento
                        <span className="text-[10px] text-slate-500 font-normal">(PDF, JPG, PNG, WEBP · max 10 MB)</span>
                      </Label>
                      {bersaniLibrettoFile ? (
                        <div className="mt-1 flex items-center gap-2 rounded-md border border-emerald-300 bg-white px-3 py-2" data-testid="pol-bersani-libretto-preview">
                          <FileText className="h-4 w-4 text-emerald-700 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{bersaniLibrettoFile.name}</div>
                            <div className="text-[11px] text-slate-500">{(bersaniLibrettoFile.size / (1024 * 1024)).toFixed(2)} MB · verrà allegato al preventivo al salvataggio</div>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="border-rose-300 text-rose-700 hover:bg-rose-50"
                            onClick={() => setBersaniLibrettoFile(null)}
                            data-testid="pol-bersani-libretto-remove"
                          >
                            <X className="h-3.5 w-3.5 mr-1" /> Rimuovi
                          </Button>
                        </div>
                      ) : (
                        <DropZone
                          testid="pol-bersani-libretto-dropzone"
                          multiple={false}
                          accept={["application/pdf", "image/jpeg", "image/png", "image/webp"]}
                          onFiles={(files) => {
                            const f = files?.[0];
                            if (!f) return;
                            if (f.size > 10 * 1024 * 1024) { toast.error("File troppo grande: max 10 MB"); return; }
                            setBersaniLibrettoFile(f);
                            toast.success(`Libretto pronto per l'allegato: ${f.name}`);
                          }}
                          className="mt-1 rounded-md border-2 border-dashed border-emerald-300 bg-white/60 hover:border-emerald-500 hover:bg-emerald-50/40 transition"
                        >
                          <label className="flex flex-col items-center gap-1 px-4 py-6 text-center cursor-pointer" data-testid="pol-bersani-libretto-label">
                            <Upload className="h-5 w-5 text-emerald-700" />
                            <span className="text-sm font-medium text-emerald-800">Trascina qui o clicca per allegare il libretto</span>
                            <span className="text-[11px] text-slate-500">PDF, JPG, PNG, WEBP · max 10 MB</span>
                            <input
                              type="file"
                              accept="application/pdf,image/jpeg,image/png,image/webp"
                              className="hidden"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                e.target.value = "";
                                if (!f) return;
                                const okType = /^(application\/pdf|image\/(jpeg|png|webp))$/.test(f.type) || /\.(pdf|jpe?g|png|webp)$/i.test(f.name || "");
                                if (!okType) { toast.error("Formato non supportato: solo PDF, JPG, PNG o WEBP"); return; }
                                if (f.size > 10 * 1024 * 1024) { toast.error("File troppo grande: max 10 MB"); return; }
                                setBersaniLibrettoFile(f);
                                toast.success(`Libretto pronto per l'allegato: ${f.name}`);
                              }}
                              data-testid="pol-bersani-libretto-input"
                            />
                          </label>
                        </DropZone>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {/* 🆕 Numero polizza + Broker/Agenzia + Compagnia: solo per polizze (non preventivi) */}
              {!isQuote && (
                <>
                  <div><Label>Numero polizza *</Label><Input data-testid="pol-number" value={policy.policy_number} onChange={(e) => setPolicy({ ...policy, policy_number: e.target.value })} /></div>
                  <div>
                    <Label className="text-amber-800 font-semibold">🤝 Broker / Agenzia</Label>
                    <Select
                      value={policy.broker_id || "none"}
                      onValueChange={(v) => {
                        const brokerId = v === "none" ? "" : v;
                        setPolicy((prev) => {
                          const newCid = brokerId && prev.company_id
                            ? (getCompaniesForBroker(brokerId).some((c) => c.id === prev.company_id) ? prev.company_id : "")
                            : prev.company_id;
                          return { ...prev, broker_id: brokerId, company_id: newCid };
                        });
                      }}
                    >
                      <SelectTrigger data-testid="pol-broker" className="bg-amber-50 border-amber-300 focus:ring-amber-400"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Nessuno</SelectItem>
                        {brokers.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {/* Badge conteggio compagnie collegate al broker (cliccabile → gestione link) */}
                    {policy.broker_id && (() => {
                      const cnt = getLinkedCompaniesCount(policy.broker_id);
                      const onOpen = () => navigate(`/brokers?expand=${policy.broker_id}&return_to_wizard=1`);
                      return cnt !== null ? (
                        <button
                          type="button"
                          onClick={onOpen}
                          className="mt-1 text-[10.5px] text-emerald-700 hover:text-emerald-900 hover:underline flex items-center gap-1"
                          title="Gestisci compagnie collegate a questo partner"
                          data-testid="pol-broker-links-count"
                        >
                          🔗 <span className="font-medium">{cnt}</span>&nbsp;{cnt === 1 ? "compagnia collegata" : "compagnie collegate"} · gestisci →
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={onOpen}
                          className="mt-1 text-[10.5px] text-amber-700 hover:text-amber-900 hover:underline flex items-center gap-1"
                          title="Configura le compagnie collegate a questo partner"
                          data-testid="pol-broker-links-count"
                        >
                          ⚠️ nessuna compagnia collegata · configura →
                        </button>
                      );
                    })()}
                  </div>
                  <div><Label className="text-amber-800 font-semibold">🏢 Compagnia *</Label>
                    <Select value={policy.company_id} onValueChange={(v) => setPolicy({ ...policy, company_id: v })}>
                      <SelectTrigger data-testid="pol-company" className="bg-amber-50 border-amber-300 focus:ring-amber-400"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                      <SelectContent>{getCompaniesForBroker(policy.broker_id).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </>
              )}
              {/* 🆕 Feb 2026 — Classe di merito + Frazionamento affiancati (stessa riga, uguale larghezza).
                  Il campo Classe di merito viene NASCOSTO quando la situazione assicurativa selezionata
                  attiva la sezione Bersani/RC Familiare (flag `triggers_bersani` nella lookup). */}
              <div className="col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {!(isQuote && !!insuranceStatusOptions.find((o) => o.code === policy.insurance_status)?.triggers_bersani) && (
                  <div>
                    <Label>Classe di merito</Label>
                    <Select
                      value={policy.merit_class || "_none"}
                      onValueChange={(v) => setPolicy({ ...policy, merit_class: v === "_none" ? "" : v })}
                    >
                      <SelectTrigger data-testid="pol-merit-class"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        <SelectItem value="_none">— Non specificata —</SelectItem>
                        {["1","2","3","4","5","6","7","8","9","10","11","12","13","14",
                          "CU1","CU2","CU3","CU4","CU5","CU6","CU7","CU8","CU9",
                          "CU10","CU11","CU12","CU13","CU14","CU15","CU16","CU17","CU18"]
                          .map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label>Frazionamento</Label>
                  <Select value={policy.frazionamento} onValueChange={(v) => setPolicy({ ...policy, frazionamento: v })}>
                    <SelectTrigger data-testid="pol-frazionamento"><SelectValue /></SelectTrigger>
                    <SelectContent>{FRAZ_OPTIONS.map((f) => <SelectItem key={f.v} value={f.v}>{f.l}</SelectItem>)}</SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-500 mt-0.5">Sincronizzato automaticamente con le offerte collegate.</p>
                </div>
              </div>
              {/* 🆕 Tipo di guida — da lookup-list "driving_type" (gestita in /admin/lookup-lists) */}
              <div>
                <Label>🪪 Tipo di guida</Label>
                <Select value={policy.driving_type || "_none"} onValueChange={(v) => setPolicy({ ...policy, driving_type: v === "_none" ? "" : v })}>
                  <SelectTrigger data-testid="pol-driving-type"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">— Non specificato —</SelectItem>
                    {drivingTypeOptions.map((d) => (
                      <SelectItem key={d.code} value={d.code}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isQuote && policy.type === "auto" && !policy.driving_type && (
                  <p className="text-[11px] text-red-600 font-medium mt-1" data-testid="pol-driving-type-warning">⚠ Campo obbligatorio per inviare il preventivo ai partner</p>
                )}
              </div>
              {/* 🆕 Broker / Agenzia: gestito sopra insieme a Compagnia */}
              <div><Label>Decorrenza *</Label><DatePicker data-testid="pol-start" value={policy.start_date} onChange={(e) => {
                const sd = e.target.value;
                const yrs = Number(policy.duration_years || 1);
                let ed = policy.end_date;
                if (sd && yrs > 0) {
                  const d = new Date(sd);
                  if (!isNaN(d)) {
                    d.setFullYear(d.getFullYear() + yrs);
                    ed = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                  }
                }
                setPolicy({ ...policy, start_date: sd, end_date: ed });
              }} /></div>
              <div><Label>Durata contratto (anni)</Label><Input
                type="number" min="0" step="0.5" data-testid="pol-duration-years"
                value={policy.duration_years ?? 1}
                onChange={(e) => {
                  const yrs = e.target.value === "" ? "" : Number(e.target.value);
                  let ed = policy.end_date;
                  if (policy.start_date && typeof yrs === "number" && yrs > 0) {
                    const d = new Date(policy.start_date);
                    if (!isNaN(d)) {
                      const wholeYears = Math.floor(yrs);
                      const extraMonths = Math.round((yrs - wholeYears) * 12);
                      d.setFullYear(d.getFullYear() + wholeYears);
                      d.setMonth(d.getMonth() + extraMonths);
                      ed = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                    }
                  }
                  setPolicy({ ...policy, duration_years: yrs, end_date: ed });
                }}
              /></div>
              <div><Label>Scadenza <span className="text-[10px] font-normal text-slate-500">(calcolata dalla durata)</span></Label><DatePicker data-testid="pol-end" value={policy.end_date} readOnly disabled className="bg-slate-100 cursor-not-allowed" /></div>
              <div><Label>{isQuote ? "Rif. premio cliente (€)" : "Premio (€)"}</Label><MoneyInput data-testid="pol-premium" value={policy.premium} onChange={(v) => setPolicy({ ...policy, premium: v })} /></div>

              {/* 🆕 Card "Durata contratto" + "Prossima rata" — calcolate da decorrenza/scadenza/frazionamento */}
              {(policy.start_date) && (
                <div className="col-span-1 sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1">
                  {(() => {
                    const fmt = (iso) => {
                      if (!iso) return "—";
                      const [y, m, d] = iso.split("-");
                      return `${d}/${m}/${y}`;
                    };
                    const durationLabel = (() => {
                      if (!policy.start_date || !policy.end_date) {
                        return policy.end_date ? "—" : "Imposta la scadenza per calcolare la durata";
                      }
                      const sd = new Date(policy.start_date);
                      const ed = new Date(policy.end_date);
                      if (isNaN(sd) || isNaN(ed) || ed < sd) return "—";
                      // 🆕 28/07/2026 — Scadenza = anniversario esatto della decorrenza (senza sottrazione di 1gg).
                      // Il numero di giorni è la differenza tra le due date (365 in anno normale, 366 in bisestile).
                      const days = Math.round((ed - sd) / (1000 * 60 * 60 * 24));
                      // 🆕 usa duration_years (esplicita) se disponibile per il label "anni"
                      const yrs = Number(policy.duration_years);
                      if (yrs && yrs > 0 && Number.isInteger(yrs)) {
                        return `${yrs === 1 ? "Annuale" : `${yrs} anni`} (${days} giorni)`;
                      }
                      // fallback: calcola mesi
                      let months = (ed.getFullYear() - sd.getFullYear()) * 12 + (ed.getMonth() - sd.getMonth());
                      if (ed.getDate() < sd.getDate()) months -= 1;
                      if (months >= 12 && months % 12 === 0) {
                        const y = months / 12;
                        return `${y === 1 ? "Annuale" : `${y} anni`} (${days} giorni)`;
                      }
                      if (months >= 1) return `${months} mes${months === 1 ? "e" : "i"} (${days} giorni)`;
                      return `${days} giorn${days === 1 ? "o" : "i"}`;
                    })();
                    const FRAZ_MONTHS = { annuale: 12, semestrale: 6, quadrimestrale: 4, trimestrale: 3, mensile: 1 };
                    const fr = policy.frazionamento || "annuale";
                    const step = FRAZ_MONTHS[fr] || 12;
                    const nextInstallment = (() => {
                      if (!policy.start_date) return null;
                      const d = new Date(policy.start_date);
                      if (isNaN(d)) return null;
                      d.setMonth(d.getMonth() + step);
                      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                      return iso;
                    })();
                    const frLabel = { annuale: "Annuale", semestrale: "Semestrale", quadrimestrale: "Quadrimestrale", trimestrale: "Trimestrale", mensile: "Mensile" }[fr] || fr;
                    return (
                      <>
                        <div data-testid="pol-duration-card" className="rounded-lg border-2 border-indigo-200 bg-indigo-50 p-3">
                          <div className="text-[11px] uppercase tracking-wide font-semibold text-indigo-700 mb-1">📋 Durata contratto</div>
                          <div className="text-lg font-bold text-indigo-900">{durationLabel}</div>
                          <div className="text-[11px] text-indigo-700/80 mt-1">Calcolata da Decorrenza → Scadenza</div>
                        </div>
                        <div data-testid="pol-installment-card" className="rounded-lg border-2 border-emerald-200 bg-emerald-50 p-3">
                          <div className="text-[11px] uppercase tracking-wide font-semibold text-emerald-700 mb-1">💶 Prossima rata ({frLabel})</div>
                          <div className="text-sm text-emerald-900 flex items-center gap-2 flex-wrap">
                            <span><span className="text-emerald-700/80">Decorrenza:</span> <strong>{fmt(policy.start_date)}</strong></span>
                            <span className="text-emerald-400">→</span>
                            <span><span className="text-emerald-700/80">Scadenza prossima rata:</span> <strong>{fmt(nextInstallment)}</strong></span>
                          </div>
                          <div className="text-[11px] text-emerald-700/80 mt-1">Frazionamento: +{step} mes{step === 1 ? "e" : "i"} dalla decorrenza</div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
              {/* Dati veicolo: visibili per polizze auto/moto/veicolo */}
              {/* 🆕 Feb 2026 — Dati personali del contraente: Persona Fisica vs Azienda */}
              <div className="col-span-2 mt-2 pt-3 border-t border-slate-200">
                {isCompanyClient(selectedClient) ? (
                  <>
                    <p className="text-xs uppercase tracking-wide text-purple-700 font-bold mb-2 flex items-center gap-1.5" data-testid="wizard-contraente-company-header">
                      <Building2 className="h-3.5 w-3.5" /> Dati azienda contraente
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div className="sm:col-span-2">
                        <Label className="text-xs text-slate-500">🏢 Ragione Sociale</Label>
                        <p className="font-semibold text-slate-800 mt-0.5" data-testid="wizard-contraente-ragione-sociale">
                          {selectedClient?.ragione_sociale?.trim() || <span className="text-slate-400 italic">Non impostata sul cliente</span>}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">🏷 Partita IVA</Label>
                        <p className="font-mono font-semibold text-slate-800 mt-0.5" data-testid="wizard-contraente-piva">
                          {selectedClient?.partita_iva || <span className="text-slate-400 italic font-sans">—</span>}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">🏷 Codice Fiscale (se diverso)</Label>
                        <p className="font-mono font-semibold text-slate-800 mt-0.5">
                          {selectedClient?.fiscal_code && selectedClient?.fiscal_code !== selectedClient?.partita_iva
                            ? selectedClient.fiscal_code
                            : <span className="text-slate-400 italic font-sans">—</span>}
                        </p>
                      </div>
                      {selectedClient?.pec_email && (
                        <div>
                          <Label className="text-xs text-slate-500">✉ PEC</Label>
                          <p className="font-medium text-slate-800 mt-0.5">{selectedClient.pec_email}</p>
                        </div>
                      )}
                      {selectedClient?.sdi_code && (
                        <div>
                          <Label className="text-xs text-slate-500">🏷 Codice SDI</Label>
                          <p className="font-mono font-medium text-slate-800 mt-0.5">{selectedClient.sdi_code}</p>
                        </div>
                      )}
                    </div>
                    {/* Legale Rappresentante */}
                    <div className="mt-4 pt-3 border-t border-slate-200" data-testid="wizard-contraente-legal-rep">
                      <p className="text-xs uppercase tracking-wide text-purple-700 font-bold mb-2 flex items-center gap-1.5">
                        <User className="h-3.5 w-3.5" /> Legale Rappresentante
                      </p>
                      {(selectedClient?.legale_rappresentante_name || selectedClient?.legale_rappresentante_surname || selectedClient?.legale_rappresentante_fiscal_code) ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                          <div>
                            <Label className="text-xs text-slate-500">Cognome e Nome</Label>
                            <p className="font-medium text-slate-800 mt-0.5">
                              {`${selectedClient.legale_rappresentante_surname || ""} ${selectedClient.legale_rappresentante_name || ""}`.trim() || "—"}
                            </p>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Codice Fiscale</Label>
                            <p className="font-mono font-medium text-slate-800 mt-0.5">
                              {selectedClient.legale_rappresentante_fiscal_code || "—"}
                            </p>
                          </div>
                          {selectedClient.legale_rappresentante_role && (
                            <div>
                              <Label className="text-xs text-slate-500">Ruolo</Label>
                              <p className="font-medium text-slate-800 mt-0.5">{selectedClient.legale_rappresentante_role}</p>
                            </div>
                          )}
                          {selectedClient.legale_rappresentante_birth_date && (
                            <div>
                              <Label className="text-xs text-slate-500">Nato il</Label>
                              <p className="font-medium text-slate-800 mt-0.5">
                                {formatDate(selectedClient.legale_rappresentante_birth_date)}
                                {selectedClient.legale_rappresentante_birth_place && (
                                  <span className="text-slate-500"> a {selectedClient.legale_rappresentante_birth_place}</span>
                                )}
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-amber-700">⚠️ Nessun Legale Rappresentante impostato per questa azienda. Puoi completarlo dalla scheda cliente.</p>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-500 mt-3">
                      ℹ Per aziende non vengono richiesti Professione e Stato Civile. Modifica ragione sociale, P.IVA e Legale Rappresentante dalla scheda cliente.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-xs uppercase tracking-wide text-blue-700 font-bold mb-2 flex items-center gap-1.5" data-testid="wizard-contraente-private-header">
                      <User className="h-3.5 w-3.5" /> Dati personali contraente
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label>💼 Professione</Label>
                        <Select
                          value={policy.profession || "_none"}
                          onValueChange={(v) => setPolicy({ ...policy, profession: v === "_none" ? "" : v })}
                        >
                          <SelectTrigger data-testid="pol-profession"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">— Non specificato —</SelectItem>
                            {professionOptions.map((p) => (
                              <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-[10px] text-slate-500 mt-1">Voci gestibili in <em>Liste preventivo → Professione</em>.</p>
                      </div>
                      <div>
                        <Label>💍 Stato civile</Label>
                        <Select
                          value={policy.marital_status || "_none"}
                          onValueChange={(v) => setPolicy({ ...policy, marital_status: v === "_none" ? "" : v })}
                        >
                          <SelectTrigger data-testid="pol-marital-status"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">— Non specificato —</SelectItem>
                            {maritalStatusOptions.map((m) => (
                              <SelectItem key={m.code} value={m.code}>{m.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-[10px] text-slate-500 mt-1">Voci gestibili in <em>Liste preventivo → Stato civile</em>.</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
              {["auto", "moto", "veicolo", "autocarro", "nautica"].includes(policy.type) && (
                <div className="col-span-2 mt-2 pt-3 border-t border-slate-200">
                  <p className="text-xs uppercase tracking-wide text-blue-700 font-bold mb-2 flex items-center gap-1.5">
                    <Car className="h-3.5 w-3.5" /> Dati veicolo
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div className="col-span-1">
                      <Label>Targa *</Label>
                      <PlateInput
                        testid="wizard-vehicle-plate"
                        value={policy.vehicle_plate}
                        onChange={(v) => setPolicy({ ...policy, vehicle_plate: v })}
                        overrideFlag={!!policy.vehicle_plate_override}
                        onOverrideChange={(v) => setPolicy({ ...policy, vehicle_plate_override: v })}
                      />
                    </div>
                    <div>
                      <Label>Marca</Label>
                      <VehicleAutocomplete
                        testid="wizard-vehicle-brand"
                        value={policy.vehicle_brand}
                        onChange={(v) => {
                          // Auto-classifica polizza come "moto" se il brand è di una moto e il tipo attuale è "auto"
                          const patch = { vehicle_brand: v };
                          if (isMotoBrand(v) && policy.type === "auto") {
                            patch.type = "moto";
                            toast.info(`🏍️ Brand ${v} riconosciuto come moto: tipo polizza auto-impostato a "Moto"`, { duration: 4500 });
                          }
                          setPolicy({ ...policy, ...patch });
                        }}
                        suggestions={wizardBrandSuggestions}
                        placeholder="es. FIAT"
                      />
                    </div>
                    <div>
                      <Label>Modello</Label>
                      <VehicleAutocomplete
                        testid="wizard-vehicle-model"
                        value={policy.vehicle_model}
                        onChange={(v) => setPolicy({ ...policy, vehicle_model: v })}
                        suggestions={wizardModelSuggestions}
                        placeholder={policy.vehicle_brand ? "es. Panda" : "Inserisci prima la marca"}
                      />
                    </div>
                    <div>
                      <Label>1ª immatricolazione</Label>
                      <DatePicker data-testid="wizard-vehicle-first-reg"
                        value={policy.vehicle_first_registration}
                        onChange={(e) => setPolicy({ ...policy, vehicle_first_registration: e.target.value })} />
                    </div>
                    <div>
                      <Label>📅 Anno immatricolazione</Label>
                      <Input
                        type="number"
                        min="1900"
                        max="2100"
                        data-testid="wizard-vehicle-year"
                        value={policy.vehicle_year}
                        onChange={(e) => setPolicy({ ...policy, vehicle_year: e.target.value })}
                        placeholder="es. 2018"
                      />
                    </div>
                    <div>
                      <Label>🔑 Data acquisto veicolo</Label>
                      <DatePicker data-testid="wizard-vehicle-purchase-date"
                        value={policy.vehicle_purchase_date}
                        onChange={(e) => setPolicy({ ...policy, vehicle_purchase_date: e.target.value })} />
                      <p className="text-[10px] text-slate-500 mt-0.5">Data del passaggio di proprietà (dal libretto)</p>
                    </div>
                    <div>
                      <Label>Alimentazione</Label>
                      <Select
                        value={policy.vehicle_fuel || "_none"}
                        onValueChange={(v) => setPolicy({ ...policy, vehicle_fuel: v === "_none" ? "" : v })}
                      >
                        <SelectTrigger data-testid="wizard-vehicle-fuel"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">— Non specificato —</SelectItem>
                          {fuelOptions.map((f) => (
                            <SelectItem key={f.code} value={f.code}>{f.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-slate-500 mt-1">Voci gestibili in <em>Liste preventivo → Alimentazione</em>.</p>
                    </div>
                    <div>
                      <Label>⛽ Alimentazione agg.</Label>
                      <Select
                        value={policy.vehicle_additional_fuel || "_none"}
                        onValueChange={(v) => setPolicy({ ...policy, vehicle_additional_fuel: v === "_none" ? "" : v })}
                      >
                        <SelectTrigger data-testid="wizard-vehicle-additional-fuel"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">— Non specificato —</SelectItem>
                          {additionalFuelOptions.map((f) => (
                            <SelectItem key={f.code} value={f.code}>{f.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[10px] text-slate-500 mt-1">Voci gestibili in <em>Liste preventivo → Alimentazione aggiuntiva</em>.</p>
                    </div>
                    <div>
                      <Label>kW / Cilindrata</Label>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          data-testid="wizard-vehicle-kw"
                          value={policy.vehicle_kw}
                          onChange={(e) => setPolicy({ ...policy, vehicle_kw: e.target.value })}
                          placeholder="kW"
                        />
                        <Input
                          type="number"
                          data-testid="wizard-vehicle-displacement"
                          value={policy.vehicle_displacement}
                          onChange={(e) => setPolicy({ ...policy, vehicle_displacement: e.target.value })}
                          placeholder="cc"
                        />
                      </div>
                    </div>
                    {/* 🆕 Task 1: campi nativi (telaio, data revisione, scadenza revisione) */}
                    <div className="sm:col-span-2">
                      <Label>🔧 Numero telaio (VIN)</Label>
                      <Input
                        data-testid="wizard-vehicle-chassis"
                        value={policy.chassis_number || ""}
                        onChange={(e) => setPolicy({ ...policy, chassis_number: e.target.value.toUpperCase() })}
                        placeholder="17 caratteri alfanumerici (es. ZFA31200000123456)"
                        className="font-mono uppercase"
                        maxLength={17}
                      />
                    </div>
                    <div>
                      <Label>📅 Data ultima revisione</Label>
                      <DatePicker data-testid="wizard-vehicle-last-review"
                        value={policy.last_review_date || ""}
                        onChange={(e) => setPolicy({ ...policy, last_review_date: e.target.value })} />
                    </div>
                    <div>
                      <Label>⏰ Scadenza prossima revisione</Label>
                      <DatePicker data-testid="wizard-vehicle-next-review"
                        value={policy.next_review_date || ""}
                        onChange={(e) => setPolicy({ ...policy, next_review_date: e.target.value })} />
                    </div>
                  </div>
                  {policy.vehicle_plate && (
                    <p className="text-[11px] text-emerald-700 mt-2">
                      ✓ Targa <span className="font-mono font-bold">{policy.vehicle_plate}</span> verrà salvata sulla polizza.
                    </p>
                  )}
                  {/* 🆕 Feb 2026 — Editor veicoli aggiuntivi per polizze multi-veicolo */}
                  <div className="mt-3 pt-3 border-t border-slate-200 space-y-3">
                    {/* Classificazione polizza multi-veicolo (solo con 2+ veicoli) */}
                    {(policy.vehicles_extra?.length || 0) >= 1 && (
                      <div className="p-2 rounded-md border border-slate-200 bg-slate-50/40 flex items-center gap-3 flex-wrap" data-testid="wizard-multi-kind-selector">
                        <span className="text-xs font-semibold text-slate-700">Tipo polizza multi-veicolo:</span>
                        <label className="inline-flex items-center gap-1.5 cursor-pointer">
                          <input type="radio" name="w_multi_vehicle_kind" value="storica_cumulativa"
                            checked={policy.multi_vehicle_kind === "storica_cumulativa"}
                            onChange={() => setPolicy((p) => ({ ...p, multi_vehicle_kind: "storica_cumulativa" }))}
                            data-testid="wizard-multi-kind-storica"
                          />
                          <span className="text-xs">🕰️ <strong>Storica cumulativa</strong></span>
                        </label>
                        <label className="inline-flex items-center gap-1.5 cursor-pointer">
                          <input type="radio" name="w_multi_vehicle_kind" value="flotta"
                            checked={policy.multi_vehicle_kind === "flotta"}
                            onChange={() => setPolicy((p) => ({ ...p, multi_vehicle_kind: "flotta" }))}
                            data-testid="wizard-multi-kind-flotta"
                          />
                          <span className="text-xs">🚛 <strong>Flotta</strong></span>
                        </label>
                        {policy.multi_vehicle_kind && (
                          <button type="button"
                            onClick={() => setPolicy((p) => ({ ...p, multi_vehicle_kind: null }))}
                            className="text-[11px] text-slate-500 hover:text-rose-600 hover:underline ml-auto"
                          >✕ Rimuovi</button>
                        )}
                      </div>
                    )}
                    <VehiclesEditor
                      extraVehicles={policy.vehicles_extra || []}
                      primaryPlate={policy.vehicle_plate || ""}
                      onChange={(next) => setPolicy((prev) => ({ ...prev, vehicles_extra: next }))}
                    />
                  </div>
                </div>
              )}

              {/* 🆕 30/07/2026 — Dati Natante (visibile solo per polizze type="natante") */}
              {policy.type === "natante" && (
                <div className="col-span-2 mt-2 pt-3 border-t border-slate-200" data-testid="natante-section">
                  <p className="text-xs uppercase tracking-wide text-cyan-700 font-bold mb-2 flex items-center gap-1.5">
                    ⛵ Dati imbarcazione
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div className="col-span-1 sm:col-span-2">
                      <Label>Marca e modello imbarcazione</Label>
                      <Input data-testid="boat-maker-model" value={policy.boat_maker_model || ""}
                        onChange={(e) => setPolicy({ ...policy, boat_maker_model: e.target.value })} />
                    </div>
                    <div>
                      <Label>Materiale scafo</Label>
                      <Select value={policy.boat_hull_material || "_none"}
                        onValueChange={(v) => setPolicy({ ...policy, boat_hull_material: v === "_none" ? "" : v })}>
                        <SelectTrigger data-testid="boat-hull-material"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">— Non specificato —</SelectItem>
                          {BOAT_HULL_MATERIAL_OPTIONS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Lunghezza (m)</Label>
                      <Input type="number" step="0.01" data-testid="boat-length-m" value={policy.boat_length_m || ""}
                        onChange={(e) => setPolicy({ ...policy, boat_length_m: e.target.value })} />
                    </div>
                    <div>
                      <Label>Larghezza / Baglio (m)</Label>
                      <Input type="number" step="0.01" data-testid="boat-beam-m" value={policy.boat_beam_m || ""}
                        onChange={(e) => setPolicy({ ...policy, boat_beam_m: e.target.value })} />
                    </div>
                    <div>
                      <Label>Anno di costruzione</Label>
                      <Input type="number" min="1900" max="2100" data-testid="boat-build-year" value={policy.boat_build_year || ""}
                        onChange={(e) => setPolicy({ ...policy, boat_build_year: e.target.value })} />
                    </div>
                    <div>
                      <Label>Matricola / Immatricolazione</Label>
                      <Input data-testid="boat-registration-number" value={policy.boat_registration_number || ""}
                        onChange={(e) => setPolicy({ ...policy, boat_registration_number: e.target.value })} />
                    </div>
                  </div>

                  <p className="text-xs uppercase tracking-wide text-cyan-700 font-bold mb-2 mt-4 flex items-center gap-1.5">
                    ⚙️ Dati motore
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div>
                      <Label>Costruttore motore</Label>
                      <Input data-testid="boat-engine-maker" value={policy.boat_engine_maker || ""}
                        onChange={(e) => setPolicy({ ...policy, boat_engine_maker: e.target.value })} />
                    </div>
                    <div>
                      <Label>Modello motore</Label>
                      <Input data-testid="boat-engine-model" value={policy.boat_engine_model || ""}
                        onChange={(e) => setPolicy({ ...policy, boat_engine_model: e.target.value })} />
                    </div>
                    <div>
                      <Label>Numero di matricola motore</Label>
                      <Input data-testid="boat-engine-serial" value={policy.boat_engine_serial || ""}
                        onChange={(e) => setPolicy({ ...policy, boat_engine_serial: e.target.value })} />
                    </div>
                    <div>
                      <Label>Tipo di installazione</Label>
                      <Select value={policy.boat_engine_install || "_none"}
                        onValueChange={(v) => setPolicy({ ...policy, boat_engine_install: v === "_none" ? "" : v })}>
                        <SelectTrigger data-testid="boat-engine-install"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">— Non specificato —</SelectItem>
                          {BOAT_ENGINE_INSTALL_OPTIONS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Combustibile</Label>
                      <Select value={policy.boat_engine_fuel || "_none"}
                        onValueChange={(v) => setPolicy({ ...policy, boat_engine_fuel: v === "_none" ? "" : v })}>
                        <SelectTrigger data-testid="boat-engine-fuel"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">— Non specificato —</SelectItem>
                          {BOAT_ENGINE_FUEL_OPTIONS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Potenza dichiarata (Kw)</Label>
                      <Input type="number" step="0.01" data-testid="boat-engine-kw" value={policy.boat_engine_kw || ""}
                        onChange={(e) => setPolicy({ ...policy, boat_engine_kw: e.target.value })} />
                    </div>
                    <div>
                      <Label>Massa motore (kg)</Label>
                      <Input type="number" step="0.1" data-testid="boat-engine-mass-kg" value={policy.boat_engine_mass_kg || ""}
                        onChange={(e) => setPolicy({ ...policy, boat_engine_mass_kg: e.target.value })} />
                    </div>
                    <div>
                      <Label>N° dichiarazione</Label>
                      <Input data-testid="boat-declaration-number" value={policy.boat_declaration_number || ""}
                        onChange={(e) => setPolicy({ ...policy, boat_declaration_number: e.target.value })} />
                    </div>
                    <div>
                      <Label>Data dichiarazione</Label>
                      <Input type="date" data-testid="boat-declaration-date" value={policy.boat_declaration_date || ""}
                        onChange={(e) => setPolicy({ ...policy, boat_declaration_date: e.target.value })} />
                    </div>
                  </div>

                  {/* 🆕 30/07/2026 — Motori aggiuntivi (catamarani/motori gemelli) */}
                  <div className="mt-4 pt-3 border-t border-cyan-100">
                    <p className="text-xs uppercase tracking-wide text-cyan-700 font-bold mb-2 flex items-center gap-1.5">
                      🔧 Motori aggiuntivi
                    </p>
                    <BoatEnginesEditor
                      extraEngines={policy.boat_engines_extra || []}
                      onChange={(next) => setPolicy((prev) => ({ ...prev, boat_engines_extra: next }))}
                    />
                  </div>
                </div>
              )}

              {/* 🆕 Task 3: GARANZIE RICHIESTE (prima del salvataggio) */}
              <div className="col-span-2 mt-2 pt-3 border-t border-slate-200">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <p className="text-xs uppercase tracking-wide text-violet-700 font-bold flex items-center gap-1.5">
                    🛡️ Garanzie richieste
                    <span className="text-[10px] font-normal text-slate-500 normal-case tracking-normal">(elenco delle coperture da quotare/richiedere)</span>
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* 🆕 Feb 2026 — Precompila da garanzie compagnia */}
                    {policy.company_id && companyGuarantees.length > 0 && (
                      <Button
                        type="button"
                        data-testid="wizard-open-company-guarantees-picker"
                        size="sm"
                        variant="outline"
                        className="border-indigo-500 text-indigo-700 hover:bg-indigo-50"
                        onClick={() => {
                          setGuaranteesPickerSelected(new Set());
                          setGuaranteesPickerOpen(true);
                        }}
                      >
                        🛡️ Da garanzie compagnia ({companyGuarantees.length})
                      </Button>
                    )}
                    {/* 🆕 15/07/2026 — Da catalogo prodotti */}
                    {policy.company_id && companyProducts.length > 0 && (
                      <Button
                        type="button"
                        data-testid="wizard-open-products-picker"
                        size="sm"
                        variant="outline"
                        className="border-purple-500 text-purple-700 hover:bg-purple-50"
                        onClick={() => {
                          setProductsPickerSelected(new Set());
                          setProductsPickerOpen(true);
                        }}
                      >
                        📦 Da catalogo prodotti ({companyProducts.length})
                      </Button>
                    )}
                    <Button
                      type="button"
                      data-testid="wizard-add-requested-coverage"
                      size="sm"
                      variant="outline"
                      className="border-violet-500 text-violet-700 hover:bg-violet-50"
                      onClick={() => setPolicy({ ...policy, requested_coverages: [...(policy.requested_coverages || []), { name: "", sum_insured: "", deductible: "", notes: "" }] })}
                    >
                      + Aggiungi garanzia
                    </Button>
                  </div>
                </div>
                {(policy.requested_coverages || []).length === 0 ? (
                  <p className="text-[11px] text-slate-500 italic">
                    Nessuna garanzia richiesta. Aggiungile per documentarle e (opzionalmente) inviarle a un partner per quotazione.
                  </p>
                ) : (
                  <div className="space-y-2" data-testid="wizard-requested-coverages-list">
                    {(policy.requested_coverages || []).map((cov, idx) => (
                      <div key={`reqcov-${idx}`} className="bg-violet-50/40 border border-violet-200 rounded-md p-2 space-y-1.5">
                        <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-start">
                        <div className="sm:col-span-4">
                          <Label className="text-[10px] uppercase tracking-wide text-violet-700">Garanzia</Label>
                          {(() => {
                            // Verifica se il valore corrente è tra le opzioni della lookup-list
                            const matchedOpt = coverageOptions.find((o) => o.label === cov.name || o.code === cov.name);
                            const isCustom = !!cov.name && !matchedOpt;
                            const selectVal = isCustom ? "__custom__" : (matchedOpt ? matchedOpt.code : "");
                            return (
                              <div className="flex flex-col gap-1">
                                <Select
                                  value={selectVal || "__placeholder__"}
                                  onValueChange={(v) => {
                                    const arr = [...policy.requested_coverages];
                                    if (v === "__custom__") {
                                      arr[idx] = { ...arr[idx], name: arr[idx].name || "" };
                                    } else if (v === "__placeholder__") {
                                      arr[idx] = { ...arr[idx], name: "" };
                                    } else {
                                      const opt = coverageOptions.find((o) => o.code === v);
                                      // 🆕 Auto-fill default_sum_insured / default_deductible se i campi sono vuoti
                                      arr[idx] = {
                                        ...arr[idx],
                                        name: opt ? opt.label : v,
                                        sum_insured: arr[idx].sum_insured || (opt && opt.default_sum_insured != null ? String(opt.default_sum_insured) : ""),
                                        deductible: arr[idx].deductible || (opt && opt.default_deductible ? opt.default_deductible : ""),
                                      };
                                    }
                                    setPolicy({ ...policy, requested_coverages: arr });
                                  }}
                                >
                                  <SelectTrigger data-testid={`reqcov-select-${idx}`} className="text-sm h-9">
                                    <SelectValue placeholder="Seleziona garanzia…" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__placeholder__">— Seleziona —</SelectItem>
                                    {coverageOptions.map((o) => (
                                      <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>
                                    ))}
                                    <SelectItem value="__custom__">✏️ Altra (testo libero)</SelectItem>
                                  </SelectContent>
                                </Select>
                                {isCustom && (
                                  <Input
                                    data-testid={`reqcov-name-${idx}`}
                                    value={cov.name || ""}
                                    onChange={(e) => {
                                      const arr = [...policy.requested_coverages];
                                      arr[idx] = { ...arr[idx], name: e.target.value };
                                      setPolicy({ ...policy, requested_coverages: arr });
                                    }}
                                    placeholder="Descrivi la garanzia…"
                                    className="text-sm h-9"
                                  />
                                )}
                              </div>
                            );
                          })()}
                        </div>
                        <div className="sm:col-span-3">
                          <Label className="text-[10px] uppercase tracking-wide text-violet-700">Massimale (€)</Label>
                          <Input
                            data-testid={`reqcov-sum-${idx}`}
                            type="number"
                            value={cov.sum_insured || ""}
                            onChange={(e) => {
                              const arr = [...policy.requested_coverages];
                              arr[idx] = { ...arr[idx], sum_insured: e.target.value };
                              setPolicy({ ...policy, requested_coverages: arr });
                            }}
                            placeholder="es. 1000000"
                            className="text-sm"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-[10px] uppercase tracking-wide text-violet-700">Franchigia</Label>
                          <Input
                            data-testid={`reqcov-deductible-${idx}`}
                            value={cov.deductible || ""}
                            onChange={(e) => {
                              const arr = [...policy.requested_coverages];
                              arr[idx] = { ...arr[idx], deductible: e.target.value };
                              setPolicy({ ...policy, requested_coverages: arr });
                            }}
                            placeholder="es. 500€"
                            className="text-sm"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-[10px] uppercase tracking-wide text-violet-700">Note</Label>
                          <Input
                            data-testid={`reqcov-notes-${idx}`}
                            value={cov.notes || ""}
                            onChange={(e) => {
                              const arr = [...policy.requested_coverages];
                              arr[idx] = { ...arr[idx], notes: e.target.value };
                              setPolicy({ ...policy, requested_coverages: arr });
                            }}
                            placeholder="opzionale"
                            className="text-sm"
                          />
                        </div>
                        <div className="sm:col-span-1 flex sm:justify-end sm:items-end">
                          <button
                            type="button"
                            data-testid={`reqcov-remove-${idx}`}
                            onClick={() => {
                              const arr = [...policy.requested_coverages];
                              arr.splice(idx, 1);
                              setPolicy({ ...policy, requested_coverages: arr });
                            }}
                            className="text-rose-600 hover:bg-rose-50 rounded p-1.5 mt-5"
                            title="Rimuovi"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                        </div>
                        {/* 🆕 Feb 2026 — Badge info per garanzie precompilate da compagnia */}
                        {cov._source_guarantee_id && (
                          <div
                            data-testid={`reqcov-guarantee-badge-${idx}`}
                            className="flex items-center gap-2 flex-wrap text-[10.5px] text-indigo-800 bg-indigo-50 border border-indigo-200 rounded px-2 py-1"
                            title={`Da garanzia compagnia · Imposte ${cov._imposte_percent ?? "—"}% · SSN ${cov._ssn_percent ?? "—"}% · Acquisto ${cov._buy_price != null ? formatCurrency(cov._buy_price) : "—"} · Vendita ${cov._sell_price != null ? formatCurrency(cov._sell_price) : "—"}`}
                          >
                            <span className="font-semibold flex items-center gap-1">🛡️ Da garanzie compagnia</span>
                            {cov._imposte_percent != null && <span>Imposte <strong>{cov._imposte_percent}%</strong></span>}
                            {cov._ssn_percent != null && <span>· SSN <strong>{cov._ssn_percent}%</strong></span>}
                            {cov._buy_price != null && <span>· Acq. <strong>{formatCurrency(cov._buy_price)}</strong></span>}
                            {cov._sell_price != null && <span>· Ven. <strong className="text-emerald-700">{formatCurrency(cov._sell_price)}</strong></span>}
                          </div>
                        )}
                      </div>
                    ))}
                    <p className="text-[10px] text-violet-700/80 italic mt-1">
                      ℹ Queste garanzie verranno {isQuote ? "incluse nella richiesta cliente" : "pre-popolate nelle coperture della polizza"} e potranno essere inviate a un partner/broker dalla schermata di riepilogo.
                    </p>
                  </div>
                )}
              </div>

              <div className="col-span-2"><Label>Note</Label>
                <Textarea rows={2} data-testid="pol-notes" value={policy.notes} onChange={(e) => setPolicy({ ...policy, notes: e.target.value })} />
              </div>
            </div>
          </div>
        )}

        {/* STEP 3 — Policy attachments */}
        {step === 3 && (
          <DropZone
            testid="dropzone-policy-att"
            onFiles={async (files) => {
              const processed = await askMultiFileAction(files);
              for (const f of processed) {
                const renamed = askRenameBeforeUpload(f);
                await uploadPolicyAtt(renamed);
              }
            }}
            accept={["application/pdf", "image/jpeg", "image/png", "image/webp"]}
            multiple
            disabled={uploadingAtt}
            className="space-y-4 p-2 rounded-md"
          >
            <h2 className="font-display text-xl font-semibold flex items-center gap-2"><Receipt className="h-5 w-5 text-blue-600" /> Allegati della polizza <span className="text-xs text-slate-400 font-normal">(opzionale)</span></h2>
            <p className="text-xs text-slate-500">Carica copia polizza firmata, condizioni generali, ricevute, ecc. (PDF, JPG, PNG, WEBP · max 10 MB ciascuno).</p>
            <p className="text-xs text-blue-700">💡 Suggerimento: puoi anche <strong>trascinare i file</strong> direttamente in questa zona.</p>
            <Button
              data-testid="upload-policy-att"
              onClick={() => policyAttInputRef.current?.click()}
              disabled={uploadingAtt}
            >
              {uploadingAtt ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              Carica file (anche multipli)
            </Button>
            <ScanButton
              testid="scan-wizard-policy-att"
              label="Importa da scanner"
              variant="outline"
              disabled={uploadingAtt}
              onFile={(f) => handlePolicyAttInput({ target: { files: [f], value: "" } })}
            />
            <CameraButton
              testid="cam-scan-wizard-policy-att"
              label="📷 Foto da fotocamera"
              variant="outline"
              disabled={uploadingAtt}
              onFile={(f) => handlePolicyAttInput({ target: { files: [f], value: "" } })}
            />            <input ref={policyAttInputRef} type="file" multiple accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden" onChange={handlePolicyAttInput} />
            <div className="space-y-1.5" data-testid="policy-att-list">
              {policyAttachments.length === 0 ? (
                <p className="text-xs text-slate-400 italic">Nessun allegato caricato ancora. Trascina i file qui o usa il pulsante sopra.</p>
              ) : policyAttachments.map((a) => (
                <div key={a.id} className="bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm flex items-center gap-3">
                  <FileText className="h-4 w-4 text-slate-400 shrink-0" />
                  <span className="flex-1 truncate" data-testid={`policy-att-name-${a.id}`}>{a.original_filename || a.filename || "allegato"}</span>
                  <span className="text-xs text-slate-500 shrink-0">{Math.round((a.size || a.size_bytes || 0) / 1024)} KB</span>
                  <button
                    type="button"
                    data-testid={`policy-att-rename-${a.id}`}
                    onClick={() => promptRenameAttachment(a, "policy")}
                    className="p-1 rounded hover:bg-slate-200 text-slate-500 hover:text-blue-600 shrink-0"
                    title="Rinomina file"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </DropZone>
        )}

        {/* STEP 4 — Summary */}
        {step === 4 && (
          <div className="space-y-4" data-testid="wizard-summary">
            <h2 className="font-display text-xl font-semibold flex items-center gap-2"><Sparkles className="h-5 w-5 text-emerald-600" /> Riepilogo</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="bg-slate-50 border border-slate-200 rounded p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-1 flex items-center gap-1.5">
                  {isCompanyClient(selectedClient) ? <Building2 className="h-3 w-3" /> : <User className="h-3 w-3" />}
                  Cliente · {isCompanyClient(selectedClient) ? "Azienda" : "Persona fisica"}
                </p>
                <p className="font-semibold" data-testid="summary-client-name">{clientDisplayName(selectedClient) || "—"}</p>
                {isCompanyClient(selectedClient) ? (
                  <>
                    <p className="text-xs text-slate-500" data-testid="summary-client-piva">P.IVA: {selectedClient?.partita_iva || "—"}</p>
                    {(selectedClient?.legale_rappresentante_surname || selectedClient?.legale_rappresentante_name) && (
                      <p className="text-xs text-slate-500" data-testid="summary-client-legal-rep">
                        Legale Rap.: {`${selectedClient.legale_rappresentante_surname || ""} ${selectedClient.legale_rappresentante_name || ""}`.trim()}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-slate-500">{selectedClient?.fiscal_code || "—"}</p>
                )}
                <p className="text-xs text-slate-500 mt-1">{clientDocs.length} document{clientDocs.length === 1 ? "o" : "i"} personal{clientDocs.length === 1 ? "e" : "i"} caricat{clientDocs.length === 1 ? "o" : "i"}</p>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">{targetNameCap}</p>
                {isQuote ? (
                  <>
                    <p className="font-semibold">{policyTypes.find((t) => t.v === policy.type)?.l || policy.type}</p>
                    <p className="text-xs text-slate-500">Rif. premio: {formatCurrency(policy.premium)}</p>
                  </>
                ) : (
                  <>
                    <p className="font-semibold">n° {policy.policy_number} · {policyTypes.find((t) => t.v === policy.type)?.l || policy.type}</p>
                    <p className="text-xs text-slate-500">{companies.find((c) => c.id === policy.company_id)?.name} · {formatCurrency(policy.premium)}</p>
                  </>
                )}
                <p className="text-xs text-slate-500 mt-1">{policyAttachments.length} allegat{policyAttachments.length === 1 ? "o" : "i"} caricat{policyAttachments.length === 1 ? "o" : "i"}</p>
              </div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-4 text-emerald-900 text-sm">
              ✅ {targetNameCap} creat{isQuote ? "o" : "a"} con successo. Tutto è stato salvato nel sistema.
              {isQuote && (
                <p className="text-xs text-emerald-800 mt-1.5">
                  ℹ Per convertire questo preventivo in polizza, attendi la conferma del cliente, marca il preventivo come <strong>Accettato</strong> dalla lista preventivi, quindi usa il bottone <strong>“Converti in polizza”</strong>.
                </p>
              )}
            </div>
            {/* Scheda "Richiedi preventivo a un partner" RIMOSSA dal wizard polizza 09/07/2026 su richiesta utente.
                Se serve richiedere una quotazione a un partner, farlo dalla sezione Preventivi. */}
            <div className="flex gap-2 justify-end flex-wrap">
              <Button variant="outline" onClick={() => navigate(listPath)}>Torna a{isQuote ? "i preventivi" : "lle polizze"}</Button>
              <Button
                data-testid="wizard-add-another-policy"
                variant="outline"
                onClick={addAnotherPolicy}
                className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
              >
                <ShieldCheck className="h-4 w-4 mr-2" />
                Aggiungi un&apos;altr{isQuote ? "o preventivo" : "a polizza"} per {clientDisplayName(selectedClient) || "questo cliente"}
              </Button>
              <Button onClick={() => navigate(`${listPath}?focus=${createdPolicyId}`)} className="bg-[#0F172A] hover:bg-[#1E293B]">
                Apri {targetName} creat{isQuote ? "o" : "a"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Navigation footer */}
      {step < 4 && (
        <div className="flex justify-between">
          <Button
            variant="outline"
            data-testid="wizard-back"
            onClick={() => setStep((s) => Math.max(s - 1, 0))}
            disabled={step === 0}
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Indietro
          </Button>
          <Button
            data-testid="wizard-next"
            onClick={goNext}
            disabled={!canGoNext() || creating}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {step === 2 ? (isQuote ? "Crea preventivo e prosegui" : "Crea polizza e prosegui") : step === 3 ? "Termina" : "Avanti"}
            {!creating && <ArrowRight className="h-4 w-4 ml-2" />}
          </Button>
        </div>
      )}

      {/* 🆕 Feb 2026 — Dialog picker Garanzie della Compagnia */}
      <Dialog open={guaranteesPickerOpen} onOpenChange={setGuaranteesPickerOpen}>
        <DialogContent className="max-w-2xl" data-testid="company-guarantees-picker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-700">
              <ShieldCheck className="h-5 w-5" /> Precompila da garanzie compagnia
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-600">
              Seleziona le garanzie da aggiungere alle &quot;Garanzie richieste&quot; della polizza. Le garanzie già inserite in polizza sono nascoste. I valori di Acquisto/Vendita/Imposte/SSN verranno riportati come informazione (visibili nel badge sotto ogni riga).
            </p>
            {(() => {
              // 🆕 15/07/2026 — Escludi le garanzie già aggiunte alla polizza (per id o per nome)
              const usedIds = new Set();
              const usedNames = new Set();
              for (const rc of (policy.requested_coverages || [])) {
                if (rc?._source_guarantee_id) usedIds.add(rc._source_guarantee_id);
                const n = (rc?.name || "").trim().toLowerCase();
                if (n) usedNames.add(n);
              }
              const available = companyGuarantees.filter((g) => {
                if (usedIds.has(g.id)) return false;
                const n = (g.name || "").trim().toLowerCase();
                return !usedNames.has(n);
              });
              if (companyGuarantees.length === 0) {
                return <div className="text-center text-sm text-slate-500 py-6">Nessuna garanzia configurata per questa compagnia.</div>;
              }
              if (available.length === 0) {
                return <div className="text-center text-sm text-slate-500 py-6">Tutte le garanzie di questa compagnia sono già in polizza.</div>;
              }
              return (
                <div className="max-h-[50vh] overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
                  {available.map((g) => {
                    const checked = guaranteesPickerSelected.has(g.id);
                    return (
                      <label
                        key={g.id}
                        data-testid={`picker-guarantee-${g.id}`}
                        className={`flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 ${checked ? "bg-indigo-50" : ""}`}
                      >
                        <input
                          type="checkbox"
                          className="mt-1 accent-indigo-600"
                          checked={checked}
                          onChange={() => {
                            setGuaranteesPickerSelected((prev) => {
                              const s = new Set(prev);
                              if (s.has(g.id)) s.delete(g.id);
                              else s.add(g.id);
                              return s;
                            });
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm text-slate-800">{g.name}</div>
                          <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                            <span>Imposte: <strong>{g.imposte_percent != null ? `${g.imposte_percent}%` : "—"}</strong></span>
                            <span>SSN: <strong>{g.ssn_percent != null ? `${g.ssn_percent}%` : "—"}</strong></span>
                            <span>Acquisto: <strong>{g.buy_price != null ? formatCurrency(g.buy_price) : "—"}</strong></span>
                            <span>Vendita: <strong className="text-emerald-700">{g.sell_price != null ? formatCurrency(g.sell_price) : "—"}</strong></span>
                          </div>
                          {g.notes && <div className="text-[11px] text-slate-500 mt-0.5 italic">{g.notes}</div>}
                        </div>
                      </label>
                    );
                  })}
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGuaranteesPickerOpen(false)}>Annulla</Button>
            <Button
              data-testid="picker-add-selected-guarantees"
              disabled={guaranteesPickerSelected.size === 0}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={() => {
                const toAdd = companyGuarantees
                  .filter((g) => guaranteesPickerSelected.has(g.id))
                  .map((g) => ({
                    name: g.name,
                    sum_insured: "",
                    deductible: "",
                    notes: g.notes || "",
                    // Metadata precompilata da compagnia (usata solo lato FE per info badge)
                    _source_guarantee_id: g.id,
                    _source_company_id: g.company_id,
                    _imposte_percent: g.imposte_percent,
                    _ssn_percent: g.ssn_percent,
                    _buy_price: g.buy_price,
                    _sell_price: g.sell_price,
                  }));
                setPolicy({
                  ...policy,
                  requested_coverages: [...(policy.requested_coverages || []), ...toAdd],
                });
                setGuaranteesPickerOpen(false);
                setGuaranteesPickerSelected(new Set());
                toast.success(`${toAdd.length} garanzi${toAdd.length === 1 ? "a aggiunta" : "e aggiunte"} da compagnia`);
              }}
            >
              Aggiungi selezionate ({guaranteesPickerSelected.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 15/07/2026 — Dialog picker Prodotti dal Catalogo */}
      <Dialog open={productsPickerOpen} onOpenChange={setProductsPickerOpen}>
        <DialogContent className="max-w-2xl" data-testid="products-picker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              📦 Aggiungi prodotti dal catalogo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-600">
              Seleziona i prodotti da aggiungere alle &quot;Garanzie richieste&quot;. Vengono nascosti quelli già inseriti.
            </p>
            {(() => {
              const usedNames = new Set();
              const usedProdIds = new Set();
              for (const rc of (policy.requested_coverages || [])) {
                const n = (rc?.name || "").trim().toLowerCase();
                if (n) usedNames.add(n);
                if (rc?._source_product_id) usedProdIds.add(rc._source_product_id);
              }
              const avail = companyProducts.filter((p) => {
                if (usedProdIds.has(p.id)) return false;
                const n = (p.name || "").trim().toLowerCase();
                return !usedNames.has(n);
              });
              if (avail.length === 0) {
                return <div className="text-center text-sm text-slate-500 py-6">
                  {companyProducts.length === 0 ? "Nessun prodotto in catalogo per questa compagnia." : "Tutti i prodotti di questa compagnia sono già in polizza."}
                </div>;
              }
              return (
                <div className="max-h-[50vh] overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
                  {avail.map((p) => {
                    const checked = productsPickerSelected.has(p.id);
                    return (
                      <label
                        key={p.id}
                        data-testid={`wizard-picker-product-${p.id}`}
                        className={`flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 ${checked ? "bg-purple-50" : ""}`}
                      >
                        <input
                          type="checkbox"
                          className="mt-1 accent-purple-600"
                          checked={checked}
                          onChange={() => {
                            setProductsPickerSelected((prev) => {
                              const s = new Set(prev);
                              if (s.has(p.id)) s.delete(p.id); else s.add(p.id);
                              return s;
                            });
                          }}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm text-slate-800 flex items-center gap-2 flex-wrap">
                            {p.code && <span className="text-[10px] text-purple-700 tabular-nums bg-purple-100 px-1 rounded">{p.code}</span>}
                            {p.name}
                            {p.ramo && <span className="text-[10px] text-slate-500 uppercase">{p.ramo}</span>}
                          </div>
                          <div className="text-[11px] text-slate-500 flex flex-wrap gap-x-3 mt-0.5">
                            {p.default_commission_percent != null && <span>Provv.: <strong>{p.default_commission_percent}%</strong></span>}
                            {p.buy_cost != null && <span>Acquisto: <strong>{formatCurrency(p.buy_cost)}</strong></span>}
                            {p.sell_cost != null && <span>Vendita: <strong className="text-emerald-700">{formatCurrency(p.sell_cost)}</strong></span>}
                          </div>
                          {p.description && <div className="text-[11px] text-slate-500 mt-0.5 italic">{p.description}</div>}
                        </div>
                      </label>
                    );
                  })}
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductsPickerOpen(false)}>Annulla</Button>
            <Button
              data-testid="wizard-picker-add-selected-products"
              disabled={productsPickerSelected.size === 0}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={() => {
                const toAdd = companyProducts
                  .filter((p) => productsPickerSelected.has(p.id))
                  .map((p) => ({
                    name: p.name,
                    sum_insured: "",
                    deductible: "",
                    notes: p.description || "",
                    _source_product_id: p.id,
                    _source_product_code: p.code || null,
                    _buy_price: p.buy_cost,
                    _sell_price: p.sell_cost,
                  }));
                setPolicy({
                  ...policy,
                  requested_coverages: [...(policy.requested_coverages || []), ...toAdd],
                });
                setProductsPickerOpen(false);
                setProductsPickerSelected(new Set());
                toast.success(`${toAdd.length} prodott${toAdd.length === 1 ? "o aggiunto" : "i aggiunti"} dal catalogo`);
              }}
            >
              Aggiungi selezionati ({productsPickerSelected.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Invia a partner per preventivo */}
      <Dialog open={sendPartnerOpen} onOpenChange={(o) => { if (!o && !sendingPartner) setSendPartnerOpen(false); }}>
        <DialogContent className="max-w-2xl" data-testid="send-partner-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-violet-700">
              <Send className="h-5 w-5" /> Invia richiesta a partner
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-slate-600">
              Stai per inviare la scheda completa (cliente <strong>{selectedClient?.surname} {selectedClient?.name}</strong>
              {policy.vehicle_plate && <> · veicolo <span className="font-mono font-bold">{policy.vehicle_plate}</span></>}
              {isQuote && Number(policy.premium) > 0 && <> · <span className="text-violet-700 font-semibold">rif. premio cliente {formatCurrency(policy.premium)}</span></>}
              ) a uno o più partner.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-wide text-violet-700 font-bold">🏢 Partner disponibili — seleziona uno o più</Label>
              <div className="mt-2 max-h-72 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="partner-checklist">
                {/* Broker / Agenzie */}
                {brokers.length > 0 && (
                  <div className="px-3 py-2 bg-violet-50/60">
                    <p className="text-[10px] uppercase tracking-wide font-bold text-violet-700 mb-1">Broker / Agenzie</p>
                    {brokers.map((p) => {
                      const hasContact = !!(p.email || p.pec);
                      const checked = isPartnerSelected("broker", p.id);
                      return (
                        <label
                          key={`b-${p.id}`}
                          className={`flex items-start gap-2 py-1.5 cursor-pointer rounded px-1.5 hover:bg-white transition ${!hasContact ? "opacity-60" : ""}`}
                          data-testid={`partner-broker-${p.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!hasContact}
                            onChange={() => togglePartnerSelection("broker", p.id, p.name, p.email || p.pec)}
                            className="mt-1"
                            data-testid={`partner-checkbox-broker-${p.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-slate-900">{p.name}</div>
                            <div className="text-xs text-slate-500 truncate">
                              {p.email ? <>📧 {p.email}</> : null}
                              {p.email && p.pec ? " · " : null}
                              {p.pec ? <>PEC: {p.pec}</> : null}
                              {!p.email && !p.pec ? <span className="text-rose-600">⚠️ Nessun contatto email/PEC</span> : null}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
                {/* Compagnie */}
                {companies.length > 0 && (
                  <div className="px-3 py-2 bg-amber-50/40">
                    <p className="text-[10px] uppercase tracking-wide font-bold text-amber-700 mb-1">Compagnie</p>
                    {companies.map((p) => {
                      const hasContact = !!(p.email || p.pec);
                      const checked = isPartnerSelected("company", p.id);
                      return (
                        <label
                          key={`c-${p.id}`}
                          className={`flex items-start gap-2 py-1.5 cursor-pointer rounded px-1.5 hover:bg-white transition ${!hasContact ? "opacity-60" : ""}`}
                          data-testid={`partner-company-${p.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!hasContact}
                            onChange={() => togglePartnerSelection("company", p.id, p.name, p.email || p.pec)}
                            className="mt-1"
                            data-testid={`partner-checkbox-company-${p.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-slate-900">{p.name}</div>
                            <div className="text-xs text-slate-500 truncate">
                              {p.email ? <>📧 {p.email}</> : null}
                              {p.email && p.pec ? " · " : null}
                              {p.pec ? <>PEC: {p.pec}</> : null}
                              {!p.email && !p.pec ? <span className="text-rose-600">⚠️ Nessun contatto email/PEC</span> : null}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
                {brokers.length === 0 && companies.length === 0 && (
                  <p className="text-xs text-slate-500 italic p-3">Nessun partner configurato. Aggiungili in /brokers o /companies.</p>
                )}
              </div>
              <p className="text-[10px] text-slate-500 mt-1">
                {partnerSelections.length > 0 ? (
                  <>✓ <strong>{partnerSelections.length}</strong> partner selezionati: {partnerSelections.map((p) => p.name).join(", ")}</>
                ) : (
                  <>⚠️ Seleziona almeno un partner. Solo i partner con email/PEC sono selezionabili.</>
                )}
              </p>
            </div>
            <div>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                <Label className="text-xs uppercase tracking-wide text-violet-700 font-bold">📎 Allegati da includere</Label>
                {availableAttachments.length > 0 && (
                  <div className="flex gap-1 text-[11px]">
                    <button type="button" onClick={selectAllAttachments} className="text-violet-700 hover:underline" data-testid="attachments-select-all">Seleziona tutti</button>
                    <span className="text-slate-300">·</span>
                    <button type="button" onClick={deselectAllAttachments} className="text-slate-500 hover:underline" data-testid="attachments-deselect-all">Deseleziona tutti</button>
                  </div>
                )}
              </div>
              <div className="border border-slate-200 rounded-md max-h-40 overflow-y-auto" data-testid="attachments-checklist">
                {loadingAttachments ? (
                  <div className="p-3 text-xs text-slate-500 flex items-center gap-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Carico allegati…
                  </div>
                ) : availableAttachments.length === 0 ? (
                  <p className="p-3 text-xs text-slate-500 italic">Nessun allegato disponibile per {isQuote ? "questo preventivo" : "questa polizza"}.</p>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {availableAttachments.map((a) => {
                      const checked = selectedAttachmentIds.includes(a.id);
                      const sizeKb = a.size ? Math.round(a.size / 1024) : 0;
                      return (
                        <label
                          key={a.id}
                          className="flex items-start gap-2 px-3 py-1.5 cursor-pointer hover:bg-slate-50 text-sm"
                          data-testid={`attachment-row-${a.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleAttachment(a.id)}
                            className="mt-1"
                            data-testid={`attachment-checkbox-${a.id}`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-900 truncate">📄 {a.filename}</div>
                            <div className="text-[11px] text-slate-500">
                              {a.kind ? <span className="uppercase tracking-wide font-semibold text-violet-600">{a.kind}</span> : null}
                              {a.kind && sizeKb ? " · " : null}
                              {sizeKb > 0 ? `${sizeKb} KB` : null}
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
              <p className="text-[10px] text-slate-500 mt-1">
                ℹ {selectedAttachmentIds.length === 0
                  ? "Nessun allegato verrà inviato (solo la scheda PDF)."
                  : `${selectedAttachmentIds.length}/${availableAttachments.length} file selezionati. Limite SMTP 18MB.`}
              </p>
            </div>
            <div>
              <Label>Note aggiuntive (opzionale)</Label>
              <Textarea
                data-testid="partner-extra-notes"
                rows={3}
                value={partnerNotes}
                onChange={(e) => setPartnerNotes(e.target.value)}
                placeholder="es. cliente neopatentato, richiede preventivo Kasko, urgente entro venerdì…"
              />
            </div>
            {/* 🆕 10/07/2026 — Checkbox URGENTE */}
            <label className="flex items-start gap-2 cursor-pointer text-sm rounded-md border border-rose-300 bg-rose-50 p-3 hover:bg-rose-100/50">
              <input
                type="checkbox"
                data-testid="partner-urgent"
                checked={partnerUrgent}
                onChange={(e) => setPartnerUrgent(e.target.checked)}
                className="mt-1 h-4 w-4 cursor-pointer accent-rose-600"
              />
              <span>
                <strong className="text-rose-800">🚨 Segnala come URGENTE</strong>
                <span className="block text-xs text-slate-600 mt-0.5">
                  Aggiunge banner rosso &quot;URGENTE&quot; in cima al PDF/email e riduce la deadline consigliata di risposta a 2 giorni.
                </span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendPartnerOpen(false)} disabled={sendingPartner}>
              Annulla
            </Button>
            <Button
              data-testid="confirm-send-partner"
              onClick={sendToPartner}
              disabled={sendingPartner || partnerSelections.length === 0}
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              {sendingPartner ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              {sendingPartner ? "Invio…" : `Invia richiesta${partnerSelections.length > 1 ? ` a ${partnerSelections.length} partner` : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Task 2: Dialog "Cliente duplicato" — mostra dati completi e permette di usare l'esistente o forzare la creazione */}
      <Dialog open={!!dupClientDialog} onOpenChange={(o) => { if (!o && !creatingClient) setDupClientDialog(null); }}>
        <DialogContent className="max-w-lg" data-testid="dup-client-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              ⚠️ Cliente già presente in archivio
            </DialogTitle>
          </DialogHeader>
          {dupClientDialog?.client && (
            <div className="space-y-3 text-sm">
              <p className="text-slate-600">
                Esiste già un cliente in archivio con
                {dupClientDialog.reason === "cf" && <> lo stesso <strong>Codice Fiscale</strong></>}
                {dupClientDialog.reason === "email" && <> la stessa <strong>email</strong></>}
                {(dupClientDialog.reason === "phone" || dupClientDialog.reason === "phone_x_mobile") && <> lo stesso <strong>numero di telefono</strong></>}
                {(dupClientDialog.reason === "mobile" || dupClientDialog.reason === "mobile_x_phone") && <> lo stesso <strong>cellulare</strong></>}
                {dupClientDialog.reason === "name" && <> lo stesso <strong>nome, cognome</strong>{dupClientDialog.client.birth_date ? " e data di nascita" : ""}</>}
                {dupClientDialog.reason === "backend" && <> dati in conflitto</>}.
                Scegli come procedere.
              </p>
              {dupClientDialog.backendMessage && (
                <p className="text-[11px] text-amber-700 italic" data-testid="dup-client-backend-msg">
                  ℹ {dupClientDialog.backendMessage}
                </p>
              )}
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="dup-client-info">
                <p className="font-semibold text-amber-900 text-base">
                  {dupClientDialog.client.surname} {dupClientDialog.client.name}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 mt-2 text-xs">
                  {dupClientDialog.client.fiscal_code && (
                    <div><span className="text-slate-500">CF:</span> <span className="font-mono font-semibold">{dupClientDialog.client.fiscal_code}</span></div>
                  )}
                  {dupClientDialog.client.birth_date && (
                    <div><span className="text-slate-500">🎂 Nato il:</span> <strong>{formatDate(dupClientDialog.client.birth_date)}</strong></div>
                  )}
                  {dupClientDialog.client.email && (
                    <div className="sm:col-span-2"><span className="text-slate-500">📧 Email:</span> <strong>{dupClientDialog.client.email}</strong></div>
                  )}
                  {dupClientDialog.client.mobile && (
                    <div><span className="text-slate-500">📱 Cell.:</span> <strong>{dupClientDialog.client.mobile}</strong></div>
                  )}
                  {dupClientDialog.client.phone && (
                    <div><span className="text-slate-500">☎️ Tel.:</span> <strong>{dupClientDialog.client.phone}</strong></div>
                  )}
                  {(dupClientDialog.client.address || dupClientDialog.client.city) && (
                    <div className="sm:col-span-2">
                      <span className="text-slate-500">🏠 Residenza:</span>{" "}
                      <strong>
                        {[dupClientDialog.client.address, dupClientDialog.client.house_number].filter(Boolean).join(" ")}
                        {dupClientDialog.client.cap || dupClientDialog.client.city ? `, ${[dupClientDialog.client.cap, dupClientDialog.client.city, dupClientDialog.client.province].filter(Boolean).join(" ")}` : ""}
                      </strong>
                    </div>
                  )}
                  {typeof dupClientDialog.client.active_policies_count === "number" && (
                    <div className="sm:col-span-2"><span className="text-slate-500">🛡️ Polizze attive:</span> <strong>{dupClientDialog.client.active_policies_count}</strong></div>
                  )}
                </div>
              </div>
              <p className="text-[11px] text-slate-500 italic">
                💡 Consigliato: usa il cliente esistente per evitare duplicazioni e mantenere lo storico unico.
              </p>
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setDupClientDialog(null)}
              disabled={creatingClient}
              data-testid="dup-client-cancel"
            >
              Annulla
            </Button>
            {/* 🆕 Feb 2026 — Collega area privata (solo se il duplicato è per email/cellulare) */}
            {dupClientDialog?.client?.id && ["email", "phone", "mobile", "phone_x_mobile", "mobile_x_phone"].includes(dupClientDialog?.reason) && (
              <Button
                data-testid="dup-client-link-portal"
                onClick={forceCreateAndLinkPortal}
                disabled={creatingClient}
                variant="outline"
                className="border-violet-300 text-violet-700 hover:bg-violet-50"
                title="Crea un cliente separato e mostra le sue polizze anche nell'area privata del cliente esistente (utile per moglie/figli)"
              >
                {creatingClient ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                🔗 Crea e collega area privata a {dupClientDialog.client.surname}
              </Button>
            )}
            <Button
              data-testid="dup-client-force"
              onClick={forceCreateClient}
              disabled={creatingClient}
              variant="outline"
              className="border-rose-300 text-rose-700 hover:bg-rose-50"
            >
              {creatingClient ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Procedi comunque (crea duplicato)
            </Button>
            <Button
              data-testid="dup-client-use-existing"
              onClick={useExistingDuplicate}
              disabled={creatingClient}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Usa cliente esistente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* 🆕 Dialog anteprima email prima dell'invio (partner) */}
      <EmailPreviewDialog
        open={!!emailPreviewCtx}
        onOpenChange={(o) => { if (!o) setEmailPreviewCtx(null); }}
        emailTypeKey={emailPreviewCtx?.typeKey}
        title={emailPreviewCtx?.title || "Anteprima email prima dell'invio"}
        fetchPreview={emailPreviewCtx?.fetchPreview || (async () => ({ subject: "", html_body: "", to: [] }))}
        onConfirmSend={async () => {
          if (emailPreviewCtx?.onConfirm) await emailPreviewCtx.onConfirm();
          setEmailPreviewCtx(null);
        }}
      />
    </div>
  );
}
